import { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import { io as socketIO } from "socket.io-client";
import AppModals from "./AppModals";
import { useT } from "./i18n";
import {Circle, BookOpen, Coffee, CheckCircle2, LifeBuoy, CheckCircle, Radio, Sun, BarChart2, CheckSquare, Users, LayoutDashboard, RollerCoaster, ShieldAlert, Search, Plus, Bell, BellOff, Settings, Power, Archive, List, Kanban, MessageSquare, Target, Zap, Tags, Shield, Home, AlignLeft, Paperclip, Clock, AlertTriangle, Activity, Folder, Save, FileText, Hexagon, Bug, Key, Trash2, Edit3, Wrench, Globe, Rocket, Monitor, Code, PenTool, Palette, Briefcase, Link } from "lucide-react";
import Sidebar from "./components/Sidebar";
import * as LucideIcons from "lucide-react";
import GlobalTicker from "./GlobalTicker";
import TeamsTab from "./TeamsTab";
import CalendarTab from './CalendarTab';

const NL2Tab = lazy(() => import("./NL2Tab"));
const TaskBoard = lazy(() => import("./components/TaskBoard"));
const BetaPortal = lazy(() => import("./components/BetaPortal"));
const RoadmapTab = lazy(() => import("./RoadmapTab"));
const PollsTab = lazy(() => import("./PollsTab"));
const PlatformTab = lazy(() => import("./PlatformTab"));
const NewsTab = lazy(() => import("./NewsTab"));
const WikiTab = lazy(() => import("./WikiTab"));
const TeamPortalTab = lazy(() => import("./TeamPortalTab"));


const API = "/api";
export const apiFetch = async (path, options={}, token=null) => {
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (!(options.body instanceof FormData)) headers["Content-Type"] = "application/json";
  
  const res = await fetch(`${API}${path}`, {...options, headers:{...headers,...(options.headers||{})}});
  
  if (!res.ok) { 
    // 🚀 GLOBALER LOGOUT BEI 401:
    if (res.status === 401) {
      localStorage.removeItem("tf_token");
      localStorage.removeItem("tf_user");
      window.location.reload(); 
    }
    
    const err = await res.json().catch(()=>({error:"Fehler"})); 
    
    // 🚀 NEU: API Fehler in den globalen "Flugschreiber" werfen
    const errorMsg = err.error || "Unbekannter Fehler";
    window.dispatchEvent(new CustomEvent('tf_system_error', { detail: `API ${res.status}: ${errorMsg} (${path})` }));
    
    throw new Error(errorMsg); 
  }
  return res.json();
};

export const ACCENT_GRADIENT = "linear-gradient(135deg, #1B3A6B, #0ECFD9)";
export const ACCENT_DARK = "#1B3A6B";
export const ACCENT_LIGHT = "#0ECFD9";

export const AVATAR_COLORS  = ["#FF6B6B","#4ECDC4","#FFE66D","#A8E6CF","#C084FC","#60A5FA","#FB923C","#34D399","#F472B6","#0ECFD9"];
export const PROJECT_COLORS = ["#0BACBD","#EC4899","#F59E0B","#10B981","#3B82F6","#8B5CF6","#EF4444","#14B8A6"];
export const TEAM_COLORS    = ["#0BACBD","#EC4899","#10B981","#F59E0B","#8B5CF6","#EF4444","#3B82F6","#14B8A6"];
export const TAG_COLORS     = ["#0BACBD","#EC4899","#F59E0B","#10B981","#3B82F6","#8B5CF6","#EF4444","#14B8A6","#FF6B6B","#34D399"];
export const PRIORITIES     = [{label:"Dringend",color:"#FF4757",bg:"#FF475718"},{label:"Hoch",color:"#FF6B35",bg:"#FF6B3518"},{label:"Mittel",color:"#FFA502",bg:"#FFA50218"},{label:"Niedrig",color:"#2ED573",bg:"#2ED57318"}];
export const DEFAULT_STATUSES = ["Offen","In Arbeit","Review","Erledigt"];
export const DEFAULT_STATUS_META = {"Offen":{color:"#94A3B8",dot:"#94A3B8",bg:"#94A3B818"},"In Arbeit":{color:"#60A5FA",dot:"#3B82F6",bg:"#60A5FA18"},"Review":{color:"#C084FC",dot:"#A855F7",bg:"#C084FC18"},"Erledigt":{color:"#34D399",dot:"#10B981",bg:"#34D39918"}};
export const PERMISSIONS    = ["edit","view","none"];
export const PERM_META      = {edit:{label:"Bearbeiten",color:"#34D399",bg:"#34D39918",icon:"✎"},view:{label:"Nur lesen",color:"#60A5FA",bg:"#60A5FA18",icon:"👁"},none:{label:"Kein Zugriff",color:"#FF4757",bg:"#FF475718",icon:"⊘"}};
export const ROLES          = ["Admin","Leitung","Nutzer","Beta Tester"];
export const ROLE_META      = {
  Admin: {color: "#C084FC", bg: "#C084FC18"},
  Leitung: {color: "#F59E0B", bg: "#F59E0B18"},
  Nutzer: {color: "#60A5FA", bg: "#60A5FA18"},
  "Beta Tester": {color: "#EF4444", bg: "#EF444418"}
};
export const NOTIF_META     = {task_assigned:{icon:"📋",color:"#60A5FA"},status_change:{icon:"🔄",color:"#C084FC"},comment:{icon:"💬",color:"#34D399"},mention:{icon:"@",color:"#F59E0B"},automation:{icon:"⚡",color:"#0ECFD9"},default:{icon:"🔔",color:"#94A3B8"}};
export const AUDIT_META = {login:{icon:"Key",color:"#60A5FA"},create:{icon:"Plus",color:"#34D399"},delete:{icon:"Trash2",color:"#FF4757"},status_change:{icon:"Activity",color:"#C084FC"},version_change:{icon:"Folder",color:"#8B5CF6"},archive:{icon:"Archive",color:"#F59E0B"},default:{icon:"FileText",color:"#94A3B8"}};

export const DARK  = {bg:"#0A0D15",sidebar:"#0D111C",card:"#0D111C",cardHover:"#141925",border:"#1A2131",border2:"#242D3F",border3:"#2F3A50",text:"#E0E5F0",textSub:"#C0C9D8",textMuted:"#6C7C9E",textFaint:"#4A5A7A",textGhost:"#2F3A50",input:"#090A11",inputBorder:"#242D3F"};
export const LIGHT = {bg:"#F0F4F9",sidebar:"#FFFFFF",card:"#FFFFFF",cardHover:"#F8FBFF",border:"#E0E7F0",border2:"#D0D9E5",border3:"#C0CAD9",text:"#0A1220",textSub:"#1A2A40",textMuted:"#6C7C9E",textFaint:"#A0ADC5",textGhost:"#D0D9E5",input:"#F9FBFF",inputBorder:"#D0D9E5"};

export const initials = n=>n?.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)||"?";
export const timeAgo  = ts=>{const d=Date.now()-new Date(ts).getTime();if(d<60000)return"gerade eben";if(d<3600000)return`${Math.floor(d/60000)} Min.`;if(d<86400000)return`${Math.floor(d/3600000)} Std.`;return`${Math.floor(d/86400000)}d`;};
export const fmtSize  = b=>{if(!b)return"0 B";if(b<1024)return`${b} B`;if(b<1048576)return`${(b/1024).toFixed(1)} KB`;if(b<1073741824)return`${(b/1048576).toFixed(1)} MB`;return`${(b/1073741824).toFixed(2)} GB`;};
export const fmtDate  = ts=>ts?new Date(ts).toLocaleDateString('de-DE',{day:'2-digit',month:'2-digit',year:'numeric'}):"";
export const makeIS   = T=>({width:"100%",background:T.input,border:`1px solid ${T.inputBorder}`,borderRadius:11,padding:"10px 14px",color:T.text,fontSize:13,outline:"none"});
export const Field    = ({label,children,T})=><div><div style={{fontSize:10,color:T.textFaint,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>{label}</div>{children}</div>;
export const Badge    = ({label,color,bg})=><span style={{fontSize:11,fontWeight:600,color,background:bg,padding:"3px 10px",borderRadius:7,border:`1px solid ${color}20`,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center"}}>{label}</span>;
export const PermBadge= ({perm})=>{const m=PERM_META[perm]||PERM_META.none;return <span style={{fontSize:11,fontWeight:600,color:m.color,background:m.bg,padding:"3px 10px",borderRadius:7,border:`1px solid ${m.color}20`,display:"inline-flex",alignItems:"center",gap:5}}>{m.icon} {m.label}</span>;};
export const Spinner  = ()=><div style={{width:22,height:22,border:`2px solid ${DARK.border3}`,borderTop:"2px solid #0ECFD9",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto"}}/>;
export const UserAvatar=({user,size=30,overlap=false,idx=0})=>{
  const [isOnline, setIsOnline] = useState(false);
  useEffect(() => {
    const check = () => {
      // 🚀 FIX 1: Ignoriert strikte Datentypen (String/Int), um sicher zu matchen
      const isHere = (window.__ONLINE_USERS || []).some(id => String(id) === String(user?.id));
      setIsOnline(isHere);
    };
    check(); window.addEventListener('online_users_changed', check);
    return () => window.removeEventListener('online_users_changed', check);
  }, [user?.id]);

  if(!user)return null; const r=Math.floor(size*0.35);
  const base={width:size,height:size,borderRadius:r,border:overlap?"2px solid transparent":"none",marginLeft:overlap&&idx>0?-size*0.3:0,zIndex:idx,flexShrink:0,position:"relative"};
  
  // 🚀 FIX 2: Der Punkt hat jetzt eine Mindestgröße (10px), damit er bei kleinen Bildern nicht verschwindet!
  const dotSize = Math.max(10, size * 0.35); 
  const dot = isOnline ? <div style={{position:"absolute",bottom:-2,right:-2,width:dotSize,height:dotSize,background:"#34D399",borderRadius:"50%",border:"2px solid #0A0D15",zIndex:2}}/> : null;
  
  if(user.avatar_url)return <div style={base}><img src={user.avatar_url} alt={user.name} title={user.name} style={{width:"100%",height:"100%",borderRadius:r,objectFit:"cover"}} onError={e=>{e.target.style.display="none";}} />{dot}</div>;
  return <div title={user.name} style={{...base,background:user.color||ACCENT_LIGHT,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.35,fontWeight:700,color:"#0A0D15"}}>{initials(user.name)}{dot}</div>;
};

const ICON_MAP = { CheckSquare, Users, LayoutDashboard, RollerCoaster, ShieldAlert, Search, Plus, Bell, BellOff, Settings, Power, Archive, List, Kanban, MessageSquare, Target, Zap, Tags, Shield, Home, AlignLeft, Paperclip, Clock, AlertTriangle, Activity, Folder, Save, FileText, Hexagon, Bug, Key, Trash2, Edit3, Wrench, Globe, Rocket, Monitor, Code, PenTool, Palette, Briefcase, BarChart2 };

export const renderMarkdown = (text) => {
  if(!text) return {__html:""};
  let html = text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/\*\*([^\*]+)\*\*/g,'<strong>$1</strong>')
    .replace(/\*([^\*]+)\*/g,'<em>$1</em>')
    .replace(/`([^`]+)`/g,'<code style="background:rgba(0,0,0,0.3);padding:2px 4px;borderRadius:4px;fontFamily:monospace">$1</code>')
    .replace(/!\[img\|([^\]]+)\]\(([^)]+)\)/g, '<img src="$2" style="width:$1; border-radius:8px; margin:16px 0; max-width:100%; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" />')
    // 🚀 NEU: Überschriften mit dynamischen IDs (wichtig für das Inhaltsverzeichnis!)
    .replace(/^(#{1,3})\s+(.*)$/gm, (match, hashes, text) => {
        const id = encodeURIComponent(text.trim().toLowerCase());
        const size = hashes.length === 1 ? '28px' : hashes.length === 2 ? '22px' : '18px';
        const color = hashes.length === 3 ? '#0ECFD9' : '#fff';
        const border = hashes.length === 2 ? 'border-bottom:1px solid rgba(255,255,255,0.1); padding-bottom:6px;' : '';
        return `<h${hashes.length} id="${id}" style="font-size:${size}; margin-top:24px; color:${color}; ${border}">${text}</h${hashes.length}>`;
    })
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="border-radius:8px; margin:16px 0; max-width:100%; box-shadow: 0 4px 12px rgba(0,0,0,0.1);" />')
    
    // 🚀 NEU: Smarter Link-Renderer (Unterscheidet zwischen Wiki-Links und externen Links)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        if (url.startsWith('#wiki-')) {
            // Interner Wiki-Link (Orange, gestrichelt, kein neues Tab!)
            return `<a href="${url}" class="internal-wiki-link" style="color:#F59E0B; text-decoration:none; font-weight:bold; border-bottom:1px dashed #F59E0B;" title="Interne Wiki-Seite">${text}</a>`;
        }
        // Normaler externer Link (Blau, neues Tab)
        return `<a href="${url}" target="_blank" rel="noreferrer" style="color:#0ECFD9; text-decoration:none; font-weight:bold;">${text}</a>`;
    })

    .replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})(?:\S+)?/g, '<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0;border-radius:12px;border:1px solid #242D3F;"><iframe src="https://www.youtube.com/embed/$1" style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;" allowfullscreen></iframe></div>')
    .replace(/(?:^|\n)(\|.*\|)\r?\n(\|[-:| ]+\|)\r?\n((?:\|.*\|(?:\r?\n|$))*)/g, (match, header, divider, rows) => {
        const getCells = row => row.split('|').slice(1, -1).map(c => c.trim());
        const ths = getCells(header).map(c => `<th style="padding:12px 16px;text-align:left;border-bottom:2px solid #0ECFD9;background:rgba(14,207,217,0.1);color:#0ECFD9;font-weight:700;">${c}</th>`).join('');
        const trs = rows.trim().split('\n').filter(r => r.trim()).map((row, i) => {
            const tds = getCells(row).map(c => `<td style="padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.05);">${c}</td>`).join('');
            return `<tr style="background:${i%2===0?'rgba(255,255,255,0.02)':'transparent'}; transition: background 0.2s;" onMouseOver="this.style.background='rgba(255,255,255,0.05)'" onMouseOut="this.style.background='${i%2===0?'rgba(255,255,255,0.02)':'transparent'}'">${tds}</tr>`;
        }).join('');
        return `<div style="overflow-x:auto;margin:24px 0;border-radius:12px;border:1px solid rgba(255,255,255,0.1);"><table style="width:100%;border-collapse:collapse;font-size:14px;text-align:left;"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table></div>`;
    });
  return {__html: html};
};

export const RenderIcon = ({ icon, size=18, color="inherit" }) => {
  if (!icon) return null;
  
  // Wandelt Schreibweisen wie "pen-tool" oder "wrench" sauber in "PenTool" / "Wrench" um
  const pascalKey = icon
    .split(/[-_ ]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
  
  const LucideComp = ICON_MAP[pascalKey] || ICON_MAP[icon] || LucideIcons[pascalKey] || LucideIcons[icon];
  if (LucideComp) {
    const Component = LucideComp;
    return <Component size={size} color={color} />;
  }
  
  // Schutz für Emojis oder reinen Text, damit nichts überlappt
  return <span style={{fontSize: size, lineHeight: 1, color, display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{icon}</span>;
};

export const FileIcon = ({mime})=>{if(!mime)return"📄";if(mime.startsWith("image/"))return"🖼";if(mime.includes("pdf"))return"📕";if(mime.includes("zip")||mime.includes("rar"))return"📦";if(mime.includes("video"))return"🎬";return"📄";};
export const CheckInputInline=({onAdd,T,iS})=>{const [val,setVal]=useState("");const submit=()=>{if(val.trim()){onAdd(val);setVal("");}};const isEN = localStorage.getItem("tf_lang") === "en"; return <div style={{display:"flex",gap:8}}><input value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder={isEN?"New item...":"Neuer Punkt…"} style={{...iS,flex:1}}/><button onClick={submit} style={{background:T.inputBorder,border:"none",color:T.textMuted,padding:"10px 14px",borderRadius:11,fontSize:12,cursor:"pointer",fontWeight:500,whiteSpace:"nowrap"}}>+ Add</button></div>;};
export const BarChart = ({data,colorKey,valueKey,labelKey,T,height=130})=>{if(!data?.length)return <div style={{fontSize:12,color:T.textFaint,textAlign:"center",padding:"26px"}}>Keine Daten</div>;const max=Math.max(...data.map(d=>parseInt(d[valueKey])||0))||1;return <div style={{display:"flex",gap:8,alignItems:"flex-end",height,padding:"0 6px"}}>{data.map((d,i)=><div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6}}><div style={{fontSize:10,color:T.textFaint,fontWeight:600}}>{d[valueKey]||0}</div><div style={{width:"100%",background:d[colorKey]||ACCENT_LIGHT,borderRadius:"5px 5px 0 0",height:`${Math.max(4,(parseInt(d[valueKey])||0)/max*90)}px`,transition:"height 0.3s ease"}}/><div style={{fontSize:10,color:T.textGhost,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%",textAlign:"center"}}>{d[labelKey]||""}</div></div>)}</div>;};

export const Modal = ({title,onClose,width=480,children,footer,T})=>(
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div className="modal-box" style={{background:T.sidebar,border:`1px solid ${T.border3}`,borderRadius:22,padding:"32px",width,maxHeight:"92vh",overflowY:"auto",animation:"scaleIn 0.2s ease",boxShadow:"0 32px 72px rgba(0,0,0,0.6)"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:26}}>
        <h2 style={{fontSize:18,fontWeight:700,color:T.text}}>{title}</h2>
        <button onClick={onClose} style={{background:T.cardHover,border:"none",color:T.textMuted,width:32,height:32,borderRadius:9,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:16}}>{children}</div>
      {footer&&<div style={{marginTop:30}}>{footer}</div>}
    </div>
  </div>
);

export function MentionInput({value,onChange,onSubmit,placeholder,users,T,iS,style={}}) {
  const [show,setShow]=useState(false);const [sugg,setSugg]=useState([]);const [mStart,setMStart]=useState(-1);
  const ref=useRef(null);
  const handleChange=e=>{const v=e.target.value;onChange(v);const cursor=e.target.selectionStart;const before=v.slice(0,cursor);const atIdx=before.lastIndexOf("@");if(atIdx>=0&&(atIdx===0||/\s/.test(before[atIdx-1]))){const q=before.slice(atIdx+1).toLowerCase();const m=users.filter(u=>u.name.toLowerCase().includes(q)||(u.username||"").toLowerCase().includes(q)).slice(0,5);setSugg(m);setShow(m.length>0);setMStart(atIdx);}else setShow(false);};
  const insert=u=>{const before=value.slice(0,mStart);const after=value.slice(ref.current?.selectionStart||value.length);onChange(`${before}@${u.username||u.name.replace(/ /g,"")} ${after}`);setShow(false);ref.current?.focus();};
  return (
    <div style={{position:"relative",flex:1}}>
      <input ref={ref} value={value} onChange={handleChange} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey&&!show){e.preventDefault();onSubmit();}if(e.key==="Escape")setShow(false);}} placeholder={placeholder} style={{...iS,...style}}/>
      {show&&sugg.length>0&&<div style={{position:"absolute",bottom:"100%",left:0,right:0,background:T.sidebar,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden",zIndex:100,marginBottom:6,boxShadow:"0 12px 32px rgba(0,0,0,0.4)"}}>{sugg.map(u=><div key={u.id} onClick={()=>insert(u)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${T.border}`}} onMouseEnter={e=>e.currentTarget.style.background=T.cardHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><UserAvatar user={u} size={26}/><div><div style={{fontSize:13,fontWeight:500,color:T.text}}>{u.name}</div><div style={{fontSize:11,color:T.textFaint}}>@{u.username}</div></div></div>)}</div>}
    </div>
  );
}

function ForgotPasswordLink() {
  const [show,setShow]=useState(false);
  const [uname,setUname]=useState("");
  const [msg,setMsg]=useState("");
  const [loading,setLoading]=useState(false);
  const iS={width:"100%",background:DARK.input,border:`1px solid ${DARK.inputBorder}`,borderRadius:11,padding:"10px 14px",color:DARK.text,fontSize:13,outline:"none"};
  const submit=async()=>{
    if(!uname.trim())return;
    setLoading(true);setMsg("");
    try{
      await apiFetch("/auth/forgot-password",{method:"POST",body:JSON.stringify({username:uname})});
      setMsg("✅ OTP wurde per Discord gesendet!");
    }catch(e){setMsg("❌ "+e.message);}
    finally{setLoading(false);}
  };
  return show?(
    <div style={{marginTop:16,padding:"16px",background:DARK.cardHover,border:`1px solid ${DARK.border3}`,borderRadius:12}}>
      <div style={{fontSize:10,color:DARK.textFaint,marginBottom:10,fontWeight:700,letterSpacing:"0.1em"}}>PASSWORT ZURÜCKSETZEN</div>
      {msg?<div style={{fontSize:12,color:msg.startsWith("✅")?"#34D399":"#FF4757",marginBottom:10}}>{msg}</div>:(
        <>
          <input value={uname} onChange={e=>setUname(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="Benutzername" style={{...iS,marginBottom:10}}/>
          <div style={{display:"flex",gap:10}}>
            <button onClick={submit} disabled={loading} style={{flex:1,background:"#5865F2",border:"none",color:"#fff",padding:"11px",borderRadius:11,fontSize:13,cursor:"pointer",fontWeight:600}}>{loading?"…":"Per Discord senden"}</button>
            <button onClick={()=>setShow(false)} style={{background:"transparent",border:`1px solid ${DARK.border3}`,color:DARK.textMuted,padding:"11px 16px",borderRadius:11,fontSize:13,cursor:"pointer"}}>×</button>
          </div>
          <div style={{fontSize:10,color:DARK.textGhost,marginTop:10}}>Du bekommst ein Einmal-Passwort per Discord DM. Discord ID muss vorher eingetragen sein.</div>
        </>
      )}
    </div>
  ):(
    <div onClick={()=>setShow(true)} style={{textAlign:"center",fontSize:13,color:DARK.textFaint,cursor:"pointer",marginTop:6,padding:"6px"}}>Passwort vergessen?</div>
  );
}

const LoginPage = ({onLogin})=>{
  const [username,setUsername]=useState("");const [password,setPassword]=useState("");
  const [error,setError]=useState("");const [loading,setLoading]=useState(false);
  const go=async e=>{e.preventDefault();setError("");setLoading(true);try{const d=await apiFetch("/auth/login",{method:"POST",body:JSON.stringify({username,password})});localStorage.setItem("tf_token",d.token);onLogin(d.token,d.user);}catch(err){setError(err.message);}finally{setLoading(false);}};
  const iS={width:"100%",background:DARK.input,border:`1px solid ${DARK.inputBorder}`,borderRadius:11,padding:"12px 16px",color:DARK.text,fontSize:14,outline:"none"};
  return (
    <div style={{minHeight:"100vh",background:DARK.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans','Segoe UI',sans-serif",color:DARK.text}}>
      <div style={{position:"fixed",inset:0,overflow:"hidden",pointerEvents:"none"}}><div style={{position:"absolute",top:-300,left:-300,width:800,height:800,borderRadius:"50%",background:"radial-gradient(circle,rgba(27,58,107,0.4) 0%,transparent 70%)",opacity:0.8}}/><div style={{position:"absolute",bottom:-200,right:-200,width:700,height:700,borderRadius:"50%",background:"radial-gradient(circle,rgba(14,207,217,0.2) 0%,transparent 70%)",opacity:0.8}}/></div>
      <div style={{position:"relative",width:420,animation:"fadeUp 0.5s ease",zIndex:10}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <img src={process.env.PUBLIC_URL+"/TaskFlow_Logo.jpg"} alt="TaskFlow Logo" style={{width:84,height:84,objectFit:"contain",margin:"0 auto 16px",display:"block",borderRadius:20}} onError={e=>{e.target.style.display="none";e.target.nextSibling&&(e.target.nextSibling.style.display="flex");}}/>
          <div style={{display:"none",width:80,height:80,borderRadius:20,background:ACCENT_GRADIENT,alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 16px"}}>⚡</div>
          <div style={{fontSize:32,fontWeight:800,background:ACCENT_GRADIENT,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",letterSpacing:"-1px"}}>TaskFlow</div>
        </div>
        <div style={{background:DARK.card,border:`1px solid ${DARK.border3}`,borderRadius:24,padding:"40px",boxShadow:"0 32px 80px rgba(0,0,0,0.6)"}}>
          {error&&<div style={{background:"#FF475718",border:"1px solid #FF475740",borderRadius:12,padding:"12px 16px",color:"#FF4757",fontSize:13,marginBottom:20}}>⚠ {error}</div>}
          <form onSubmit={go} style={{display:"flex",flexDirection:"column",gap:20}}>
            <Field label="BENUTZERNAME" T={DARK}><input type="text" value={username} onChange={e=>setUsername(e.target.value)} style={iS} autoFocus required/></Field>
            <Field label="PASSWORT" T={DARK}><input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" style={iS} required/></Field>
            <button type="submit" disabled={loading} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"14px",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer",opacity:loading?0.7:1,boxShadow:`0 8px 24px rgba(14,207,217,0.25)`,transition:"all 0.2s"}}> {loading?<Spinner/>:"Anmelden"}</button>
            <ForgotPasswordLink/>
          </form>
        </div>
        <div style={{textAlign:"center",fontSize:12,color:DARK.textGhost,marginTop:32,letterSpacing:"0.05em"}}>TASKFLOW WORKSPACE © 2026</div>
      </div>
    </div>
  );
};

export default function TaskFlow() {
  return (
    <BrowserRouter>
      <TaskFlowInner />
    </BrowserRouter>
  );
}

function TaskFlowInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem("tf_token"));
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("tf_user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) { return null; }
  });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [versions, setVersions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [wikiPages, setWikiPages] = useState([]);
  const [roadmapTasks, setRoadmapTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("tf_theme") || "dark");
  const [lang, setLang] = useState(() => localStorage.getItem("tf_lang") || "de");
  const [isMenuOpen, setIsMenuOpen] = useState(true); 
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleLang = () => { const n = lang === "de" ? "en" : "de"; setLang(n); localStorage.setItem("tf_lang", n); };
  const toggleTheme = () => { const n = theme === "dark" ? "light" : "dark"; setTheme(n); localStorage.setItem("tf_theme", n); }; 
  const t = useT(lang);
  const tPrio = (lbl) => t?.[lbl?.toLowerCase()] || lbl;
  const T = theme === "dark" ? DARK : LIGHT;
  const isEN = lang === "en";
  const socketRef = useRef(null);

  // 🚀 Echte URLs statt lokalem State (Einmalig reicht!)
  const activeSection = location.pathname === '/' ? 'myTasks' : location.pathname.substring(1).split('/')[0];
  
  const setActiveSection = useCallback((section) => {
    navigate(`/${section}`);
  }, [navigate]);

  useEffect(() => {
    localStorage.setItem("tf_last_tab", activeSection);
  }, [activeSection]);
  const [activeProjectId,setActiveProjectId]=useState(null);
  const [filterStatus,setFilterStatus]=useState("Alle");
  const [filterTag,setFilterTag]=useState(null);
const [filterAssignee, setFilterAssignee] = useState("Alle"); // Für Projekte & Beta
  const [myFilterStatus, setMyFilterStatus] = useState("Alle"); // Für "Meine Aufgaben"
  const [myFilterTag, setMyFilterTag] = useState("Alle");       // Für "Meine Aufgaben"loadInitialData
const [myFilterProject, setMyFilterProject] = useState("Alle");
  const [filterMilestone,setFilterMilestone]=useState(null);
  const [searchQuery,setSearchQuery]=useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [cmdOpen, setCmdOpen] = useState(false);
  const [cmdSearch, setCmdSearch] = useState("");
const [systemLogs, setSystemLogs] = useState([]);
  const [showSystemFeedback, setShowSystemFeedback] = useState(false);
  const [sysFeedbackForm, setSysFeedbackForm] = useState({ type: "bug", title: "", description: "" });
const [systemFeatures, setSystemFeatures] = useState({ feature_nl2: true, feature_roadmap: true, feature_beta: true, feature_polls: true, feature_wiki: true });
const isDiscordActive = systemFeatures.feature_discord !== false && systemFeatures.discord_enabled !== false;

// 🚀 DER FLUGSCHREIBER: Hört unsichtbar auf alle Fehler in der App
  useEffect(() => {
    const logError = (msg) => {
      setSystemLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 8)); // Speichert immer die letzten 8
    };
    
    window.onerror = (msg, url, line) => logError(`UI Error: ${msg} (Line ${line})`);
    window.addEventListener('unhandledrejection', (e) => logError(`Promise Rejection: ${e.reason}`));
    window.addEventListener('tf_system_error', (e) => logError(e.detail));
    
    return () => {
      window.onerror = null;
      window.removeEventListener('tf_system_error', logError);
    };
  }, []);

  const cmdInputRef = useRef(null);
  const [projectView,setProjectView]=useState("list");
  const [showArchive,setShowArchive]=useState(false);
  const [archiveTasks,setArchiveTasks]=useState([]);
  const [modal,setModal]=useState(null);
  const [editTarget,setEditTarget]=useState(null);
  const [editAutoTarget,setEditAutoTarget]=useState(null); 
  const [detailTask,setDetailTask]=useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [detailTab, setDetailTab] = useState("comments"); // Steuert den Tab (Kommentare vs Aktivität)
  const [taskForm,setTaskForm]=useState({title:"",desc:"",status:"Offen",priority:2,assignees:[],checklist:[],tagIds:[],milestone_id:null,depends_on:[], version_id: null, due_date: "", parent_task_id: null});
  // 🚀 Standard-Layout für das Drag & Drop Dashboard
  const defaultDashboardLayout = {
    lg: [
      { i: 'chart-status', x: 0, y: 0, w: 6, h: 3, minW: 4, minH: 3 },
      { i: 'chart-project', x: 6, y: 0, w: 6, h: 3, minW: 4, minH: 3 },
      { i: 'progress', x: 0, y: 3, w: 7, h: 4, minW: 4, minH: 3 },
      { i: 'tags', x: 7, y: 3, w: 5, h: 4, minW: 3, minH: 3 },
      { i: 'users', x: 0, y: 7, w: 12, h: 4, minW: 6, minH: 3 }
    ]
  };
  // 🚀 Native Drag & Drop Reihenfolge für das Dashboard
  const [dashOrder, setDashOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tf_dash_order")) || ["chart-status", "chart-project", "progress", "tags", "users"]; }
    catch { return ["chart-status", "chart-project", "progress", "tags", "users"]; }
  });
  const [dashDragOver, setDashDragOver] = useState(null);;  
const [dndMenuOpen, setDndMenuOpen] = useState(false);
  const toggleDND = async (hours) => {
    try {
      const res = await api("/auth/dnd", { method: "POST", body: JSON.stringify({ hours }) });
      setCurrentUser(u => ({...u, dnd_until: res.dnd_until}));
      setDndMenuOpen(false);
      notify(hours ? `Ruhemodus für ${hours}h aktiviert 🌙` : "Ruhemodus beendet 🔔");
    } catch(e) { notify("Fehler: " + e.message); }
  };
// 🚀 NEU: Speicher für die letzten Suchanfragen
  const [recentSearches, setRecentSearches] = useState(() => { 
    try { return JSON.parse(localStorage.getItem("tf_recent_searches"))||[]; } 
    catch{ return []; } 
  });

  // 🚀 NEU: Funktion zum Hinzufügen zur Historie
  const addRecentSearch = (item) => {
    const updated = [item, ...recentSearches.filter(r => r.id !== item.id || r.type !== item.type)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("tf_recent_searches", JSON.stringify(updated));
  };
  const [userForm,setUserForm]=useState({name:"",email:"",username:"",password:"",role:"Nutzer",color:AVATAR_COLORS[0],extra_permissions:{can_create_projects:true,can_create_automations:true}});
  const [teamForm,setTeamForm]=useState({name:"",color:TEAM_COLORS[0],icon:"🚀",memberIds:[]});
  const [projectForm,setProjectForm]=useState({name:"",icon:"🌐",color:PROJECT_COLORS[0]});
  const [permForm,setPermForm]=useState({});
  const [statusForm,setStatusForm]=useState([]);
  const [tagForm,setTagForm]=useState({name:"",color:TAG_COLORS[0]});
  const [editTagTarget,setEditTagTarget]=useState(null);
  const [autoForm,setAutoForm]=useState({trigger_type:"status_change",trigger_value:"",action_type:"assign_user",action_value:"",label:""});
  const [automations,setAutomations]=useState([]);
  const [milestoneForm,setMilestoneForm]=useState({name:"",description:"",due_date:"",color:"#0BACBD"});
  const [milestones,setMilestones]=useState([]);
  const [unreadChat,setUnreadChat]=useState({});
  const [storageData,setStorageData]=useState(null);
  const [reportingData,setReportingData]=useState(null);
  const [reportingTab,setReportingTab]=useState("stats");
  const [adminTab, setAdminTab] = useState("users"); 
  const [rpId,setRpId]=useState(""); 
  const [auditLog,setAuditLog]=useState([]);
  const [backups,setBackups]=useState([]);
  const [betaFeedback,setBetaFeedback]=useState([]);
  const [notifications,setNotifications]=useState([]);
  const [showNotifPanel,setShowNotifPanel]=useState(false);
  const [commentText,setCommentText]=useState("");
  const [newCheckItem,setNewCheckItem]=useState("");
  const [notification,setNotification]=useState(null);
  const [oneTimePassword,setOneTimePassword]=useState(null);
  const [settingsForm,setSettingsForm]=useState({name:"",username:"",discord_id:"",color:AVATAR_COLORS[0],currentPassword:"",newPassword:"",newPassword2:"",notif_task_assigned:true,notif_status_change:true,notif_comment:true,notif_mention:true,notif_beta:true});
  const [changePwForm,setChangePwForm]=useState({newPassword:"",newPassword2:""});
  const [chatMessages,setChatMessages]=useState({});
  const [chatText,setChatText]=useState("");
  const [uploadingFile,setUploadingFile]=useState(false);
  const [driveEmbeds,setDriveEmbeds]=useState([]);
  const avatarInputRef = useRef(null);
  const [driveForm,setDriveForm]=useState({title:"",url:"",project_id:null});
  const [nl2Reservation,setNl2Reservation]=useState(null);
  const [nl2ActivePrompt,setNl2ActivePrompt]=useState(false);
  const [nl2Log,setNl2Log]=useState([]);
  const [nl2Betas,setNl2Betas]=useState([]);
  const [nl2Settings,setNl2Settings]=useState({});
  const [nl2ReleaseNote,setNl2ReleaseNote]=useState({modelling:"",scripting:"",others:""});
  const [nl2LogText,setNl2LogText]=useState("");
  const [nl2LogType,setNl2LogType]=useState("modelling");
  const [nl2BetaForm,setNl2BetaForm]=useState({version:"",changelog:"",links:[{label:"",url:""}]});
  const [nl2BetaFile,setNl2BetaFile]=useState(null);
  const [nl2Uploading,setNl2Uploading]=useState(false);
  const [nl2UploadProgress,setNl2UploadProgress]=useState(0);
  const [betaTeam,setBetaTeam]=useState([]);
  const [betaTeamOverride,setBetaTeamOverride]=useState([]);
  const [rolePerms,setRolePerms]=useState({}); 
  const [formSettings,setFormSettings]=useState(null); 
  const [feedbackForm,setFeedbackForm]=useState({beta_version:"",type:"bug",title:"",description:"",steps:"",severity:"medium"});
  const chatBottomRef=useRef(null);
  const commentAreaRef=useRef(null);
  const fileInputRef=useRef(null);
  const betaFileRef=useRef(null);
  const iS=makeIS(T);
  const notify=msg=>{setNotification(msg);setTimeout(()=>setNotification(null),2500);};
  
// 🚀 KUGELSICHERES ÖFFNEN VON AUFGABEN
  const handleOpenTask = useCallback((task) => {
    const proj = projects.find(p => String(p.id) === String(task.project_id) || String(p.id) === String(task.projectId));

    if (!proj) {
      console.warn("Projekt für diese Aufgabe nicht gefunden!", task);
      return;
    }

    setActiveProjectId(proj.id);
    setDetailTask(task);

    if (proj.name.toLowerCase().includes("beta")) {
      navigate("/beta", { replace: true });
    } else {
      navigate("/tasks", { replace: true });
    }
  }, [projects, navigate]);

  // 🚀 1. CLIENT-SIDE REDIRECT (Fängt den Link ab, falls der Browser-Cache ihn blockiert)
  useEffect(() => {
    if (location.pathname.includes('/api/share')) {
      const params = new URLSearchParams(location.search);
      const type = params.get("type");
      const id = params.get("id");
      
      if (type === "task") navigate(`/tasks?taskId=${id}`, { replace: true });
      else if (type === "idea") navigate(`/polls?ideaId=${id}`, { replace: true });
      else if (type === "poll") navigate(`/polls?pollId=${id}`, { replace: true });
      else navigate("/", { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // 🚀 VERBESSERTES DEEP LINKING FÜR AUFGABEN (Löst den Lade-Deadlock!)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get("taskId");
    
    // Wir warten NUR noch auf die Projekte, nicht mehr auf allTasks!
    if (taskIdParam && projects.length > 0) {
      const idNum = parseInt(taskIdParam);
      
      if (detailTask?.id !== idNum) {
        // 1. Ist die Aufgabe zufällig schon im Arbeitsspeicher?
        const existingTask = allTasks.find(x => String(x.id) === String(idNum));
        
        if (existingTask) {
          handleOpenTask(existingTask);
        } else {
          // 2. WENN NICHT: Wir laden die Aufgaben jetzt aktiv per API nach!
          api('/tasks/all-active')
            .then(activeTasks => {
              setAllTasks(activeTasks); // Speichern für später
              const foundTask = activeTasks.find(x => String(x.id) === String(idNum));
              
              if (foundTask) {
                handleOpenTask(foundTask);
              } else {
                // Aufgabe existiert wirklich nicht mehr (gelöscht/archiviert)
                notify("Aufgabe nicht gefunden (evtl. archiviert oder gelöscht).");
                navigate("/tasks", { replace: true });
              }
            })
            .catch(() => navigate("/tasks", { replace: true }));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.search]); // 'allTasks' wurde hier absichtlich entfernt!

  const pushNotify = (title, body, icon='⚡') => {
    const isDnd = currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date();
    if (!isDnd) notify(title + (body ? ': ' + body.slice(0,50) : ''));
    if (!isDnd && 'Notification' in window && Notification.permission === 'granted') {
      try { new Notification('TaskFlow · ' + title, { body: body||'', icon: '/favicon.ico', tag: 'taskflow-' + Date.now(), requireInteraction: false }); } catch(e) {}
    }
  };
  
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);
  
  const closeModal=()=>{setModal(null);setEditTarget(null);setEditAutoTarget(null);};
  const api=useCallback((path,options={})=>apiFetch(path,options,token),[token]);
  const handleLogin = (tok, user) => {
    setToken(tok);
    setCurrentUser(user);
    localStorage.setItem("tf_token", tok);
    localStorage.setItem("tf_user", JSON.stringify(user)); 
    if (user.must_change_password) {
      setChangePwForm({newPassword: "", newPassword2: ""});
      setModal("change_password");
    }
  };

 const logout = () => {
    localStorage.removeItem("tf_token");
    localStorage.removeItem("tf_user");
    setToken(null);
    setCurrentUser(null);
  };

  const toggleMaintenance = async () => {
    try {
      const res = await api('/system/status', {
        method: 'PUT',
        body: JSON.stringify({ maintenance_mode: !maintenanceMode })
      });
      notify(res.maintenance_mode ? "Wartungsmodus AKTIVIERT 🚧" : "System wieder ONLINE 🟢");
    } catch (e) {
      notify("Fehler: " + e.message);
    }
  };

  const updateTaskState = useCallback((taskId, updater) => {
    setTasks(ts => ts.map(t => t.id === taskId ? updater(t) : t));
    setAllTasks(ts => ts.map(t => t.id === taskId ? updater(t) : t));
  }, []);

const [tickerSettings, setTickerSettings] = useState({ ticker_admin: true, ticker_beta: true, ticker_poll: true, ticker_nl2: true, ticker_roadmap: true, ticker_wiki: true });

  const loadTickerSettings = async () => {
    try {
      const d = await api("/system/ticker-settings");
      setTickerSettings(d);
    } catch(e) { console.log(e.message); }
  };

  const toggleTickerSetting = async (key) => {
    const newVal = !tickerSettings[key];
    const newSettings = { ...tickerSettings, [key]: newVal };
    setTickerSettings(newSettings); // Direkt in der UI aktualisieren
    try {
      await api("/system/ticker-settings", { method: "PUT", body: JSON.stringify(newSettings) });
      notify("Ticker-Einstellung gespeichert ✅");
    } catch(e) {
      notify("Fehler: " + e.message);
      setTickerSettings({ ...tickerSettings, [key]: !newVal }); // Bei Fehler zurücksetzen
    }
  };

useEffect(() => {
    if (!token) return;
    if (!socketRef.current) {
      // 🚀 Zwingt das Frontend, über die API-Route zu funken, die der Nginx-Proxy bereits kennt
      socketRef.current = socketIO({ 
        auth: { token },
        path: '/api/socket.io'
      });
      
      socketRef.current.on('connect', () => console.log("🟢 Socket verbunden!"));
      socketRef.current.on('connect_error', err => console.log("   Socket-Fehler:", err.message));
      
      socketRef.current.on('online_users', users => {
        console.log("📡 Online-User empfangen:", users);
        window.__ONLINE_USERS = users;
        window.dispatchEvent(new Event('online_users_changed'));
      });
    }
    return () => { if(socketRef.current) socketRef.current.disconnect(); socketRef.current = null; };
  }, [token]);

// 🚀 LIVE-UPDATES: Das Frontend hört jetzt in Echtzeit auf Änderungen!
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // 1. Eine neue Aufgabe wurde irgendwo erstellt
    const onTaskCreated = (newTask) => {
      setTasks(prev => prev.some(t => t.id === newTask.id) ? prev : [...prev, newTask]);
      setAllTasks(prev => prev.some(t => t.id === newTask.id) ? prev : [...prev, newTask]);
    };

    // 2. Eine Aufgabe wurde bearbeitet oder verschoben (Drag & Drop)
    const onTaskUpdated = (updatedTask) => {
      updateTaskState(updatedTask.id, () => updatedTask);
      
      // Falls wir diese Aufgabe gerade zufällig offen haben -> Live aktualisieren!
      setDetailTask(prev => prev?.id === updatedTask.id ? updatedTask : prev);
    };

    // 3. Eine Aufgabe wurde gelöscht
    const onTaskDeleted = ({ id }) => {
      setTasks(prev => prev.filter(t => t.id !== id));
      setAllTasks(prev => prev.filter(t => t.id !== id));
      setDetailTask(prev => prev?.id === id ? null : prev); // Detailfenster schließen, falls es offen war
    };

    // 4. Eine Chat-Nachricht kam an
    const onChatMessage = (msg) => {
      setChatMessages(prev => {
        const projectChat = prev[msg.project_id] || [];
        if (projectChat.some(m => m.id === msg.id)) return prev;
        return { ...prev, [msg.project_id]: [...projectChat, msg] };
      });
      // Wenn wir nicht in genau diesem Projekt sind -> roten Zähler hochzählen!
      if (activeProjectId !== msg.project_id) {
        setUnreadChat(prev => ({...prev, [msg.project_id]: (prev[msg.project_id] || 0) + 1}));
      }
    };

    // "Ohren" anschalten
    socket.on('global:task_created', onTaskCreated);
    socket.on('global:task_updated', onTaskUpdated);
    socket.on('global:task_deleted', onTaskDeleted);
    socket.on('global:chat_message', onChatMessage);
socket.on('new_broadcast', (broadcast) => {
      pushNotify("Neue System-Nachricht", broadcast.title, "📢");
      // Wenn der Ticker aktiv ist, könnten wir ihn hier live befüllen!
    });
socket.on('broadcast_deleted', (id) => {
      window.dispatchEvent(new CustomEvent('broadcast_deleted', { detail: id }));
    });
socket.on('features_updated', (newFeatures) => {
      setSystemFeatures(prev => ({ ...prev, ...newFeatures }));
      notify("System-Module wurden vom Server aktualisiert! 🔄");
    });
    
    // 🚀 NEU: Hört auf Wiki-Änderungen und informiert den Wiki-Tab
    socket.on('wiki_updated', () => {
      window.dispatchEvent(new Event('wiki_updated'));
      api("/wiki").then(d => setWikiPages(d.pages)).catch(()=>{});
    });

const onChecklistToggled = (item) => {
      setTasks(prev => prev.map(t => t.id === item.task_id ? 
        {...t, checklist: t.checklist.map(c => c.id === item.id ? item : c)} : t));
    };

    const onCommentAdded = (comment) => {
      setTasks(prev => prev.map(t => t.id === comment.task_id ? 
        {...t, comments: [...(t.comments || []), comment]} : t));
      setDetailTask(prev => prev?.id === comment.task_id ? 
        {...prev, comments: [...(prev.comments || []), comment]} : prev);
    };

    socket.on('global:checklist_toggled', onChecklistToggled);
    socket.on('global:comment_added', onCommentAdded);
socket.on('maintenance_changed', (isMaintenance) => {
    setMaintenanceMode(isMaintenance);
  });
    // "Ohren" wieder ausschalten, wenn die Komponente neu lädt
    return () => {
      socket.off('global:task_created', onTaskCreated);
      socket.off('global:task_updated', onTaskUpdated);
      socket.off('global:task_deleted', onTaskDeleted);
      socket.off('global:chat_message', onChatMessage);
      // Hier waren die Fehler: Wir nutzen die Namen der konstanten Funktionen, die wir oben definiert haben
      socket.off('global:checklist_toggled', onChecklistToggled);
      socket.off('global:comment_added', onCommentAdded);
      socket.off('features_updated');
      socket.off('wiki_updated');
socket.off('features_updated');
    };
  }, [updateTaskState, activeProjectId]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const loadInitialData = async () => {
      try {
      const sysStatus = await api("/system/status").catch(() => ({ maintenance_mode: false }));
      setMaintenanceMode(sysStatus.maintenance_mode);

const feats = await api("/system/features").catch(() => null);
      if (feats) setSystemFeatures(feats);

        const [u, t, p, v, rt] = await Promise.all([
          api("/users").catch(() => []),
          // 🚀 NEU: Holt die Teams aus dem neuen System und baut die memberIds für die Projekt-Rechte zusammen!
          api("/workspace/teams")
            .then(res => (res?.teams || []).map(team => ({
              ...team, 
              memberIds: (team.members || []).map(m => m.user_id) 
            })))
            .catch(() => []),
          api("/projects").catch(() => []),
   	      api("/wiki").then(d => setWikiPages(d.pages)).catch(() => {}),
          api("/versions").catch(e => { console.error("Versions Fetch Error:", e); return []; }), 
          api("/roadmap/tasks").catch(e => { console.error("Roadmap Tasks Fetch Error:", e); return []; }) 
        ]);
        
        setUsers(u || []);
        
        // 🚀 FIX 3: Aktualisiert deinen eigenen Account (currentUser) beim Start mit den neuesten DB-Daten!
        if (u && u.length > 0 && currentUser) {
          const freshMe = u.find(x => x.id === currentUser.id);
          if (freshMe) {
            setCurrentUser(freshMe);
            localStorage.setItem("tf_user", JSON.stringify(freshMe));
          }
        }
        
        setTeams(t || []);
        setProjects(p || []);
        setVersions(v || []);
        setRoadmapTasks(rt || []); 
      } catch (e) {
        console.error("Fehler beim initialen Laden:", e);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [token, api]);

  useEffect(() => {
    // 🚀 FIX: Wir warten auf 'loading' (bis die User/Projects wirklich da sind)
    if (!token || loading) return; 
    
    const triggerLoad = async () => {
      if (activeSection === "roadmap" || activeSection === "nl2" || activeSection === "beta") {
        await loadNL2().catch(() => {}); 
        await loadAllTasks().catch(() => {}); 
        if(activeSection === "roadmap") {
           api('/roadmap/tasks').then(setRoadmapTasks).catch(() => {});
           api('/versions').then(setVersions).catch(() => {});
        }
        if(activeSection === "beta") loadFeedback().catch(() => {});
      }

      if (activeSection === "admin") {
        loadAudit().catch(() => {});
        loadBackups().catch(() => {});
	loadTickerSettings().catch(() => {});
      }
      
      if (activeSection === "reporting") {
        loadReporting().catch(() => {});
      }
    };

    triggerLoad();
  }, [activeSection, token, loading]); // 🚀 loading als Abhängigkeit 

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setCmdOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

// 🚀 FIX: Füllt das Einstellungs-Formular automatisch mit den echten Nutzerdaten, 
  // sobald das Modal geöffnet wird. Verhindert das Überschreiben mit leeren Feldern!
  useEffect(() => {
    if (modal === "settings" && currentUser) {
      setSettingsForm({
        name: currentUser.name || "",
        username: currentUser.username || "",
        discord_id: currentUser.discord_id || "",
        color: currentUser.color || AVATAR_COLORS[0],
        currentPassword: "",
        newPassword: "",
        newPassword2: "",
        notif_task_assigned: currentUser.notif_task_assigned !== false,
        notif_status_change: currentUser.notif_status_change !== false,
        notif_comment: currentUser.notif_comment !== false,
        notif_mention: currentUser.notif_mention !== false,
        notif_beta: currentUser.notif_beta !== false
      });
    }
  }, [modal, currentUser]);

  useEffect(() => {
    if (cmdOpen && cmdInputRef.current) {
      cmdInputRef.current.focus();
      setCmdSearch("");
    }
  }, [cmdOpen]);

  useEffect(()=>{
    if(!activeProjectId||!token)return;
    setMobileMenuOpen(false); 
    api(`/projects/${activeProjectId}/tasks`).then(setTasks).catch(()=>{});
    api(`/projects/${activeProjectId}/chat`).then(cd=>setChatMessages(m=>({...m,[activeProjectId]:cd}))).catch(()=>{});
    api(`/projects/${activeProjectId}/milestones`).then(setMilestones).catch(()=>{});
    setProjectView("list");setShowArchive(false);setFilterTag(null);setFilterMilestone(null);
    api(`/drive-embeds?project_id=${activeProjectId}`).then(setDriveEmbeds).catch(()=>{});
  },[activeProjectId, token, api]);

  const lastPollTs = useRef(Date.now());
  useEffect(()=>{
    if(!token)return;
    const poll=setInterval(async()=>{
      try{
        const since = lastPollTs.current;
        const result = await api(`/poll?project_id=${activeProjectId||''}&since=${since}`);
        lastPollTs.current = result.ts || Date.now();
        if(result.tasks_changed && activeProjectId){
          const td=await api(`/projects/${activeProjectId}/tasks`);
          setTasks(td);
        }
        if(result.tasks_changed){ loadAllTasks().catch(()=>{}); }
        if(result.chat_changed && activeProjectId){
          const cd=await api(`/projects/${activeProjectId}/chat`);
          setChatMessages(m=>({...m,[activeProjectId]:cd}));
          const unread=await api('/chat/unread');
          setUnreadChat(unread);
        }
        if(result.new_notifications > 0){
          const notifs=await api('/notifications');
          setNotifications(notifs);
        }
      }catch(e){}
    },10000);
    return ()=>clearInterval(poll);
  }, [activeProjectId, token, api, projects, teams, currentUser]);

useEffect(() => {
    if (detailTask && token) {
      api(`/tasks/${detailTask.id}/history`).then(setTaskHistory).catch(()=>{});
      setDetailTab("comments"); // Beim Öffnen einer Aufgabe immer zuerst Kommentare zeigen
    }
  }, [detailTask, token, api]);

const isAdmin = currentUser?.role === "Admin";
  const canSeeDashboard = isAdmin || currentUser?.role === "Projektleiter";

  useEffect(() => {
    if (activeSection === "reporting" && !canSeeDashboard) {
      setActiveSection("myTasks");
    }
  }, [activeSection, canSeeDashboard]);	

  const canCreateProject=isAdmin||currentUser?.permissions?.can_create_projects!==false;
  const canCreateAutomation=isAdmin||currentUser?.permissions?.can_create_automations!==false;
  const canManageTags=isAdmin||currentUser?.permissions?.can_manage_tags!==false;
  const canManageMilestones=isAdmin||currentUser?.permissions?.can_manage_milestones!==false;
  const canManageForms=isAdmin||currentUser?.permissions?.can_manage_forms!==false;
  const canManagePolls = isAdmin || !!currentUser?.permissions?.can_manage_polls;
  const unreadNotifications = notifications.filter(n=>!n.read);
  const unreadNotifs = unreadNotifications.length;
  
 const getProjectPerm = pid => {
    const p = projects.find(x => x.id === pid);
    if (!p) return "none";
    if (isAdmin || p.created_by === currentUser?.id) return "edit";
    
    // 🚀 FIX: Automatische Rechte für das versteckte Beta-Board!
    // Jeder Nutzer darf die Bugs sehen und eintragen, auch wenn das Projekt versteckt ist.
    if (p.name === "Beta-Reports" || (nl2Settings?.beta_project_id && parseInt(pid) === parseInt(nl2Settings.beta_project_id))) {
      return "edit";
    }
  
    const myTeams = teams.filter(t => t.memberIds?.includes(currentUser?.id));
    let best = "none";
    
    for (const t of myTeams) {
      const perm = p.permissions?.[t.id];
      if (perm === "edit") return "edit"; // Wenn ein Team "edit" hat, sofort fertig.
      if (perm === "view") best = "view";
    }
    return best;
  };

  const activePerm=getProjectPerm(activeProjectId);
  const canEdit=activePerm==="edit";
  const canView=activePerm!=="none";
  const activeProject=projects.find(p=>p.id===activeProjectId);
  const visibleProjects=projects.filter(p=>(isAdmin||getProjectPerm(p.id)!=="none") && p?.name!=="Beta-Reports");
  const projectStatuses=activeProject?.customStatuses?.length?activeProject.customStatuses.map(s=>s.label):DEFAULT_STATUSES;
  const projectTags=activeProject?.tags||[];
  const getStatusMeta=label=>{if(activeProject?.customStatuses?.length){const s=activeProject.customStatuses.find(s=>s.label===label);if(s)return{color:s.color,dot:s.color,bg:s.color+"18"};}return DEFAULT_STATUS_META[label]||{color:"#94A3B8",dot:"#94A3B8",bg:"#94A3B818"};};
  
  const projectTasks=tasks.filter(t=>t.project_id===activeProjectId||t.projectId===activeProjectId);
 const filteredTasks=projectTasks.filter(t=>{
    // 🚀 NEU: Unteraufgaben auf dem Haupt-Board ausblenden!
    if(t.parent_task_id) return false;

    if(filterStatus!=="Alle"&&t.status!==filterStatus)return false;
    if(filterTag&&String(filterTag)!=="Alle"&&!(t.tags||[]).some(tg=>String(tg.id)===String(filterTag)))return false;
    if(filterMilestone&&String(filterMilestone)!=="Alle"&&String(t.milestone_id)!==String(filterMilestone))return false;
    
    if(filterAssignee !== "Alle") {
      const assigneeId = parseInt(filterAssignee, 10);
      const taskAssigns = (t.assignees || []).map(a => typeof a === 'object' ? (a.user_id || a.id) : a);
      if (!taskAssigns.includes(assigneeId)) return false;
    }
    
    if(searchQuery&&!t.title.toLowerCase().includes(searchQuery.toLowerCase()))return false;
    return true;
  });

  const tasksByStatus=projectStatuses.reduce((a,s)=>{a[s]=filteredTasks.filter(t=>t.status===s);return a;},{});
  const stats={total:projectTasks.length,done:projectTasks.filter(t=>t.status===projectStatuses[projectStatuses.length-1]).length,inProgress:projectTasks.filter(t=>t.status==="In Arbeit").length,urgent:projectTasks.filter(t=>t.priority===0).length};
  const getTaskAssigneeIds=task=>(task.assignees||[]).map(a=>a.user_id||a);
  const catMeta={modelling:{icon:"🎨",color:"#EC4899",bg:"#EC489918"},scripting:{icon:"💻",color:"#60A5FA",bg:"#60A5FA18"},others:{icon:"📌",color:"#F59E0B",bg:"#F59E0B18"},manual:{icon:"📌",color:"#F59E0B",bg:"#F59E0B18"},update:{icon:"✅",color:"#34D399",bg:"#34D39918"}};

  const sendChat=async()=>{if(!chatText.trim()||!activeProjectId)return;try{await api(`/projects/${activeProjectId}/chat`,{method:"POST",body:JSON.stringify({text:chatText.trim()})});setChatText("");}catch(e){notify("Fehler: "+e.message);}};
  const markChatRead=async pid=>{try{await api(`/projects/${pid}/chat/read`,{method:"POST"});setUnreadChat(u=>({...u,[pid]:0}));}catch{}};
  const openCreateTask = (parentId = null) => {
    // 🚀 FIX: Wenn parentId ein Event-Objekt vom Button-Klick ist, mache es zu null!
    const actualParentId = (parentId && typeof parentId === 'object') ? null : parentId;

    setEditTarget(null);
    setTaskForm({
      title: "", 
      desc: "", 
      status: projectStatuses[0] || "Offen", 
      priority: 2, 
      assignees: [], 
      checklist: [], 
      tagIds: [], 
      milestone_id: null, 
      depends_on: [], 
      version_id: null, 
      due_date: "", 
      parent_task_id: actualParentId 
    });
    setModal("task");
  };
  const openEditTask=task=>{setEditTarget(task);setTaskForm({title:task.title,desc:task.description||task.desc||"",status:task.status,priority:task.priority,assignees:getTaskAssigneeIds(task),checklist:(task.checklist||[]).map(c=>({...c})),tagIds:(task.tags||[]).map(tg=>tg.id),milestone_id:task.milestone_id||null,depends_on:task.dependencies||[], version_id: task.version_id || null, due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : ""});setModal("task");}; 
 const saveTask = async () => {
    if (!taskForm.title?.trim()) return;
    try {
      const p = {
        title: taskForm.title,
        description: taskForm.desc,
        status: taskForm.status,
        priority: taskForm.priority,
        assignees: taskForm.assignees,
        checklist: taskForm.checklist,
        tagIds: taskForm.tagIds,
        milestone_id: taskForm.milestone_id,
        depends_on: taskForm.depends_on, 
        version_id: taskForm.version_id, 
        due_date: taskForm.due_date || null,
        parent_task_id: taskForm.parent_task_id
      };

      if (editTarget) {
        await api(`/tasks/${editTarget.id}`, { method: "PUT", body: JSON.stringify(p) });
        notify("Aufgabe aktualisiert ✅");
      } else {
        await api(`/projects/${activeProjectId}/tasks`, { method: "POST", body: JSON.stringify(p) });
        notify("Aufgabe erstellt ✅");
      }
      closeModal();
      loadAllTasks(); 
    } catch(e) {
      notify("Fehler beim Speichern: " + e.message);
    }
  };  

  const deleteTask=async id=>{try{await api(`/tasks/${id}`,{method:"DELETE"});}catch(e){notify("Fehler: "+e.message);}};
  const archiveTask=async id=>{if(!window.confirm("Aufgabe archivieren?"))return;try{await api(`/tasks/${id}/archive`,{method:"POST"});notify("Archiviert ✅");}catch(e){notify("Fehler: "+e.message);}};
  // 🚀 LINK TEILEN (Aufgaben)
  const handleShareTask = (e, taskId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/api/share?type=task&id=${taskId}`;
    navigator.clipboard.writeText(url);
    notify(isEN ? "Task link copied! 🔗" : "Aufgaben-Link kopiert! 🔗");
  };

  // 🚀 DEEP LINKING: Öffnet die Aufgabe direkt beim Start, wenn taskId in der URL steht
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get("taskId");
    
    if (taskIdParam && allTasks.length > 0) {
      const idNum = parseInt(taskIdParam);
      // Öffnet die Aufgabe nur, wenn sie nicht sowieso schon offen ist
      if (detailTask?.id !== idNum) {
        const t = allTasks.find(x => x.id === idNum);
        if (t) handleOpenTask(t);
      }
    }
  }, [allTasks, location.search]);
  const toggleFocus = async (task) => {
    try {
      const isFocused = (task.focused_by || []).includes(currentUser?.id);
      const newVal = !isFocused;
      await api(`/tasks/${task.id}/focus`, { method: "PATCH", body: JSON.stringify({ focus_today: newVal }) });
      
      updateTaskState(task.id, t => {
        const currentFocus = t.focused_by || [];
        return {
          ...t, 
          focused_by: newVal ? [...currentFocus, currentUser?.id] : currentFocus.filter(id => id !== currentUser?.id)
        };
      });
      
      notify(newVal ? (isEN ? "Added to My Day ☀️" : "Zu 'Mein Tag' hinzugefügt ☀️") : (isEN ? "Removed from My Day" : "Von Tagesliste entfernt"));
    } catch(e) { notify("Fehler: " + e.message); }
  };
  
const updateTaskStatus=async(taskId,status)=>{try{const task=tasks.find(t=>t.id===taskId);if(!task)return;await api(`/tasks/${taskId}`,{method:"PUT",body:JSON.stringify({title:task.title,description:task.description||"",status,priority:task.priority,assignees:getTaskAssigneeIds(task),checklist:task.checklist||[],tagIds:(task.tags||[]).map(tg=>tg.id),milestone_id:task.milestone_id,depends_on:task.dependencies||[],version_id:task.version_id||null,due_date:task.due_date||null})});}catch(e){notify("Fehler: "+e.message);}};
  const addComment=async()=>{if(!commentText.trim()||!detailTask)return;try{await api(`/tasks/${detailTask.id}/comments`,{method:"POST",body:JSON.stringify({text:commentText.trim()})});setCommentText("");}catch(e){notify("Fehler: "+e.message);}};
  const toggleCheck=async(taskId,itemId)=>{try{await api(`/checklist/${itemId}/toggle`,{method:"PATCH"});}catch(e){notify("Fehler: "+e.message);}};
  const addCheckToDetail=async()=>{if(!newCheckItem.trim()||!detailTask)return;try{const task=tasks.find(t=>t.id===detailTask.id);await api(`/tasks/${detailTask.id}`,{method:"PUT",body:JSON.stringify({title:task.title,description:task.description||"",status:task.status,priority:task.priority,assignees:getTaskAssigneeIds(task),checklist:[...(task.checklist||[]),{text:newCheckItem.trim(),done:false}],tagIds:(task.tags||[]).map(tg=>tg.id),milestone_id:task.milestone_id,depends_on:task.dependencies||[]})});setNewCheckItem("");}catch(e){notify("Fehler: "+e.message);}};
  const uploadFile=async file=>{if(!detailTask||!file)return;setUploadingFile(true);try{const fd=new FormData();fd.append("file",file);const f=await apiFetch(`/tasks/${detailTask.id}/files`,{method:"POST",body:fd},token);setTasks(ts=>ts.map(t=>t.id===detailTask.id?{...t,files:[...(t.files||[]),f]}:t));notify("Hochgeladen ✅");}catch(e){notify("Fehler: "+e.message);}finally{setUploadingFile(false);}};
  const deleteFile=async id=>{try{await api(`/files/${id}`,{method:"DELETE"});setTasks(ts=>ts.map(t=>({...t,files:(t.files||[]).filter(f=>f.id!==id)})));}catch(e){notify("Fehler: "+e.message);}};
const saveDriveEmbed=async()=>{if(!driveForm.url.trim())return notify("URL fehlt");try{const res=await api("/drive-embeds",{method:"POST",body:JSON.stringify({...driveForm,project_id:activeProjectId})});setDriveEmbeds([res,...driveEmbeds]);closeModal();setDriveForm({title:"",url:"",project_id:null});notify("Dokument eingebunden ✅");}catch(e){notify("Fehler: "+e.message);}};
  const deleteDriveEmbed=async id=>{if(!window.confirm("Dokument entfernen?"))return;try{await api(`/drive-embeds/${id}`,{method:"DELETE"});setDriveEmbeds(d=>d.filter(x=>x.id!==id));}catch(e){notify("Fehler: "+e.message);}};  
const generateOTP=()=>{const c="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";return Array.from({length:12},()=>c[Math.floor(Math.random()*c.length)]).join("");};
  const openCreateUser=()=>{const otp=generateOTP();setEditTarget(null);setUserForm({name:"",email:"",username:"",password:otp,role:"Nutzer",color:AVATAR_COLORS[0],extra_permissions:{can_create_projects:true,can_create_automations:true}});setOneTimePassword(otp);setModal("user");};
  const openEditUser=u=>{setEditTarget(u);setUserForm({name:u.name,email:u.email,username:u.username||"",password:"",role:u.role,color:u.color,extra_permissions:u.extra_permissions||{}});setOneTimePassword(null);setModal("user");};
  const saveUser=async()=>{if(!userForm.name.trim())return;try{if(editTarget){const u=await api(`/users/${editTarget.id}`,{method:"PUT",body:JSON.stringify(userForm)});setUsers(us=>us.map(x=>x.id===editTarget.id?u:x));notify("Nutzer aktualisiert ✅");closeModal();}else{await api("/users",{method:"POST",body:JSON.stringify({...userForm,must_change_password:true})});const us=await api("/users");setUsers(us);setModal("user_otp");}}catch(e){notify("Fehler: "+e.message);}};
  const deleteUser=async id=>{if(!window.confirm("Löschen?"))return;try{await api(`/users/${id}`,{method:"DELETE"});}catch(e){notify("Fehler: "+e.message);}};
  const openCreateTeam=()=>{setEditTarget(null);setTeamForm({name:"",color:TEAM_COLORS[0],icon:"🚀",memberIds:[]});setModal("team");};
  const openEditTeam=t=>{setEditTarget(t);setTeamForm({name:t.name,color:t.color,icon:t.icon,memberIds:[...(t.memberIds||[])]});setModal("team");};
  const saveTeam=async()=>{if(!teamForm.name.trim())return;try{if(editTarget){const u=await api(`/teams/${editTarget.id}`,{method:"PUT",body:JSON.stringify(teamForm)});setTeams(ts=>ts.map(t=>t.id===editTarget.id?u:t));notify("Team aktualisiert ✅");}else{const c=await api("/teams",{method:"POST",body:JSON.stringify(teamForm)});setTeams(ts=>[...ts,c]);notify("Team erstellt ✅");}closeModal();}catch(e){notify("Fehler: "+e.message);}};
  const deleteTeam=async id=>{try{await api(`/teams/${id}`,{method:"DELETE"});setTeams(ts=>ts.filter(t=>t.id!==id));}catch(e){notify("Fehler: "+e.message);}};
  const deleteProject = async (id) => {
    if(!window.confirm("⚠ ACHTUNG: Willst du dieses Projekt wirklich löschen? Alle Aufgaben, Chats und Dateien gehen dauerhaft verloren!")) return;
    try {
      await api("/projects/"+id, { method: "DELETE" });
      setProjects(ps => ps.filter(p => p.id !== id));
      notify("Projekt erfolgreich gelöscht ✅");
      if(activeProjectId === id) { 
        setActiveProjectId(null); 
        setActiveSection("reporting"); 
      }
      closeModal();
    } catch(e) { notify("Fehler: " + e.message); }
  };
  const openCreateProject=()=>{setEditTarget(null);setProjectForm({name:"",icon:"🌐",color:PROJECT_COLORS[0]});setModal("project");};
  const openEditProject=(p,e)=>{if(e)e.stopPropagation();setEditTarget(p);setProjectForm({name:p.name,icon:p.icon,color:p.color});setModal("project");};
  const saveProject=async()=>{if(!projectForm.name.trim())return;try{if(editTarget){await api(`/projects/${editTarget.id}`,{method:"PUT",body:JSON.stringify(projectForm)});closeModal();notify("Projekt aktualisiert ✅");}else{const c=await api("/projects",{method:"POST",body:JSON.stringify(projectForm)});setActiveProjectId(c.id);setTasks([]);closeModal();notify("Projekt erstellt ✅");}}catch(e){notify("Fehler: "+e.message);}};
  const openPermissions=proj=>{setEditTarget(proj);const init={};teams.forEach(t=>{init[t.id]=proj.permissions?.[t.id]||"none";});setPermForm(init);setModal("permissions");};
  const savePermissions=async()=>{try{await api(`/projects/${editTarget.id}/permissions`,{method:"PUT",body:JSON.stringify({permissions:permForm})});setProjects(ps=>ps.map(p=>p.id===editTarget.id?{...p,permissions:{...permForm}}:p));closeModal();notify("Gespeichert ✅");}catch(e){notify("Fehler: "+e.message);}};
  const openStatuses=(proj,e)=>{if(e)e.stopPropagation();setEditTarget(proj);const ss=proj.customStatuses?.length?proj.customStatuses:DEFAULT_STATUSES.map((l,i)=>({label:l,color:Object.values(DEFAULT_STATUS_META)[i]?.dot||"#94A3B8",sort_order:i}));setStatusForm(ss.map(s=>({...s})));setModal("statuses");};
  const saveStatuses=async()=>{try{const u=await api(`/projects/${editTarget.id}/statuses`,{method:"PUT",body:JSON.stringify({statuses:statusForm})});setProjects(ps=>ps.map(p=>p.id===editTarget.id?{...p,customStatuses:u}:p));closeModal();notify("Status gespeichert ✅");}catch(e){notify("Fehler: "+e.message);}};
  const openTags=()=>{setTagForm({name:"",color:TAG_COLORS[0]});setEditTagTarget(null);setModal("tags");};
  const saveTag=async()=>{if(!tagForm.name.trim())return;try{if(editTagTarget){const u=await api(`/tags/${editTagTarget.id}`,{method:"PUT",body:JSON.stringify(tagForm)});setProjects(ps=>ps.map(p=>p.id===activeProjectId?{...p,tags:(p.tags||[]).map(tg=>tg.id===editTagTarget.id?u:tg)}:p));setEditTagTarget(null);}else{const c=await api(`/projects/${activeProjectId}/tags`,{method:"POST",body:JSON.stringify(tagForm)});setProjects(ps=>ps.map(p=>p.id===activeProjectId?{...p,tags:[...(p.tags||[]).filter(tg=>tg.name!==c.name),c]}:p));}setTagForm({name:"",color:TAG_COLORS[0]});notify("Tag gespeichert ✅");}catch(e){notify("Fehler: "+e.message);}};
  const deleteTag=async id=>{try{await api(`/tags/${id}`,{method:"DELETE"});setProjects(ps=>ps.map(p=>p.id===activeProjectId?{...p,tags:(p.tags||[]).filter(tg=>tg.id!==id)}:p));}catch(e){notify("Fehler: "+e.message);}};
  const openMilestones=async()=>{try{const ms=await api(`/projects/${activeProjectId}/milestones`);setMilestones(ms);setMilestoneForm({name:"",description:"",due_date:"",color:"#0BACBD"});setModal("milestones");}catch(e){notify("Fehler: "+e.message);}};
  const saveMilestone=async()=>{if(!milestoneForm.name.trim())return;try{const c=await api(`/projects/${activeProjectId}/milestones`,{method:"POST",body:JSON.stringify(milestoneForm)});setMilestones(m=>[...m,c]);setMilestoneForm({name:"",description:"",due_date:"",color:"#0BACBD"});notify("Milestone erstellt ✅");}catch(e){notify("Fehler: "+e.message);}};
  const deleteMilestone=async id=>{try{await api(`/milestones/${id}`,{method:"DELETE"});setMilestones(m=>m.filter(x=>x.id!==id));}catch(e){notify("Fehler: "+e.message);}};
  const openAutomations=async()=>{try{const d=await api(`/projects/${activeProjectId}/automations`);setAutomations(d);setAutoForm({trigger_type:"status_change",trigger_value:projectStatuses[0]||"",action_type:"assign_user",action_value:"",label:""});setEditAutoTarget(null);setModal("automations");}catch(e){notify("Fehler: "+e.message);}};
  const saveAutomation=async()=>{try{
    if(editAutoTarget){
      const u=await api(`/automations/${editAutoTarget.id}`,{method:"PUT",body:JSON.stringify(autoForm)});
      setAutomations(a=>a.map(x=>x.id===editAutoTarget.id?u:x));
      notify("Automation aktualisiert ✅");
      setEditAutoTarget(null);
    } else {
      const c=await api(`/projects/${activeProjectId}/automations`,{method:"POST",body:JSON.stringify(autoForm)});
      setAutomations(a=>[...a,c]);
      notify("Automation erstellt ✅");
    }
    setAutoForm({trigger_type:"status_change",trigger_value:projectStatuses[0]||"",action_type:"assign_user",action_value:"",label:""});
  }catch(e){notify("Fehler: "+e.message);}};
  const deleteAutomation=async id=>{try{await api(`/automations/${id}`,{method:"DELETE"});setAutomations(a=>a.filter(x=>x.id!==id));}catch(e){notify("Fehler: "+e.message);}};
  const toggleAutomation=async id=>{try{const u=await api(`/automations/${id}/toggle`,{method:"PATCH"});setAutomations(a=>a.map(x=>x.id===id?u:x));}catch(e){notify("Fehler: "+e.message);}};
  const uploadAvatar=async(file)=>{
    if(!file)return;
    setUploadingFile(true);
    try{
      const fd=new FormData();
      fd.append("avatar",file);
      const r=await apiFetch(`/users/${currentUser.id}/avatar`,{method:"POST",body:fd},token);
      
      // 🚀 FIX 1: Lokalen Speicher im Browser sofort updaten!
      const updatedUser = { ...currentUser, avatar_url: r.avatar_url };
      setCurrentUser(updatedUser);
      localStorage.setItem("tf_user", JSON.stringify(updatedUser));
      setUsers(us=>us.map(u=>u.id===currentUser.id?updatedUser:u));
      
      notify("Avatar hochgeladen ✅");
    }catch(e){notify("Fehler: "+e.message);}
    finally{setUploadingFile(false);}
  };
  
  const deleteAvatar=async()=>{
    try{
      await api(`/users/${currentUser.id}/avatar`,{method:"DELETE"});
      
      // 🚀 FIX 2: Lokalen Speicher auch beim Löschen updaten!
      const updatedUser = { ...currentUser, avatar_url: null };
      setCurrentUser(updatedUser);
      localStorage.setItem("tf_user", JSON.stringify(updatedUser));
      setUsers(us=>us.map(u=>u.id===currentUser.id?updatedUser:u));
      
      notify("Avatar entfernt");
    }catch(e){notify("Fehler: "+e.message);}};  const saveSettings=async()=>{
    try{
      const payload={name:settingsForm.name,username:settingsForm.username,discord_id:settingsForm.discord_id,color:settingsForm.color,notif_task_assigned:settingsForm.notif_task_assigned!==false,notif_status_change:settingsForm.notif_status_change!==false,notif_comment:settingsForm.notif_comment!==false,notif_mention:settingsForm.notif_mention!==false,notif_beta:settingsForm.notif_beta!==false};
      if(settingsForm.newPassword){
        if(settingsForm.newPassword!==settingsForm.newPassword2){notify("Passwörter stimmen nicht überein");return;}
        if(settingsForm.newPassword.length<6){notify("Passwort zu kurz");return;}
        payload.currentPassword=settingsForm.currentPassword;
        payload.newPassword=settingsForm.newPassword;
      }
      const u=await api("/users/"+currentUser.id,{method:"PUT",body:JSON.stringify(payload)});
      setCurrentUser(x=>({...x,...u}));
      setUsers(us=>us.map(x=>x.id===currentUser.id?{...x,...u}:x));
      setSettingsForm(f=>({...f,currentPassword:"",newPassword:"",newPassword2:""}));
      closeModal();
      notify("Gespeichert ✅");
    }catch(e){notify("Fehler: "+e.message);}
  };
  const changePassword=async()=>{if(changePwForm.newPassword!==changePwForm.newPassword2){notify("Passwörter stimmen nicht überein");return;}if(changePwForm.newPassword.length<6){notify("Zu kurz");return;}try{await api(`/users/${currentUser.id}`,{method:"PUT",body:JSON.stringify({name:currentUser.name,username:currentUser.username,email:currentUser.email,color:currentUser.color,newPassword:changePwForm.newPassword,must_change_password:false})});setCurrentUser(u=>({...u,must_change_password:false}));setModal(null);notify("Passwort geändert ✅");}catch(e){notify("Fehler: "+e.message);}};
  const markAllRead=async()=>{try{await api("/notifications/read",{method:"POST"});setNotifications(ns=>ns.map(n=>({...n,read:true})));}catch{}};
  const loadAllTasks = async () => {
    try {
      // 🚀 Ein einziger Request anstelle von hunderten!
      const allActiveTasks = await api('/tasks/all-active');
      setAllTasks(allActiveTasks);
    } catch (e) {
      console.error("loadAllTasks:", e.message);
    }
  };
  const loadNL2=async()=>{try{const [res,log,betas,settings,team]=await Promise.all([api("/nl2/reservation"),api("/nl2/log"),api("/nl2/betas"),api("/nl2/settings"),api("/nl2/beta-team").catch(()=>[])]);setNl2Reservation(res);setNl2Log(log);setNl2Betas(betas);setNl2Settings(settings);setBetaTeam(team||[]);}catch(e){notify("Fehler: "+e.message);}};
  const nl2Reserve=async()=>{try{const r=await api("/nl2/reserve",{method:"POST"});setNl2Reservation({...r,user_name:currentUser.name,user_color:currentUser.color,user_id:currentUser.id});notify("Reserviert ✅");}catch(e){notify("Fehler: "+e.message);}};
const nl2Release=async()=>{
    const {modelling,scripting,others}=nl2ReleaseNote;
    // 🚀 NEU: EINES der drei Felder reicht jetzt aus!
    if(!modelling.trim() && !scripting.trim() && !others.trim()){
      notify("Mindestens einen Bereich ausfüllen");
      return;
    }
    try{
      await api("/nl2/release",{method:"POST",body:JSON.stringify({modelling,scripting,others})});
      setNl2Reservation(null);
      setNl2ReleaseNote({modelling:"",scripting:"",others:""});
      await loadNL2();
      notify("Freigegeben ✅");
    }catch(e){
      notify("Fehler: "+e.message);
    }
  };  
const nl2AddLog=async()=>{if(!nl2LogText.trim())return;try{const r=await api("/nl2/log",{method:"POST",body:JSON.stringify({text:nl2LogText,type:nl2LogType})});setNl2Log(l=>[r,...l]);setNl2LogText("");notify("Eintrag hinzugefügt ✅");}catch(e){notify("Fehler: "+e.message);}};
  const nl2DeleteLog=async id=>{try{await api(`/nl2/log/${id}`,{method:"DELETE"});setNl2Log(l=>l.filter(x=>x.id!==id));}catch(e){notify("Fehler: "+e.message);}};
  const nl2ReleaseBeta=async()=>{
    if(!nl2BetaForm.version.trim()){notify("Version erforderlich");return;}
    setNl2Uploading(true);
    try{
      await api("/nl2/beta",{method:"POST",body:JSON.stringify({
        version:nl2BetaForm.version,
        changelog:nl2BetaForm.changelog,
        download_links:JSON.stringify(nl2BetaForm.links||[]),
        notify_user_ids:JSON.stringify(betaTeamOverride)
      })});
      await loadNL2();closeModal();
      setNl2BetaForm({version:"",changelog:"",links:[{label:"",url:""}]});
      setBetaTeamOverride([]);
      notify("Beta veröffentlicht ✅");
    }catch(e){notify("Fehler: "+e.message);}
    finally{setNl2Uploading(false);}
  };
  const nl2DeleteBeta=async id=>{if(!window.confirm("Beta löschen?"))return;try{await api(`/nl2/beta/${id}`,{method:"DELETE"});setNl2Betas(b=>b.filter(x=>x.id!==id));}catch(e){notify("Fehler: "+e.message);}};
  const loadReporting=async(pid)=>{
    try{
      let url="/reporting/overview";
      if(pid){url+="?project_id="+pid;}
      else if(!isAdmin){
        const ids=projects.filter(p=>getProjectPerm(p.id)!=="none").map(p=>p.id);
        if(ids.length)url+="?project_ids="+ids.join(",");
      }
      const d=await api(url);
      setReportingData(d);
    }catch(e){notify("Fehler: "+e.message);}
  };
  const loadAudit=async()=>{try{const d=await api("/audit");setAuditLog(d);}catch(e){notify("Fehler: "+e.message);}};
  const loadBackups=async()=>{try{const d=await api("/admin/backups");setBackups(d);}catch(e){notify("Fehler: "+e.message);}};
  const saveRolePerms=async()=>{try{for(const [role,perms] of Object.entries(rolePerms)){if(role!=='Admin')await api("/role-permissions",{method:"PUT",body:JSON.stringify({role,perms})});}notify("Berechtigungen gespeichert ✅");closeModal();}catch(e){notify("Fehler: "+e.message);}};
  const openRolePerms=async()=>{try{const d=await api("/role-permissions");setRolePerms(d);setModal("role_perms");}catch(e){notify("Fehler: "+e.message);}}; 
  const openFormSettings=async()=>{try{const d=await api("/projects/"+activeProjectId+"/form-settings");setFormSettings(d);setModal("form_settings");}catch(e){notify("Fehler: "+e.message);}};
  const saveFormSettings=async()=>{try{await api("/projects/"+activeProjectId+"/form-settings",{method:"PUT",body:JSON.stringify(formSettings)});closeModal();notify("Formular gespeichert ✅");}catch(e){notify("Fehler: "+e.message);}};
  const followTask=async id=>{try{await api("/tasks/"+id+"/follow",{method:"POST"});notify("Aufgabe gefolgt 🔔");}catch(e){notify("Fehler: "+e.message);}};
  const unfollowTask=async id=>{try{await api("/tasks/"+id+"/unfollow",{method:"POST"});notify("Nicht mehr gefolgt");}catch(e){notify("Fehler: "+e.message);}};
  const runBackup=async()=>{try{notify("Backup wird erstellt…");const r=await api("/admin/backup",{method:"POST"});if(r.success){notify("Backup erstellt ✅");loadBackups();}else notify("Fehler: "+r.error);}catch(e){notify("Fehler: "+e.message);}};
  const openStorage=async()=>{try{const d=await api("/admin/storage");setStorageData(d);setModal("storage");}catch(e){notify("Fehler: "+e.message);}};
  const loadFeedback=async()=>{try{const d=await api("/nl2/feedback").catch(()=>[]);setBetaFeedback(d||[]);}catch(e){console.log("feedback:",e.message);}};
  const updateFeedback=async(id,status)=>{try{await api(`/nl2/feedback/${id}`,{method:"PUT",body:JSON.stringify({status})});setBetaFeedback(fb=>fb.map(f=>f.id===id?{...f,status}:f));}catch(e){notify("Fehler: "+e.message);}};
  const submitFeedback=async()=>{if(!feedbackForm.title.trim())return;try{await api("/nl2/feedback",{method:"POST",body:JSON.stringify(feedbackForm)});closeModal();notify("Feedback gesendet ✅");setFeedbackForm({beta_version:"",type:"bug",title:"",description:"",steps:"",severity:"medium"});}catch(e){notify("Fehler: "+e.message);}};
  const loadArchive=async()=>{try{const d=await api(`/projects/${activeProjectId}/archive`);setArchiveTasks(d);setShowArchive(true);}catch(e){notify("Fehler: "+e.message);}};

  if(!token) return <LoginPage onLogin={handleLogin}/>;
  if(loading) return <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',sans-serif"}}><div style={{textAlign:"center"}}><Spinner/><div style={{color:T.textMuted,fontSize:14,marginTop:16}}>{t?.loading||"Laden…"}</div></div></div>;

  // 🚀 LOGIK: Wer darf trotz Wartungsmodus rein?
  const isMaintenanceExempt = currentUser?.role === 'Admin' || currentUser?.username?.toLowerCase() === 'test';

  // 🔴 SPERRBILDSCHIRM FÜR NORMALE USER
  if (maintenanceMode && !isMaintenanceExempt) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0A0D15", color: "#fff", textAlign: "center", padding: 20 }}>
        <div style={{ fontSize: 70, marginBottom: 20 }}>🚧</div>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 12, color: "#0ECFD9" }}>
          {isEN ? "Maintenance in Progress" : "Wartungsarbeiten"}
        </h1>
        <p style={{ color: "#94A3B8", maxWidth: 400, lineHeight: 1.6, fontSize: 15 }}>
          {isEN ? "TaskFlow is currently being updated and optimized in the background." : "TaskFlow wird aktuell im Hintergrund aktualisiert und optimiert."} <br/>
          {isEN ? "Please try again in a few minutes!" : "Bitte versuche es in wenigen Minuten noch einmal!"}
        </p>
        <button onClick={logout} style={{ marginTop: 32, background: "transparent", border: `1px solid #FF4757`, color: "#FF4757", padding: "10px 24px", borderRadius: 10, cursor: "pointer", fontWeight: 700 }}>
          {isEN ? "Back to Login" : "Zurück zum Login"}
        </button>
      </div>
    );
  }

const isAnyTeamLeader = teams?.some(t => t.leader_id === currentUser?.id);
 const navGroups = [
    {
      title: isEN ? "WORKSPACE" : "MEIN BEREICH",
      items: [
        { id: "myTasks", label: "Home", icon: Home, color: ACCENT_LIGHT },
        { id: "news", label: "News", icon: Radio, color: "#8B5CF6" },
        { id: "calendar", label: isEN ? "Calendar" : "Kalender", icon: Clock, color: "#34D399" }
      ]
    },
    {
      title: "STUDIO & DEV",
      items: [
        ...(canSeeDashboard ? [{ id: "reporting", label: t.dashboard || "Dashboard", icon: LayoutDashboard, color: "#34D399" }] : []),
        ...(systemFeatures.feature_nl2 !== false ? [{ id: "nl2", label: "Dev & Releases", icon: Code, color: "#F59E0B" }] : []),
        ...(systemFeatures.feature_roadmap !== false ? [{ id: "roadmap", label: "Roadmap", icon: Target, color: "#8B5CF6" }] : []),
        ...(systemFeatures.feature_beta !== false ? [{ id: "beta", label: "Beta & QA", icon: Bug, color: "#EF4444" }] : [])
      ]
    },
    {
      title: isEN ? "COMMUNITY & TEAM" : "COMMUNITY & TEAM",
      items: [
        ...(systemFeatures.feature_polls !== false ? [{ id: "polls", label: isEN ? "Polls & Ideas" : "Umfragen & Ideen", icon: BarChart2, color: "#0ECFD9" }] : []),
        ...(isAdmin || isAnyTeamLeader ? [{ id: "teams", label: "Workspace Management", icon: Shield, color: "#8B5CF6" }] : []),
 	{ id: "teamportal", label: isEN ? "Team Lounge" : "Team Lounge", icon: Users, color: "#F59E0B" },
        ...(systemFeatures.feature_wiki !== false ? [{ id: "wiki", label: "Wiki", icon: BookOpen, color: "#F472B6" }] : []),
	...(isAdmin ? [{ id: "admin", label: t.admin || "Admin", icon: ShieldAlert, color: "#FF4757" }] : [])
      ]
    }
  ];

  const currentDetailTask = detailTask ? (tasks.find(t => t.id === detailTask.id) || allTasks.find(t => t.id === detailTask.id) || detailTask) : null;

  // 🚀 Wichtig: Wir übergeben setCmdOpen jetzt an die Sidebar!
  const ctx={isDiscordActive, systemFeatures, loadTickerSettings, maintenanceMode, setCmdOpen, newCheckItem, setNewCheckItem, addCheckToDetail, driveForm, setDriveForm, saveDriveEmbed, deleteDriveEmbed, filterAssignee, canManagePolls, setFilterAssignee, roadmapTasks, setRoadmapTasks, openRolePerms, versions, setVersions, isEN, users, betaTeamOverride, setBetaTeamOverride, updateTaskStatus, deleteTask, allTasks, nl2Betas, getProjectPerm, setActiveProjectId, openTags, openAutomations, canManageTags, T,iS,api,apiFetch,token,notify,closeModal,users,teams,projects,tasks,milestones,projectStatuses,projectTags,getStatusMeta,getTaskAssigneeIds,catMeta,AUDIT_META,activeProject,activeProjectId,currentUser,isAdmin,canEdit,canView,canCreateAutomation,editTarget,editAutoTarget,setEditAutoTarget,modal,setModal,taskForm,setTaskForm,saveTask,userForm,setUserForm,saveUser,oneTimePassword,teamForm,setTeamForm,saveTeam,projectForm,setProjectForm,saveProject,deleteProject,permForm,setPermForm,savePermissions,statusForm,setStatusForm,saveStatuses,tagForm,setTagForm,editTagTarget,setEditTagTarget,saveTag,deleteTag,autoForm,setAutoForm,automations,saveAutomation,deleteAutomation,toggleAutomation,milestoneForm,setMilestoneForm,saveMilestone,deleteMilestone,nl2Settings,setNl2Settings,nl2BetaForm,setNl2BetaForm,nl2BetaFile,setNl2BetaFile,nl2Uploading,nl2ReleaseBeta,betaFileRef,storageData,feedbackForm,setFeedbackForm,submitFeedback,settingsForm,setSettingsForm,saveSettings,toggleTheme,theme,changePwForm,setChangePwForm,changePassword,betaFeedback,updateFeedback,reportingData,auditLog,backups,runBackup,uploadFile,deleteFile,addComment,commentText,setCommentText,toggleCheck,fileInputRef,commentAreaRef,uploadingFile,detailTask,setDetailTask,openEditTask,archiveTask,fmtSize,fmtDate,rolePerms,setRolePerms,saveRolePerms,formSettings,setFormSettings,saveFormSettings,openFormSettings,followTask,unfollowTask,betaTeam,setBetaTeam,nl2UploadProgress,lang,toggleLang,t,avatarInputRef,uploadAvatar,deleteAvatar,openStatuses, logout, unreadNotifs, setShowNotifPanel, AVATAR_COLORS, openEditProject, loadNL2, loadAllTasks, loadAudit, loadBackups, loadFeedback, loadReporting};

  return (
    <div style={{minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'DM Sans','Segoe UI',sans-serif",display:"flex",height:"100vh",overflow:"hidden",position:"relative"}}>
      
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=DM+Mono:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0}::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-thumb{background:${T.border3};border-radius:5px}::-webkit-scrollbar-track{background:transparent}@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}@keyframes slideIn{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:translateX(0)}}@keyframes scaleIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}@keyframes spin{to{transform:rotate(360deg)}}.tr:hover{background:${T.cardHover} !important}.hov:hover{opacity:0.75;cursor:pointer}.del{opacity:0;transition:opacity 0.2s}.tr:hover .del{opacity:1}input,textarea,select{font-family:inherit;color:${T.text}} .mobile-only { display: none !important; }
      .desktop-only { display: flex !important; }
      .task-list-header { display: grid; grid-template-columns: 1fr 150px 120px 120px 140px 70px; gap: 16px; padding: 12px 20px; border-bottom: 1px solid ${T.border}; font-size: 10px; color: ${T.textFaint}; font-weight: 700; letter-spacing: 0.12em; background: rgba(0,0,0,0.15); }
      .task-row { display: grid; grid-template-columns: 1fr 150px 120px 120px 140px 70px; gap: 16px; padding: 14px 20px; border-bottom: 1px solid ${T.bg}; align-items: center; cursor: pointer; transition: background 0.2s; }

      @media(max-width:768px){
        .mobile-only { display: flex !important; }
        .desktop-only { display: none !important; }
        
        .sidebar { position: fixed !important; top: 0; bottom: 0; left: 0; z-index: 1000; transform: translateX(-100%); transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); width: 85% !important; max-width: 320px; box-shadow: 4px 0 24px rgba(0,0,0,0.5); }
        .sidebar.open { transform: translateX(0); }
        .main-content { margin-top: 60px !important; height: calc(100vh - 60px) !important; }
        
        .main-header { padding: 12px 16px !important; height: auto !important; flex-direction: column; align-items: flex-start !important; gap: 16px !important; }
        .header-actions { width: 100%; justify-content: flex-end; }
        
        .header-btns { width: 100%; overflow-x: auto; padding-bottom: 4px; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
        .header-btns::-webkit-scrollbar { display: none; }
        .header-btns button { white-space: nowrap; }
        
        .search-bar-mobile { width: 38px !important; padding: 0 !important; justify-content: center; }
        .search-text { display: none; }
        
        .fab-button { position: fixed !important; bottom: 24px !important; right: 24px !important; width: 56px !important; height: 56px !important; border-radius: 28px !important; padding: 0 !important; justify-content: center !important; z-index: 850 !important; box-shadow: 0 12px 28px rgba(14,207,217,0.4) !important; }
        .fab-text { display: none; }
        .fab-button svg { width: 24px; height: 24px; }
        
        .detail-panel { width: 100% !important; border-radius: 24px 24px 0 0 !important; top: 60px !important; height: calc(100% - 60px) !important; position: fixed !important; box-shadow: 0 -12px 40px rgba(0,0,0,0.5) !important; animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important; }
        
        .task-list-header { display: none !important; }
        .task-row { display: flex !important; flex-direction: column !important; align-items: flex-start !important; gap: 12px; margin: 0 0 12px 0 !important; padding: 16px !important; border: 1px solid var(--border2); border-radius: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.02); }
        .task-row > div { width: 100%; display: flex; justify-content: space-between; align-items: center; }
        .task-row > div:first-child { flex-direction: column; align-items: flex-start; gap: 6px; }
        .board-grid { grid-template-columns: 1fr !important; }
        .modal-box{width:100%!important;max-width:100%!important;margin:0!important;border-radius:20px 20px 0 0!important;position:fixed!important;bottom:0!important;max-height:90vh!important}
      }
      @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      input::placeholder,textarea::placeholder{color:${T.textFaint}}
      select option{background:${T.input};color:${T.text}}
      .drag-over{border:2px dashed ${ACCENT_LIGHT} !important;background:rgba(14,207,217,0.05) !important}
      `}</style>

      {notification&&<div style={{position:"fixed",top:24,right:24,zIndex:3000,background:T.sidebar,border:"1px solid #34D399",borderRadius:14,padding:"14px 24px",color:"#34D399",fontSize:14,fontWeight:600,boxShadow:"0 12px 36px rgba(0,0,0,0.5)",animation:"slideIn 0.3s ease",cursor:"pointer",maxWidth:340}} onClick={()=>setNotification(null)}>{notification}</div>}

      {/* MOBILE NAV OVERLAY */}
      {mobileMenuOpen && (
        <div className="mobile-only" onClick={()=>setMobileMenuOpen(false)} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:998, backdropFilter:"blur(4px)"}} />
      )}

      {/* MOBILE TOP BAR */}
      <div className="mobile-only" style={{alignItems:"center", padding:"0 16px", height:60, background:T.sidebar, borderBottom:`1px solid ${T.border}`, position:"absolute", top:0, left:0, right:0, zIndex:700}}>
        <button onClick={()=>setMobileMenuOpen(true)} style={{background:"transparent", border:"none", fontSize:24, color:T.text, cursor:"pointer"}}>☰</button>
        <div style={{marginLeft:16, display:"flex", alignItems:"center", height:30}}>
          <img src={process.env.PUBLIC_URL+"/TaskFlow_Logo.jpg"} alt="TF Logo" style={{maxWidth:"100%", maxHeight:"100%", objectFit:"contain"}} onError={e=>{e.target.style.display="none";e.target.insertAdjacentHTML("afterend",'<span style="font-size:18px;font-weight:700;color:'+ACCENT_LIGHT+'">TaskFlow</span>');}}/>
        </div>
      </div>

      {showNotifPanel&&(
        <div style={{position:"fixed",top:0,right:0,width:380,height:"100vh",background:T.sidebar,borderLeft:`1px solid ${T.border}`,zIndex:900,display:"flex",flexDirection:"column",animation:"slideIn 0.25s ease",boxShadow:"-12px 0 40px rgba(0,0,0,0.4)"}}>
          <div style={{padding:"22px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
            <div style={{flex:1,fontSize:16,fontWeight:700,color:T.text}}>{t.notifications||"Benachrichtigungen"}</div>
            {unreadNotifications.length>0&&<button onClick={markAllRead} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"6px 14px",borderRadius:9,fontSize:12,cursor:"pointer"}}>{t.markRead||"Alle lesen"}</button>}
            <button onClick={()=>setShowNotifPanel(false)} style={{background:"none",border:"none",color:T.textMuted,fontSize:20,cursor:"pointer"}}>×</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"12px"}}>
            {unreadNotifications.length===0&&<div style={{fontSize:14,color:T.textGhost,textAlign:"center",padding:"40px"}}>{t.noNotifs||"Keine neuen Benachrichtigungen. Alles erledigt! 🎉"}</div>}
            {unreadNotifications.map(n=>{const m=NOTIF_META[n.type]||NOTIF_META.default;return(<div key={n.id} style={{display:"flex",gap:12,padding:"14px",borderRadius:12,marginBottom:8,background:"rgba(14,207,217,0.06)",border:"1px solid rgba(14,207,217,0.2)"}}><div style={{width:34,height:34,borderRadius:10,background:m.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{m.icon}</div><div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:T.text,marginBottom:3}}>{n.title}</div><div style={{fontSize:12,color:T.textMuted,lineHeight:1.5}}>{n.message}</div><div style={{fontSize:11,color:T.textGhost,marginTop:5}}>{timeAgo(n.created_at)}</div></div></div>);})}
          </div>
        </div>
      )}

      {/* AUSGELAGERTE SIDEBAR */}
      <Sidebar 
        ctx={ctx} 
        navGroups={navGroups.filter(group => group.items.length > 0)}
        activeSection={activeSection} 
        setActiveSection={setActiveSection} 
        activeProjectId={activeProjectId} 
        setActiveProjectId={setActiveProjectId} 
        visibleProjects={visibleProjects} 
        unreadChat={unreadChat} 
        setMobileMenuOpen={setMobileMenuOpen}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        mobileMenuOpen={mobileMenuOpen}
      />

      {/* MAIN CONTENT AREA */}
      <div className="main-content" style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        
        {/* MAIN HEADER */}
        <div className="main-header" style={{ height: "70px", minHeight: "70px", boxSizing: "border-box", padding:"0 26px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, background:T.bg, flexShrink:0 }}>
          
          {/* LINKE SPALTE: Titel / Projektinfo */}
          <div style={{flex: 1, display: "flex", alignItems: "center", minWidth: 0}}>
            {activeSection==="tasks" && activeProject && (
              <div style={{display:"flex",alignItems:"center",gap:12, minWidth: 0}}>
                <div style={{width:36,height:36,borderRadius:11,background:activeProject.color+"20",display:"flex",alignItems:"center",justifyContent:"center", flexShrink: 0}}>
                  <RenderIcon icon={activeProject.icon} size={20} color={activeProject.color} />
                </div>
                <div style={{minWidth: 0}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <h1 style={{fontSize:18,fontWeight:700,color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{activeProject.name}</h1>
                    <PermBadge perm={activePerm}/>
                  </div>
                  <p style={{fontSize:12,color:T.textFaint,marginTop:2}}>{filteredTasks.length} {t.task||"Aufgaben"}</p>
                </div>
              </div>
            )}
            {activeSection==="focus" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Sun size={22} color="#F59E0B"/> {isEN ? "My Day" : "Mein Tag"}</h1>}
            {activeSection==="myTasks" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Home size={22} color={ACCENT_LIGHT}/> {t.myTasks||"Meine Aufgaben"}</h1>}
            {activeSection==="team" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Users size={22} color="#60A5FA"/> {t.team||"Team"}</h1>}
            {activeSection==="reporting" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><LayoutDashboard size={22} color="#34D399"/> {t.dashboard||"Dashboard"}</h1>}
            {activeSection==="nl2" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Code size={22} color="#F59E0B"/> Dev & Releases</h1>}
            {activeSection==="beta" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Bug size={22} color="#EF4444"/> Beta Portal</h1>}
            {activeSection==="roadmap" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Target size={22} color="#8B5CF6"/> {isEN ? "Beta Roadmap & Goals" : "Beta Roadmap & Ziele"}</h1>}
            {activeSection==="polls" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><BarChart2 color="#0ECFD9" size={22}/> {isEN ? "Polls & Ideas" : "Umfragen & Ideen"}</h1>}
            {activeSection==="admin" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><ShieldAlert size={22} color="#FF4757"/> {t.admin||"Admin"}</h1>}
            {activeSection==="platform" && <h1 style={{fontSize:18,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:10}}><Activity color="#F472B6" size={22}/> TaskFlow Status</h1>}
          </div>

          {activeSection==="tasks" && canView && (
            <div className="desktop-only" style={{display:"flex",background:T.input,border:`1px solid ${T.border2}`,borderRadius:11,padding:3,gap:3,marginRight:16}}>
              {[{id:"list",icon:<List size={14}/>,label:t.list||"Liste"},{id:"board",icon:<Kanban size={14}/>,label:t.board||"Board"},{id:"docs",icon:<FileText size={14}/>,label:isEN?"Docs":"Dokumente"},{id:"chat",icon:<MessageSquare size={14}/>,label:t.chat||"Chat"}].map(v=>(
                <div key={v.id} onClick={()=>{setProjectView(v.id);if(v.id==="chat")markChatRead(activeProjectId);}} style={{padding:"6px 12px",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6,background:projectView===v.id?T.cardHover:"transparent",color:projectView===v.id?T.text:T.textMuted,transition:"all 0.15s"}}>
                  {v.icon} {v.label}
                </div>
              ))}
            </div>
          )}

          {/* Die scrollbaren Projekt-Buttons */}
          {activeSection==="tasks" && activeProject && (
            <div className="header-btns" style={{display:"flex",gap:8,marginRight:16}}>
              {canManageMilestones && <button onClick={openMilestones} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"8px 14px",borderRadius:10,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><Target size={14}/> {t.milestones||"Milestones"}</button>}
              {canCreateAutomation && canEdit && <button onClick={openAutomations} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"8px 14px",borderRadius:10,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><Zap size={14}/> {t.automations||"Auto"}</button>}
              {canManageTags && <button onClick={openTags} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"8px 14px",borderRadius:10,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><Tags size={14}/> {t.tags||"Tags"}</button>}
              {(isAdmin || activeProject?.created_by === currentUser?.id) && <button onClick={()=>openPermissions(activeProject)} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"8px 14px",borderRadius:10,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><Shield size={14}/> {t.permissions||"Rechte"}</button>}
            </div>
          )}

          {/* MITTLERE SPALTE: DER TICKER */}
          <div className="desktop-only" style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <GlobalTicker ctx={ctx} />
          </div>

          {/* RECHTE SPALTE: Actions, Theme, Benachrichtigungen */}
          <div className="header-actions" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
            
            <div onClick={toggleTheme} className="hov desktop-only" style={{ width: 36, height: 36, borderRadius: 10, background: T.cardHover, border: `1px solid ${T.border2}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textMuted }}>{theme === "dark" ? "☀" : "🌙"}</div>
            <div onClick={toggleLang} className="hov desktop-only" style={{ width: 36, height: 36, borderRadius: 10, background: T.cardHover, border: `1px solid ${T.border2}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textMuted, fontSize: 12, fontWeight: 700 }}>{lang === "de" ? "EN" : "DE"}</div>
            
            <div onClick={() => setShowSystemFeedback(true)} className="hov" style={{ width: 36, height: 36, borderRadius: 10, background: T.cardHover, border: `1px solid ${T.border2}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textMuted, marginRight: 8 }} title={isEN ? "Report Bug or Idea" : "Bug/Idee melden"}>
              <LifeBuoy size={18} />
            </div>

            <div style={{position:"relative"}}>
              <div onClick={()=>setShowNotifPanel(v=>!v)} onContextMenu={(e)=>{e.preventDefault(); setDndMenuOpen(!dndMenuOpen);}} className="hov" style={{ position: "relative", width: 36, height: 36, borderRadius: 10, background: (currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) ? "#8B5CF620" : T.cardHover, border: `1px solid ${(currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) ? "#8B5CF650" : T.border2}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: (currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) ? "#8B5CF6" : T.textMuted }}>
                {(currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) ? <BellOff size={18} /> : <Bell size={18} />}
                {!(currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) && unreadNotifs > 0 && <div style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: "#FF4757", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadNotifs}</div>}
              </div>
              {dndMenuOpen && (
                <div style={{position:"absolute", top: 44, right: 0, background: T.sidebar, border:`1px solid ${T.border2}`, borderRadius: 12, padding: 8, zIndex: 2000, boxShadow:"0 12px 32px rgba(0,0,0,0.3)", width: 200, animation: "scaleIn 0.2s ease"}}>
                  <div style={{fontSize: 11, fontWeight: 700, color: T.textFaint, padding: "4px 8px", marginBottom: 4}}>{isEN ? "DO NOT DISTURB" : "RUHEMODUS"}</div>
                  <div onClick={()=>toggleDND(1)} className="tr" style={{padding: "8px", fontSize: 13, color: T.text, cursor:"pointer", borderRadius: 8}}>🌙 {isEN ? "Mute for 1 hour" : "1 Stunde stummschalten"}</div>
                  <div onClick={()=>toggleDND(4)} className="tr" style={{padding: "8px", fontSize: 13, color: T.text, cursor:"pointer", borderRadius: 8}}>🌙 {isEN ? "Mute for 4 hours" : "4 Stunden stummschalten"}</div>
                  <div onClick={()=>toggleDND(8)} className="tr" style={{padding: "8px", fontSize: 13, color: T.text, cursor:"pointer", borderRadius: 8}}>🌙 {isEN ? "Mute for 8 hours" : "8 Stunden stummschalten"}</div>
                  {(currentUser?.dnd_until && new Date(currentUser.dnd_until) > new Date()) && <div onClick={()=>toggleDND(null)} className="tr" style={{padding: "8px", fontSize: 13, color: "#34D399", cursor:"pointer", borderRadius: 8, marginTop: 4, borderTop: `1px solid ${T.border}`}}>🔔 {isEN ? "Turn off DND" : "Ruhemodus beenden"}</div>}
                </div>
              )}
            </div>            

            {activeSection==="tasks" && canEdit && projectView!=="chat" && (
              <button className="fab-button" onClick={openCreateTask} style={{background:ACCENT_GRADIENT,color:"#fff",border:"none",borderRadius:11,padding:"0 16px",height:36,fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,boxShadow:`0 6px 20px rgba(14,207,217,0.2)`,whiteSpace:"nowrap"}}>
                <Plus size={16} /> 
                <span className="fab-text">{t.newTask||"Neue Aufgabe"}</span>
              </button>
            )}
            
            {activeSection==="reporting" && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>loadReporting(rpId||"")} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>↻</button>
              </div>
            )}

            {activeSection==="admin" && isAdmin && (
              <div className="header-btns" style={{display:"flex",gap:8}}>
                <button onClick={openCreateProject} style={{background:"#10B981",color:"#fff",border:"none",borderRadius:10,padding:"0 16px",height:36,fontSize:13,fontWeight:700,cursor:"pointer", display:"flex", alignItems:"center", gap:4}}><Plus size={16}/> {isEN?"Project":"Projekt"}</button>
              </div>
            )}
          </div>
        </div>

        {/* QUICK STATS (nur im Task-Bereich) */}
        {activeSection==="tasks"&&activeProject&&canView&&(
          <div style={{padding:"14px 26px 8px",display:"flex",gap:12,flexShrink:0,overflowX:"auto"}}>
            {[
              {label:t.total||"Gesamt",value:stats.total,color:ACCENT_LIGHT,icon:"◈"},
              ...projectStatuses.map(s=>({label:s,value:projectTasks.filter(tk=>tk.status===s).length,color:getStatusMeta(s).dot,icon:"⚫"})),
              ...(stats.urgent>0?[{label:t.urgent||"Dringend",value:stats.urgent,color:"#FF4757",icon:"! "}]:[])
            ].map(s=><div key={s.label} style={{minWidth:100,flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"12px 16px"}}><div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}><span style={{color:s.color,fontSize:12,width:8,height:8,borderRadius:"50%",background:s.icon==="⚫"?s.color:"transparent",display:"inline-block"}}>{s.icon!=="⚫"&&s.icon}</span><span style={{fontSize:10,color:T.textGhost,fontWeight:700,letterSpacing:"0.1em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.label.toUpperCase()}</span></div><div style={{fontSize:24,fontWeight:700,color:s.color,fontFamily:"'DM Mono',monospace"}}>{s.value}</div></div>)}
          </div>
        )}

        {/* MAIN SCROLLABLE CONTENT */}
        <div style={{flex:1,overflow:"auto",padding:"12px 26px 26px"}}>
	<Suspense key={location.pathname} fallback={<div style={{display:"flex",justifyContent:"center",padding:60}}><Spinner/></div>}>
          {activeSection==="tasks"&&activeProject&&!canView&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60%",gap:16,color:T.textFaint}}><div style={{fontSize:56}}>⊘</div><div style={{fontSize:16,fontWeight:600}}>{t.noAccess||"Kein Zugriff"}</div></div>}
          {activeSection==="tasks"&&!activeProject&&<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60%",gap:16,textAlign:"center"}}><div style={{fontSize:56}}>📁</div><div style={{fontSize:16,fontWeight:600,color:T.textMuted}}>{t.noProjects||"Keine Projekte"}</div>{canCreateProject&&<button onClick={openCreateProject} style={{background:ACCENT_GRADIENT,color:"#fff",border:"none",borderRadius:11,padding:"11px 22px",fontSize:14,fontWeight:700,cursor:"pointer"}}>{t.createFirst||"+ Erstes Projekt erstellen"}</button>}</div>}

          {/* FILTER-LEISTE FÜR NORMALE PROJEKTE */}
          {activeSection==="tasks" && activeProject && canView && !showArchive && (projectView==="list" || projectView==="board") && (
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t.search || "Suchen..."} style={{ ...iS, width: 200, height: 36, padding: "0 14px" }} />
              
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                <option value="Alle">Alle Status</option>
                {projectStatuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              
              {/* 🚀 NEU: Mitarbeiter-Filter */}
              <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                <option value="Alle">Alle Personen</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>

              <select value={filterTag || ""} onChange={e => setFilterTag(e.target.value || null)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                <option value="">Alle Tags</option>
                {(projectTags || []).map(tg => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
              </select>
              
              {canManageMilestones && (
                <select value={filterMilestone || ""} onChange={e => setFilterMilestone(e.target.value || null)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                  <option value="">Alle Milestones</option>
                  {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              )}
              
              {(filterStatus !== "Alle" || filterTag !== null || filterMilestone !== null || filterAssignee !== "Alle" || searchQuery !== "") && (
                <button onClick={() => { setFilterStatus("Alle"); setFilterTag(null); setFilterMilestone(null); setFilterAssignee("Alle"); setSearchQuery(""); }} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "0 14px", height: 36, borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                  Reset
                </button>
              )}
            </div>
          )}

          {/* 🚀 HIER IST DAS NEUE, AUSGELAGERTE TASKBOARD 🚀 */}
          {activeSection==="tasks" && activeProject && canView && !showArchive && (projectView==="list" || projectView==="board") && (
            <TaskBoard 
              ctx={ctx}
              filteredTasks={filteredTasks}
              tasksByStatus={tasksByStatus}
              projectView={projectView}
              setProjectView={setProjectView}
              markChatRead={markChatRead}
            />
          )}

          {/* ARCHIVE VIEW */}
          {activeSection==="tasks"&&projectView==="list"&&canView&&activeProject&&showArchive&&(
            <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden"}}>
              <div style={{padding:"14px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10}}><span>  </span><span style={{fontWeight:600,color:T.text}}>Archiv</span><button onClick={()=>setShowArchive(false)} style={{marginLeft:"auto",background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"5px 12px",borderRadius:7,fontSize:11,cursor:"pointer"}}>← Zurück</button></div>
              {archiveTasks.length===0&&<div style={{padding:"30px",textAlign:"center",color:T.textGhost}}>{lang==="en"?"No archived tasks.":"Keine archivierten Aufgaben."}</div>}
              {archiveTasks.map(task=>(<div key={task.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 18px",borderBottom:`1px solid ${T.bg}`}}><span style={{color:"#34D399"}}>✓</span><div style={{flex:1}}><div style={{fontSize:13,color:T.textMuted,textDecoration:"line-through"}}>{task.title}</div><div style={{fontSize:11,color:T.textGhost}}>Archiviert {fmtDate(task.archived_at)}</div></div></div>))}
            </div>
          )}

          {/* DOKUMENTE VIEW */}
          {activeSection==="tasks"&&projectView==="docs"&&canView&&activeProject&&(
            <div style={{display:"flex",flexDirection:"column",gap:16, maxWidth:1200, margin:"0 auto", width:"100%"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:16,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:8}}><FileText color="#0ECFD9"/> {isEN?"Documents":"Dokumente"}</div>
                {canEdit && <button onClick={()=>setModal("drive")} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"8px 16px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 12px rgba(14,207,217,0.2)"}}>+ Dokument einbinden</button>}
              </div>
              
              {driveEmbeds.length===0 ? (
                <div style={{textAlign:"center", padding:60, color:T.textFaint, background:T.card, borderRadius:16, border:`1px solid ${T.border}`}}>Keine Dokumente eingebunden.</div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:16}}>
                  {driveEmbeds.map(doc => (
                    <div key={doc.id} style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 4px 12px rgba(0,0,0,0.02)"}}>
                      <div style={{padding:"14px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", background:"rgba(0,0,0,0.02)"}}>
                        <span style={{fontWeight:700, fontSize:14, color:T.text}}>{doc.title}</span>
                        {canEdit && <button onClick={()=>ctx.deleteDriveEmbed(doc.id)} style={{background:"transparent",border:"none",color:"#FF4757",cursor:"pointer",padding:4}} className="hov"><Trash2 size={16}/></button>}
                      </div>
                      <iframe src={doc.embed_url} style={{width:"100%", height:240, border:"none"}} title={doc.title} />
                      <a href={doc.url} target="_blank" rel="noreferrer" style={{display:"block", padding:"12px", textAlign:"center", fontSize:12, fontWeight:600, color:T.textMuted, background:T.cardHover, borderTop:`1px solid ${T.border}`, textDecoration:"none"}} className="hov">In Google Docs öffnen ↗</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PROJECT CHAT VIEW */}
          {activeSection==="tasks"&&projectView==="chat"&&canView&&activeProject&&(
            <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 160px)",maxWidth:800,margin:"0 auto",background:T.card,border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden"}}>
              <div style={{flex:1,overflowY:"auto",padding:"24px 24px 12px",display:"flex",flexDirection:"column",gap:14}}>
                {!(chatMessages[activeProjectId]||[]).length&&<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,opacity:0.4,margin:"auto",color:T.textFaint}}><div style={{fontSize:48}}>💬</div><div style={{fontSize:14}}>Noch keine Nachrichten.</div></div>}
                {(chatMessages[activeProjectId]||[]).map((msg,i)=>{const author=users.find(u=>u.id===(msg.author_id||msg.authorId));const isMe=(msg.author_id||msg.authorId)===currentUser?.id;return (
                  <div key={msg.id||i} style={{display:"flex",gap:12,alignItems:"flex-start",flexDirection:isMe?"row-reverse":"row"}}>
                    <UserAvatar user={isMe?currentUser:author} size={32}/>
                    <div style={{maxWidth:"75%"}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexDirection:isMe?"row-reverse":"row"}}><span style={{fontSize:12,fontWeight:600,color:T.textSub}}>{isMe?"Du":author?.name||"?"}</span><span style={{fontSize:10,color:T.textGhost}}>{timeAgo(msg.created_at)}</span></div><div style={{background:isMe?ACCENT_DARK:T.cardHover,border:`1px solid ${isMe?ACCENT_LIGHT+"30":T.border2}`,borderRadius:isMe?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:"11px 16px",fontSize:13,color:isMe?"#fff":T.textSub,lineHeight:1.6,whiteSpace:"pre-wrap",boxShadow:isMe?`0 4px 12px rgba(27,58,107,0.3)`:"none"}}>{msg.text}</div></div>
                  </div>
                );})}
                <div ref={chatBottomRef}/>
              </div>
              <div style={{padding:"14px 20px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10,alignItems:"center",background:"rgba(0,0,0,0.15)"}}>
                <UserAvatar user={currentUser} size={32}/>
                <MentionInput value={chatText} onChange={setChatText} onSubmit={sendChat} placeholder={t.comment||"Nachricht… (@mention)"} users={users} T={T} iS={iS}/>
                <button onClick={sendChat} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",width:38,height:38,borderRadius:11,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,opacity:chatText.trim()?1:0.4,transition:"all 0.2s",boxShadow:chatText.trim()?`0 4px 12px rgba(14,207,217,0.2)`:"none"}}>→</button>
              </div>
            </div>
          )}

          {/* 🏠 HOME DASHBOARD (FUSION AUS DAY & TASKS) */}
          {activeSection==="myTasks"&&(
            <div style={{display:"flex",flexDirection:"column",gap:24}}>
              
              {/* ☀️ FOCUS MODE / MEIN TAG */}
              <div style={{marginBottom: 8}}>
                <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 16}}>
                  <div style={{width: 36, height: 36, borderRadius: 12, background: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(245,158,11,0.3)"}}>
                    <Sun size={18} color="#F59E0B" />
                  </div>
                  <div>
                    <h2 style={{fontSize: 16, fontWeight: 800, color: T.text, margin: 0}}>{isEN ? "Focus for Today" : "Fokus für Heute"}</h2>
                    <div style={{fontSize: 12, color: T.textMuted, marginTop: 2}}>{isEN ? "Tasks marked for your day." : "Aufgaben auf deiner Tagesliste."}</div>
                  </div>
                </div>
                
                <div style={{background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.02)"}}>
                  {(()=>{
                    // 🚀 FILTERT AUFGABEN HERAUS, DIE SCHON AUF "ERLEDIGT" STEHEN
                    const isTaskDone = (t) => {
                      const p = projects.find(proj => proj.id === t.project_id);
                      const statuses = p?.customStatuses?.length ? p.customStatuses.map(s=>s.label) : DEFAULT_STATUSES;
                      return t.status === statuses[statuses.length - 1];
                    };
                    const focusTasks = allTasks.filter(t => !t.archived && (t.focused_by || []).includes(currentUser?.id) && !isTaskDone(t));
                    
                    if(focusTasks.length === 0) return (
                      <div style={{padding: "40px 20px", textAlign: "center", color: T.textGhost}}>
                        <Coffee size={36} style={{marginBottom: 12, opacity: 0.5}} />
                        <div style={{fontSize: 13, fontWeight: 600, color: T.textMuted}}>{isEN ? "Your day is clear." : "Dein Tag ist leer."}</div>
                        <div style={{fontSize: 12, marginTop: 4}}>{isEN ? "Open a task and click the sun to add it here." : "Öffne eine Aufgabe und klicke auf das Sonnen-Icon, um sie hier zu pinnen."}</div>
                      </div>
                    );
                    return focusTasks.map((task, i) => {
                      const p = projects.find(x => x.id === task.project_id);
                      const sm = getStatusMeta(task.status);
                      return (
                        <div key={task.id} className="tr task-row" onClick={()=>{setActiveProjectId(task.project_id);setDetailTask(task);setActiveSection("tasks");}} style={{cursor:"pointer", borderBottom: i < focusTasks.length-1 ? `1px solid ${T.border}` : "none", display: "flex", alignItems: "center", padding: "16px 20px", gap: 16}}>
                          
                          {/* 🚀 NEUER ABHAK-BUTTON MIT STATUS-AUSWAHL */}
                          <div className="hov" style={{position: "relative", width: 22, height: 22, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted}}>
                            <select
                              value={task.status}
                              onChange={e => {
                                updateTaskStatus(task.id, e.target.value);
                                api(`/tasks/${task.id}/focus`, { method: "PATCH", body: JSON.stringify({ focus_today: false }) }).catch(()=>{});
                                updateTaskState(task.id, t => ({...t, focused_by: (t.focused_by || []).filter(id => id !== currentUser?.id)}));
                                notify("Status aktualisiert & aus Fokus entfernt! ✅");
                              }}
                              style={{position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%"}}
                              onClick={e => e.stopPropagation()}
                            >
                              {(p?.customStatuses?.length ? p.customStatuses.map(s=>s.label) : DEFAULT_STATUSES).map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <Circle size={20} />
                          </div>

                          <div style={{flex: 1, minWidth: 0}}>
                            <div style={{fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{task.title}</div>
                            <div style={{fontSize: 11, color: T.textFaint, display: "flex", alignItems: "center", gap: 6}}>
                              {p && <span style={{display: "flex", alignItems: "center", gap: 4}}><RenderIcon icon={p.icon} size={12} color={p.color}/> {p.name}</span>}
                            </div>
                          </div>
                          <span style={{fontSize: 11, background: sm.bg, color: sm.color, padding: "4px 10px", borderRadius: 8, fontWeight: 600, whiteSpace: "nowrap"}}>{task.status}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* STATS */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:14}}>{[
                  {label:t.myAssigned||"Mir zugewiesen",value:allTasks.filter(task=>!task.archived&&getTaskAssigneeIds(task).includes(currentUser?.id)).length,color:ACCENT_LIGHT,icon:<CheckSquare size={20}/>},
                  {label:t.myFollowed||"Ich folge",value:allTasks.filter(task=>!task.archived&&(task.followers||[]).includes(currentUser?.id)).length,color:"#60A5FA",icon:<Bell size={20}/>},
                  {label:t.overdue||"Überfällig",value:allTasks.filter(task=>!task.archived&&task.due_date&&new Date(task.due_date)<new Date()&&getTaskAssigneeIds(task).includes(currentUser?.id)).length,color:"#FF4757",icon:<AlertTriangle size={20}/>},
                  {label:t.upcoming||"Bald fällig",value:allTasks.filter(task=>{if(task.archived||!task.due_date)return false;const d=new Date(task.due_date);const now=new Date();return d>now&&d<new Date(now.getTime()+3*86400000)&&getTaskAssigneeIds(task).includes(currentUser?.id);}).length,color:"#F59E0B",icon:<Clock size={20}/>},
                ].map(s=><div key={s.label} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"20px",boxShadow:"0 4px 12px rgba(0,0,0,0.1)"}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><span style={{display:"flex",color:s.color}}>{s.icon}</span><span style={{fontSize:11,color:T.textGhost,fontWeight:700,letterSpacing:"0.1em"}}>{s.label.toUpperCase()}</span></div><div style={{fontSize:32,fontWeight:700,color:s.color,fontFamily:"'DM Mono',monospace"}}>{s.value}</div></div>)}
              </div>
              
             {/* MEINE AUFGABEN LISTE (GRUPPIERT & EINKLAPPBAR) */}
              {(()=>{
                const myAssignedAll = allTasks.filter(task=>!task.archived&&getTaskAssigneeIds(task).includes(currentUser?.id));
                const allAccessibleProjects = projects.filter(p => isAdmin || getProjectPerm(p.id) !== "none");
                
                // 1. Sammelt alle eindeutigen Tags
                const myTagsMap = new Map();
                myAssignedAll.forEach(t => (t.tags || []).forEach(tg => myTagsMap.set(tg.id, tg)));
                const myUniqueTags = Array.from(myTagsMap.values());

                // 2. 🚀 Dynamische Status-Liste generieren (Je nach ausgewähltem Projekt)
                let availableStatuses = [];
                if (myFilterProject === "Alle") {
                  const statusSet = new Set();
                  allAccessibleProjects.forEach(p => (p.customStatuses || []).forEach(s => statusSet.add(s.label)));
                  DEFAULT_STATUSES.forEach(s => statusSet.add(s));
                  availableStatuses = Array.from(statusSet);
                } else {
                  const selectedProj = allAccessibleProjects.find(p => String(p.id) === String(myFilterProject));
                  availableStatuses = selectedProj?.customStatuses?.map(s => s.label) || DEFAULT_STATUSES;
                }

                // 3.    Filter anwenden inkl. Projekt-Filter
                const myFiltered = myAssignedAll.filter(t => {
                  if (myFilterProject !== "Alle" && String(t.project_id) !== String(myFilterProject)) return false;
                  if (myFilterStatus !== "Alle" && t.status !== myFilterStatus) return false;
                  if (myFilterTag !== "Alle" && !(t.tags || []).some(tg => String(tg.id) === String(myFilterTag))) return false;
                  return true;
                });

                const byProject=allAccessibleProjects.map(p=>({proj:p,tasks:myFiltered.filter(t=>t.project_id===p.id)})).filter(g=>g.tasks.length>0);
                const normalProjects = byProject.filter(g => !g.proj.name.toLowerCase().includes("beta"));
                const bugProjects = byProject.filter(g => g.proj.name.toLowerCase().includes("beta"));

                if(myAssignedAll.length===0) return (
                  <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"60px 40px",textAlign:"center",color:T.textGhost}}>
                    <div style={{fontSize:40,marginBottom:14}}>◎</div>
                    <div style={{fontSize:15,fontWeight:500}}>{t.noAssigned||"Keine zugewiesenen Aufgaben"}</div>
                  </div>
                );

                const toggleGroup = (groupId, e) => {
                  e.stopPropagation();
                  setCollapsedGroups(prev => ({...prev, [groupId]: !prev[groupId]}));
                };

                const renderProjectGroup = (proj, pTasks, isBug) => {
                  const isCollapsed = collapsedGroups[proj.id];
                  const hasOverdue = pTasks.some(t => t.due_date && new Date(t.due_date) < new Date());

                  return (
                    <div key={proj.id} style={{background:T.card,border:`1px solid ${isBug ? "#EF444440" : T.border}`,borderRadius:16,overflow:"hidden",marginBottom:16,boxShadow:"0 4px 12px rgba(0,0,0,0.02)",transition:"all 0.3s ease"}}>
                      <div onClick={(e)=>toggleGroup(proj.id, e)} style={{padding:"14px 20px",borderBottom: isCollapsed ? "none" : `1px solid ${isBug ? "rgba(239, 68, 68, 0.15)" : T.border}`,display:"flex",alignItems:"center",gap:12,cursor:"pointer",background:isBug ? "rgba(239, 68, 68, 0.05)" : (theme==="dark"?"rgba(255,255,255,0.02)":"#F8FAFC")}} className="tr">
                        <div style={{color: T.textMuted, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s ease", fontSize: 12, display:"flex", alignItems:"center"}}>▼</div>
                        <div style={{width:30,height:30,borderRadius:9,background:isBug ? "#EF444420" : proj.color+"20",color: isBug ? "#EF4444" : proj.color, display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <RenderIcon icon={proj.icon || (isBug ? "Bug" : "Folder")} size={15} color={isBug ? "#EF4444" : proj.color} />
                        </div>
                        <span style={{fontSize:15,fontWeight:700,color:T.text,flex:1, display:"flex", alignItems:"center", gap:8}}>
                          {proj.name}
                          {hasOverdue && <div style={{width:8, height:8, borderRadius:"50%", background:"#FF4757", boxShadow:"0 0 8px #FF4757"}} title="Enthält überfällige Aufgaben!" />}
                        </span>
                        <span style={{fontSize:11,background:isBug ? "#EF444420" : proj.color+"20",color:isBug ? "#EF4444" : proj.color,padding:"3px 12px",borderRadius:8,fontWeight:700}}>{pTasks.length} {t.task||"Aufgabe"}{pTasks.length!==1?"s":""}</span>
                        <button onClick={(e)=>{e.stopPropagation(); setActiveProjectId(proj.id); setActiveSection(isBug ? "beta" : "tasks");}} style={{background:"transparent", border:"none", color:T.textMuted, cursor:"pointer", padding:"4px 8px", borderRadius:6, display:"flex", alignItems:"center", gap:4, fontWeight:600}} className="hov">
                          Öffnen <span style={{fontSize:14}}>→</span>
                        </button>
                      </div>
                      {!isCollapsed && (
                        <div style={{animation: "fadeUp 0.2s ease"}}>
                          {pTasks.map((task,i)=>{
                            const sInfo=(proj.customStatuses||[]).find(s=>s.label===task.status);
                            const sm=sInfo?{color:sInfo.color,bg:sInfo.color+"15"}:DEFAULT_STATUS_META[task.status]||{color:"#94A3B8",bg:"#94A3B815"};
                            const isOverdue=task.due_date&&new Date(task.due_date)<new Date();
                            const isDueSoon=task.due_date&&!isOverdue&&(new Date(task.due_date)-new Date())<3*86400000;
                            const cDone=(task.checklist||[]).filter(c=>c.done).length;
                            const cTotal=(task.checklist||[]).length;
                            return <div key={task.id} className="tr task-row" onClick={()=>{setActiveProjectId(proj.id);setDetailTask(task);setActiveSection(isBug ? "beta" : "tasks");}} style={{cursor:"pointer", borderBottom: i<pTasks.length-1 ? `1px solid ${T.bg}` : "none"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:500,color:T.textSub,marginBottom:5,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{task.title}</div>
                                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                                  {isOverdue&&<span style={{fontSize:10,color:"#FF4757",background:"#FF475715",padding:"2px 7px",borderRadius:5,fontWeight:600}}>⚠ Überfällig</span>}
                                  {isDueSoon&&<span style={{fontSize:10,color:"#F59E0B",background:"#F59E0B15",padding:"2px 7px",borderRadius:5,fontWeight:600}}>⏰ {fmtDate(task.due_date)}</span>}
                                  {task.due_date&&!isOverdue&&!isDueSoon&&<span style={{fontSize:10,color:T.textFaint}}>{fmtDate(task.due_date)}</span>}
                                  {cTotal>0&&<span style={{fontSize:10,color:cDone===cTotal?"#34D399":T.textFaint,background:T.cardHover,padding:"1px 6px",borderRadius:5,fontWeight:600}}>✓ {cDone}/{cTotal}</span>}
                                  {(task.comments||[]).length>0&&<span style={{fontSize:10,color:T.textFaint,background:T.cardHover,padding:"1px 6px",borderRadius:5}}>💬 {task.comments.length}</span>}
                                  {(task.dependencies||[]).some(depId=>allTasks.find(t=>t.id===depId)&&!allTasks.find(t=>t.id===depId)?.archived)&&<span title="Blockiert" style={{fontSize:10,color:T.textFaint}}>🔒</span>}
                                </div>
                              </div>
                              <span style={{fontSize:11,background:sm.bg,color:sm.color,padding:"4px 10px",borderRadius:7,fontWeight:600,border:`1px solid ${sm.color}20`,whiteSpace:"nowrap",flexShrink:0}}>{task.status}</span>
                              <span style={{fontSize:11,background:PRIORITIES[task.priority]?.bg,color:PRIORITIES[task.priority]?.color,padding:"4px 10px",borderRadius:7,fontWeight:600,flexShrink:0}}>{tPrio(PRIORITIES[task.priority]?.label)}</span>
                            </div>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return <>
                  {/* 🚀 DIE NEUE FILTERLEISTE FÜR MEINE AUFGABEN */}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: T.card, padding: "12px 20px", borderRadius: 14, border: `1px solid ${T.border}`, marginBottom: 16 }}>
                    <div style={{fontSize: 13, fontWeight: 700, color: T.textMuted, marginRight: 8}}>Filter:</div>
                    
                    <select value={myFilterProject} onChange={e => { setMyFilterProject(e.target.value); setMyFilterStatus("Alle"); }} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                      <option value="Alle">Alle Projekte</option>
                      {allAccessibleProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>

                    <select value={myFilterStatus} onChange={e => setMyFilterStatus(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                      <option value="Alle">Alle Status</option>
                      {availableStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>

                    <select value={myFilterTag} onChange={e => setMyFilterTag(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
                      <option value="Alle">Alle Tags</option>
                      {myUniqueTags.map(tg => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
                    </select>

                    {(myFilterProject !== "Alle" || myFilterStatus !== "Alle" || myFilterTag !== "Alle") && (
                      <button onClick={() => { setMyFilterProject("Alle"); setMyFilterStatus("Alle"); setMyFilterTag("Alle"); }} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "0 14px", height: 36, borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Reset</button>
                    )}
                  </div>

                  {myFiltered.length === 0 && <div style={{textAlign:"center", color:T.textGhost, padding: "20px"}}>Keine Aufgaben für diese Filter gefunden.</div>}

                  {bugProjects.length > 0 && (
                    <div style={{marginBottom: 32}}>
                      <div style={{fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, paddingLeft: 4}}>
                        <Bug size={18} color="#EF4444" /> Meine Bug-Reports
                      </div>
                      {bugProjects.map(({proj,tasks:pTasks}) => renderProjectGroup(proj, pTasks, true))}
                    </div>
                  )}

                  {normalProjects.length > 0 && (
                    <div>
                      <div style={{fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, paddingLeft: 4}}>
                        <CheckSquare size={18} color={ACCENT_LIGHT} /> Reguläre Projekte
                      </div>
                      {normalProjects.map(({proj,tasks:pTasks}) => renderProjectGroup(proj, pTasks, false))}
                    </div>
                  )}
                </>;
              })()}               
           </div>
          )}         

          {/* TEAM ANSICHT */}
          {activeSection==="team"&&activeProject&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
              {teams.map(team=>{const memberUsers=((team.memberIds)||[]).map(id=>users.find(u=>u.id===id)).filter(Boolean);const teamTasks=tasks.filter(t=>{const aids=getTaskAssigneeIds(t);return aids.some(a=>(team.memberIds||[]).includes(a));});const done=teamTasks.filter(t=>t.status===projectStatuses[projectStatuses.length-1]).length;const pct=teamTasks.length>0?Math.round((done/teamTasks.length)*100):0;return (
                <div key={team.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"18px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}><div style={{width:36,height:36,borderRadius:11,background:team.color+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>{team.icon}</div><div style={{flex:1}}><div style={{fontSize:14,fontWeight:600,color:T.text}}>{team.name}</div><div style={{fontSize:11,color:T.textFaint}}>{memberUsers.length} Mitglieder</div></div><PermBadge perm={activeProject?.permissions?.[team.id]||"none"}/></div>
                  <div style={{display:"flex",marginBottom:12}}>{memberUsers.map((u,idx)=><UserAvatar key={u.id} user={u} size={28} overlap idx={idx}/>)}</div>
                  <div style={{background:T.cardHover,borderRadius:5,height:3,marginBottom:8}}><div style={{width:`${pct}%`,height:"100%",background:team.color,borderRadius:5,transition:"width 0.6s ease"}}/></div>
                  <div style={{fontSize:11,color:T.textFaint}}>{done}/{teamTasks.length} Aufgaben erledigt</div>
                </div>
              );})}
            </div>
          )}

          {/* FESTES MANAGEMENT DASHBOARD */}
          {activeSection === "reporting" && reportingData && (
            <div style={{display:"flex",flexDirection:"column",gap:24, animation:"fadeUp 0.2s ease", paddingBottom: 24}}>
              
              {/* TOP MANAGEMENT KPIs - KOMPAKT & MODERN */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
                {[
                  {label:isEN ? "Critical Blockers" : "Kritische Blocker", value:reportingData.projectStats.reduce((sum, p) => sum + parseInt(p.urgent || 0), 0), color:"#FF4757", icon:<AlertTriangle size={24}/>},
                  {label:isEN ? "Open Tasks" : "Offene Aufgaben", value:allTasks.filter(t => !t.archived && t.status !== (projectStatuses[projectStatuses.length-1]||"Erledigt")).length, color:ACCENT_LIGHT, icon:<CheckSquare size={24}/>},
                  {label:isEN ? "Active Projects" : "Aktive Projekte", value:reportingData.projectStats.length, color:"#8B5CF6", icon:<Folder size={24}/>},
                  {label:isEN ? "Team Members" : "Team Mitglieder", value:users.length, color:"#34D399", icon:<Users size={24}/>}
                ].map(s=>
                  <div key={s.label} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"20px",display:"flex",alignItems:"center",gap:16,boxShadow:"0 4px 12px rgba(0,0,0,0.02)",transition:"transform 0.2s"}}>
                    <div style={{width:54,height:54,borderRadius:14,background:s.color+"15",color:s.color,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {s.icon}
                    </div>
                    <div>
                      <div style={{fontSize:28,fontWeight:800,color:T.text,fontFamily:"'DM Mono',monospace",lineHeight:1}}>{s.value}</div>
                      <div style={{fontSize:12,color:T.textGhost,fontWeight:700,letterSpacing:"0.05em",marginTop:6}}>{s.label.toUpperCase()}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* FESTES WIDGET LAYOUT - BESSERE STRUKTUR */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(420px,1fr))",gap:20}}>

                {/* 1. AKUTE BLOCKER */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"24px",display:"flex",flexDirection:"column"}}>
                  <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:20,display:"flex",alignItems:"center",gap:8}}><AlertTriangle size={18} color="#FF4757"/> {isEN ? "Urgent Tasks & Blockers" : "Akute Blocker & Dringendes"}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:10,flex:1}}>
                    {(()=>{
                      const urgent = allTasks.filter(t => !t.archived && t.priority === 0).slice(0,5);
                      if(urgent.length === 0) return (
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, padding: "30px 20px", background: T.cardHover, borderRadius: 12, border: `1px dashed ${T.border2}`}}>
                          <CheckCircle size={32} color="#34D399" style={{marginBottom: 12}} />
                          <div style={{fontSize:14,fontWeight:600,color:T.text}}>{isEN?"All clear!":"Alles im grünen Bereich!"}</div>
                          <div style={{fontSize:12,color:T.textFaint,marginTop:4}}>{isEN?"No urgent tasks found.":"Keine akuten Blocker gefunden."}</div>
                        </div>
                      );
                      return urgent.map(t => {
                        const p = projects.find(x=>x.id===t.project_id);
                        return (
                          <div key={t.id} onClick={() => handleOpenTask(t)} className="tr hov" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:T.cardHover,borderRadius:12,border:`1px solid ${T.border2}`,cursor:"pointer"}}>
                            <div style={{width:10,height:10,borderRadius:"50%",background:"#FF4757",boxShadow:"0 0 8px #FF4757"}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:14,fontWeight:600,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.title}</div>
                              <div style={{fontSize:12,color:T.textFaint,marginTop:2}}>{p?.name}</div>
                            </div>
                            <span style={{fontSize:11,background:"#FF475715",color:"#FF4757",padding:"4px 10px",borderRadius:8,fontWeight:700}}>{t.status}</span>
                          </div>
                        )
                      });
                    })()}
                  </div>
                </div>

                {/* 2. LIVE TICKER (ALS TIMELINE) */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"24px",display:"flex",flexDirection:"column"}}>
                  <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:20,display:"flex",alignItems:"center",gap:8}}><Activity size={18} color="#0ECFD9"/> {isEN ? "Live Activity Ticker" : "Live-Aktivitäts-Ticker"}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:0,position:"relative",flex:1}}>
                    {/* Vertikale Linie */}
                    <div style={{position:"absolute", left: 17, top: 10, bottom: 10, width: 2, background: T.border2, zIndex: 0}} />
                    
                    {(reportingData.recentActivity||[]).slice(0,5).map((a,i) => {
                      const m = AUDIT_META[a.action] || AUDIT_META.default;
                      const actionMap = { create: isEN?"created":"erstellte", delete: isEN?"deleted":"löschte", login: isEN?"logged in":"loggte sich ein", status_change: isEN?"changed status of":"änderte Status von", version_change: isEN?"changed version of":"änderte Version von", archive: isEN?"archived":"archivierte" };
                      const actionText = actionMap[a.action] || a.action;
                      return (
                        <div key={i} style={{display:"flex",gap:16,alignItems:"flex-start", paddingBottom: 16, position: "relative", zIndex: 1}}>
                          <div style={{width:36,height:36,borderRadius:"50%",background:T.card,border:`2px solid ${m.color}`,display:"flex",alignItems:"center",justifyContent:"center",color:m.color,flexShrink:0}}>
                            <RenderIcon icon={m.icon} size={16} />
                          </div>
                          <div style={{flex:1, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}`, boxShadow:"0 2px 6px rgba(0,0,0,0.02)"}}>
                            <div style={{fontSize:13,color:T.textSub,lineHeight:1.5}}>
                              <b style={{color:T.text}}>{a.user_name||"System"}</b> {actionText} <b style={{color:T.textMuted}}>{a.entity}</b> {a.detail&&`(${a.detail.replace(/"/g, '')})`}
                            </div>
                            <div style={{fontSize:11,color:T.textGhost,marginTop:6,display:"flex",alignItems:"center",gap:4}}><Clock size={12}/> {timeAgo(a.created_at)}</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* 3. PROJECT HEALTH CHECK (Volle Breite mit eigenen Kacheln) */}
                <div style={{gridColumn: "1 / -1", background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"24px"}}>
                  <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:24,display:"flex",alignItems:"center",gap:8}}><Target size={18} color="#34D399"/> {isEN ? "Project Health Check" : "Projekt Gesundheits-Check"}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
                    {[...reportingData.projectStats].sort((a,b) => parseInt(b.urgent) - parseInt(a.urgent)).map(p=>{
                      const pct=p.total>0?Math.round((parseInt(p.done)/parseInt(p.total))*100):0;
                      const hasUrgent = parseInt(p.urgent) > 0;
                      return(
                        <div key={p.id} style={{background: T.cardHover, border: `1px solid ${hasUrgent ? "#FF475740" : T.border2}`, borderRadius: 12, padding: "16px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                            <div style={{width:32,height:32,borderRadius:10,background:p.color+"20",display:"flex",alignItems:"center",justifyContent:"center"}}><RenderIcon icon={p.icon} size={16} color={p.color}/></div>
                            <span style={{fontSize:15,fontWeight:700,color:T.text,flex:1,display:"flex",alignItems:"center",gap:6}}>
                              {p.name}
                              {hasUrgent && <div style={{width:8, height:8, borderRadius:"50%", background:"#FF4757", boxShadow:"0 0 8px #FF4757"}} title="Kritische Aufgaben offen!" />}
                            </span>
                            {hasUrgent&&<span style={{fontSize:11,background:"#FF475715",color:"#FF4757",padding:"4px 8px",borderRadius:8,fontWeight:700}}>! {p.urgent} Blocker</span>}
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:700,color:T.textSub,marginBottom:8}}>
                            <span>{isEN?"Progress":"Fortschritt"}</span><span style={{color:p.color}}>{pct}%</span>
                          </div>
                          <div style={{background:T.bg,borderRadius:6,height:8,border:`1px solid ${T.border2}`}}>
                            <div style={{width:`${pct}%`,height:"100%",background:hasUrgent ? "#FF4757" : p.color,borderRadius:5,transition:"width 0.8s ease"}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"flex-end",fontSize:11,color:T.textFaint,marginTop:8,fontFamily:"'DM Mono',monospace"}}>{p.done} / {p.total} {isEN?"Tasks":"Aufgaben"}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 4. TAGS */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"24px"}}>
                  <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:20,display:"flex",alignItems:"center",gap:8}}><Tags size={18} color="#EC4899"/> {isEN ? "Most Used Tags" : "Meistgenutzte Tags"}</div>
                  {(reportingData.tagStats||[]).length===0&&<div style={{fontSize:13,color:T.textFaint,textAlign:"center",padding:"20px"}}>{isEN?"No tag data.":"Keine Tag-Daten."}</div>}
                  <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                    {(reportingData.tagStats||[]).slice(0,12).map(tg=>(
                      <div key={tg.name} style={{display:"flex",alignItems:"center",gap:8,background:tg.color+"15",border:`1px solid ${tg.color}30`,padding:"8px 14px",borderRadius:20}}>
                        <span style={{fontSize:13,fontWeight:600,color:tg.color}}>{tg.name}</span>
                        <span style={{fontSize:11,background:tg.color,color:"#fff",padding:"2px 8px",borderRadius:10,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>{tg.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 5. TEAM WORKLOAD */}
                <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:"24px"}}>
                  <div style={{fontSize:16,fontWeight:700,color:T.text,marginBottom:20,display:"flex",alignItems:"center",gap:8}}><Users size={18} color="#F59E0B"/> {isEN ? "Team Workload" : "Team Auslastung"}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:16}}>
                    {[...reportingData.userStats].sort((a,b) => (parseInt(b.assigned)-parseInt(b.done)) - (parseInt(a.assigned)-parseInt(a.done))).slice(0,4).map((u,i)=>{
                      const open = parseInt(u.assigned) - parseInt(u.done);
                      const pct = u.assigned > 0 ? Math.round((parseInt(u.done)/parseInt(u.assigned))*100) : 0;
                      return(
                        <div key={u.id} style={{display:"flex",alignItems:"center",gap:14, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}`}}>
                          <UserAvatar user={u} size={36}/>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                              <div style={{fontSize:14,fontWeight:700,color:T.text}}>{u.name}</div>
                              <div style={{fontSize:11,color:open > 10 ? "#FF4757" : T.textGhost,background:open > 10 ? "#FF475715" : T.bg, border: `1px solid ${open > 10 ? "#FF475740" : T.border2}`, padding:"3px 8px",borderRadius:6,fontWeight:600}}>{open} {isEN?"open":"offen"}</div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:12}}>
                              <div style={{flex:1,background:T.bg, border:`1px solid ${T.border2}`, borderRadius:5,height:8}}><div style={{width:`${pct}%`,height:"100%",background:open > 10 ? "#FF4757" : (u.color||ACCENT_LIGHT),borderRadius:4}}/></div>
                              <div style={{fontSize:12,color:T.textFaint,minWidth:40,textAlign:"right",fontFamily:"'DM Mono',monospace",fontWeight:600}}>{u.done}/{u.assigned}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            </div>
          )}
         
          {activeSection==="nl2"&&<NL2Tab ctx={{...ctx,nl2Reservation,nl2Log,nl2Betas,nl2Settings,nl2ReleaseNote,setNl2ReleaseNote,nl2LogText,setNl2LogText,nl2LogType,setNl2LogType,nl2Reserve,nl2Release,nl2AddLog,nl2DeleteLog,nl2DeleteBeta,betaFeedback,updateFeedback}}/>}
          {activeSection==="beta" && <BetaPortal ctx={ctx} />}
          {activeSection==="roadmap" && <RoadmapTab ctx={ctx} />}
          {activeSection==="polls" && <PollsTab ctx={ctx} />}
	  {activeSection==="wiki" && <WikiTab ctx={ctx} />}
	  {activeSection === "teams" && <TeamsTab ctx={ctx} />}
 	  {activeSection==="teamportal" && <TeamPortalTab ctx={ctx} />}
          {activeSection==="platform" && <PlatformTab ctx={ctx} />}
	  {activeSection==="news" && <NewsTab ctx={ctx} />}
	  {activeSection==="calendar" && <CalendarTab ctx={ctx} />}

	  {/* 🚀 NEUER, MODERNER ADMIN TAB (SIDEBAR LAYOUT) */}
          {activeSection==="admin"&&isAdmin&&(
            <div style={{display:"flex", minHeight:"calc(100vh - 70px)", margin: "-12px -26px -26px", background: T.bg }}>
              
              {/* ADMIN SIDEBAR */}
              <div className="desktop-only" style={{width: 260, background: T.card, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", flexShrink: 0, zIndex: 10}}>
                <div style={{padding: "24px 20px", borderBottom: `1px solid ${T.border}`}}>
                  <div style={{fontSize: 16, fontWeight: 800, color: T.text, display: "flex", alignItems: "center", gap: 8}}>
                    <ShieldAlert size={20} color="#FF4757" /> Workspace Admin
                  </div>
                  <div style={{fontSize: 12, color: T.textFaint, marginTop: 4}}>Zentrale Einstellungen</div>
                </div>

                <div style={{flex: 1, overflowY: "auto", padding: "20px 12px", display: "flex", flexDirection: "column", gap: 24}}>
                  
                  {/* GRUPPE: ORGANISATION */}
                  <div>
                    <div style={{fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.08em", marginBottom: 8, paddingLeft: 12}}>ORGANISATION</div>
                    <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                      <div onClick={()=>setAdminTab("users")} className="tr hov" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,cursor:"pointer",background:adminTab==="users"?T.cardHover:"transparent",color:adminTab==="users"?T.text:T.textMuted,fontWeight:adminTab==="users"?700:500}}>
                        <Users size={16} color={adminTab==="users"?ACCENT_LIGHT:T.textGhost} /> Nutzerverwaltung
                      </div>
                      
                      {/* ALTES TEAM-MENÜ WURDE HIER ENTFERNT */}

                      <div onClick={openRolePerms} className="tr hov" style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:10,cursor:"pointer",color:T.textMuted,fontWeight:500}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}><Shield size={16} color={T.textGhost} /> Rollen & Rechte</div>
                        <span style={{fontSize:14}}>↗</span>
                      </div>
                    </div>
                  </div>

                  {/* GRUPPE: KOMMUNIKATION */}
                  <div>
                    <div style={{fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.08em", marginBottom: 8, paddingLeft: 12}}>KOMMUNIKATION</div>
                    <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                      <div onClick={()=>setAdminTab("broadcast")} className="tr hov" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,cursor:"pointer",background:adminTab==="broadcast"?T.cardHover:"transparent",color:adminTab==="broadcast"?T.text:T.textMuted,fontWeight:adminTab==="broadcast"?700:500}}>
                        <MessageSquare size={16} color={adminTab==="broadcast"?"#F59E0B":T.textGhost} /> System Broadcasts
                      </div>
                      {systemFeatures.feature_discord !== false && (
                        <div onClick={()=>setAdminTab("discord")} className="tr hov" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,cursor:"pointer",background:adminTab==="discord"?T.cardHover:"transparent",color:adminTab==="discord"?T.text:T.textMuted,fontWeight:adminTab==="discord"?700:500}}>
                          <Globe size={16} color={adminTab==="discord"?"#5865F2":T.textGhost} /> Discord Bot
                        </div>
                      )}
                    </div>
                  </div>

                  {/* GRUPPE: SYSTEM */}
                  <div>
                    <div style={{fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.08em", marginBottom: 8, paddingLeft: 12}}>SYSTEM & TECHNIK</div>
                    <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                      <div onClick={()=>setAdminTab("system")} className="tr hov" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,cursor:"pointer",background:adminTab==="system"?T.cardHover:"transparent",color:adminTab==="system"?T.text:T.textMuted,fontWeight:adminTab==="system"?700:500}}>
                        <Settings size={16} color={adminTab==="system"?"#34D399":T.textGhost} /> Wartung & Features
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* ADMIN CONTENT AREA */}
              <div style={{flex: 1, overflowY: "auto", padding: "32px 40px"}}>
                
                {/* 1. NUTZER */}
                {adminTab === "users" && (
                  <div style={{animation: "fadeUp 0.2s ease"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24,paddingBottom:16,borderBottom:`1px solid ${T.border}`}}>
                      <div>
                        <h2 style={{fontSize:24,fontWeight:800,color:T.text,letterSpacing:"-0.5px"}}>Nutzerverwaltung</h2>
                        <div style={{fontSize:14,color:T.textMuted,marginTop:6}}>Verwalte die Zugänge und Mitarbeiter im Workspace. Aktuell {users.length} aktive Nutzer.</div>
                      </div>
                      <button onClick={openCreateUser} style={{background:ACCENT_GRADIENT,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,boxShadow:`0 4px 12px rgba(14,207,217,0.2)`}}>
                        <Plus size={16}/> Neues Mitglied
                      </button>
                    </div>

                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16}}>
                      {/* Bisheriges User-Mapping übernehmen */}
                      {users.map(u => (
                        <div key={u.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:20,transition:"all 0.2s",boxShadow:"0 4px 12px rgba(0,0,0,0.02)",display:"flex",flexDirection:"column"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                            <div style={{display:"flex",gap:12,alignItems:"center"}}>
                              <UserAvatar user={u} size={46}/>
                              <div>
                                <div style={{fontSize:15,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:6}}>
                                  {u.name} 
                                  {u.id===currentUser?.id&&<span style={{fontSize:10,background:ACCENT_LIGHT+"20",color:ACCENT_LIGHT,padding:"2px 6px",borderRadius:6}}>Ich</span>}
                                </div>
                                <div style={{fontSize:12,color:T.textFaint}}>@{u.username}</div>
                                <div style={{display:"flex", alignItems:"center", gap: 4, marginTop: 6, fontSize: 11, color: T.textGhost}}>
                                  <Activity size={12}/> Aktiv: {u.last_active ? timeAgo(u.last_active) : "Unbekannt"}
                                </div>
                              </div>
                            </div>
                            <Badge label={u.role} color={ROLE_META[u.role]?.color} bg={ROLE_META[u.role]?.bg}/>
                          </div>
                          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20,flex:1}}>
                            {u.discord_id&&<span style={{fontSize:11,background:"#5865F215",color:"#5865F2",padding:"3px 8px",borderRadius:6,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>Discord ✓</span>}
                            {teams.filter(t=>(t.memberIds||[]).includes(u.id)).map(team=><span key={team.id} style={{fontSize:11,background:team.color+"15",color:team.color,padding:"3px 8px",borderRadius:6,fontWeight:600}}>{team.name}</span>)}
                          </div>
                          <div style={{display:"flex",gap:8,borderTop:`1px solid ${T.border}`,paddingTop:16}}>
                            <button onClick={()=>openEditUser(u)} style={{flex:1,background:T.cardHover,border:`1px solid ${T.border2}`,color:T.text,padding:"8px",borderRadius:10,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",justifyContent:"center",alignItems:"center",gap:6}} className="hov"><Edit3 size={14}/> Edit</button>
                            {u.id!==currentUser?.id&&<button onClick={async()=>{if(!window.confirm("Passwort zurücksetzen?"))return;try{const r=await api("/admin/users/"+u.id+"/reset-password",{method:"POST"});notify("OTP: "+r.otp);alert("Neues OTP Passwort: "+r.otp);}catch(e){notify("Fehler: "+e.message);}}} style={{flex:1,background:"#F59E0B15",border:"1px solid #F59E0B30",color:"#F59E0B",padding:"8px",borderRadius:10,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",justifyContent:"center",alignItems:"center",gap:6}} className="hov"><Key size={14}/> Reset</button>}
                            {u.id!==currentUser?.id&&<button onClick={()=>deleteUser(u.id)} style={{background:"#FF475715",border:"1px solid #FF475730",color:"#FF4757",padding:"8px 12px",borderRadius:10,cursor:"pointer"}} className="hov"><Trash2 size={16}/></button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. TEAMS */}
                {adminTab === "teams" && (
                  <div style={{animation: "fadeUp 0.2s ease"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24,paddingBottom:16,borderBottom:`1px solid ${T.border}`}}>
                      <div>
                        <h2 style={{fontSize:24,fontWeight:800,color:T.text,letterSpacing:"-0.5px"}}>Teams & Abteilungen</h2>
                        <div style={{fontSize:14,color:T.textMuted,marginTop:6}}>Gruppiere Nutzer für einfachere Aufgabenzuweisung.</div>
                      </div>
                      <button onClick={openCreateTeam} style={{background:"#EC4899",color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6,boxShadow:`0 4px 12px rgba(236,72,153,0.2)`}}>
                        <Plus size={16}/> Neues Team
                      </button>
                    </div>

                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16}}>
                      {/* Bisheriges Team-Mapping übernehmen */}
                      {teams.map(team => {
                        const memberUsers = ((team.memberIds)||[]).map(id=>users.find(u=>u.id===id)).filter(Boolean);
                        return(
                          <div key={team.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:20,transition:"all 0.2s",boxShadow:"0 4px 12px rgba(0,0,0,0.02)"}}>
                            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
                              <div style={{width:48,height:48,borderRadius:14,background:team.color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}><RenderIcon icon={team.icon} size={24} color={team.color} /></div>
                              <div style={{flex:1}}>
                                <div style={{fontSize:16,fontWeight:700,color:T.text}}>{team.name}</div>
                                <div style={{fontSize:12,color:T.textFaint}}>{memberUsers.length} Mitglieder</div>
                              </div>
                            </div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:20,minHeight:36}}>
                              {memberUsers.length === 0 && <span style={{fontSize:12, color:T.textGhost, fontStyle:"italic"}}>Keine Mitglieder</span>}
                              {memberUsers.map((u,idx)=><UserAvatar key={u.id} user={u} size={32} overlap idx={idx}/>)}
                            </div>
                            <div style={{display:"flex",gap:8,borderTop:`1px solid ${T.border}`,paddingTop:16}}>
                              <button onClick={()=>openEditTeam(team)} style={{flex:1,background:T.cardHover,border:`1px solid ${T.border2}`,color:T.text,padding:"8px",borderRadius:10,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",justifyContent:"center",alignItems:"center",gap:6}} className="hov"><Edit3 size={14}/> Bearbeiten</button>
                              <button onClick={()=>deleteTeam(team.id)} style={{background:"#FF475715",border:"1px solid #FF475730",color:"#FF4757",padding:"8px 12px",borderRadius:10,cursor:"pointer"}} className="hov"><Trash2 size={16}/></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 3. BROADCAST */}
                {adminTab === "broadcast" && (
                  <div style={{animation: "fadeUp 0.2s ease"}}>
                    <div style={{marginBottom:24,paddingBottom:16,borderBottom:`1px solid ${T.border}`}}>
                      <h2 style={{fontSize:24,fontWeight:800,color:T.text,letterSpacing:"-0.5px"}}>System Broadcasts</h2>
                      <div style={{fontSize:14,color:T.textMuted,marginTop:6}}>Sende Benachrichtigungen an den globalen Ticker und verwalte Live-Infos.</div>
                    </div>
                    {/* Bisheriger Broadcast-Inhalt */}
                    <div style={{display: "grid", gridTemplateColumns: "1fr", gap: 20}}>
                      <div style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"24px", display:"flex", flexDirection:"column", gap:14, boxShadow: "0 4px 12px rgba(0,0,0,0.02)"}}>
                        <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
                          <span style={{fontSize:18}}>  </span> {isEN ? "Global System Broadcast" : "Globale System-Nachricht (Ticker)"}
                        </div>
                        <p style={{fontSize:13, color:T.textMuted, lineHeight:1.5}}>
                          {isEN ? "Send a real-time message to all users." : "Sende eine globale Nachricht an das gesamte Team."}
                        </p>
                        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                          <input 
                            id="adminBroadcastInput"
                            placeholder={isEN ? "Enter message..." : "Nachricht eingeben..."} 
                            style={{ flex: 1, minWidth: 280, background: T.input, border: `1px solid ${T.border2}`, color: T.text, fontSize: 14, padding: "12px 16px", borderRadius: 12, outline: "none" }}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter" && e.target.value.trim()) {
                                try {
                                  await api('/system-message', { method: "POST", body: JSON.stringify({ text: e.target.value, is_active: true }) });
                                  notify("Nachricht live! 🚀");
                                  e.target.value = "";
                                } catch(err) { notify("Fehler: " + err.message); }
                              }
                            }}
                          />
                          <button 
                            onClick={async () => {
                              try {
                                await api('/system-message', { method: "POST", body: JSON.stringify({ text: "", is_active: false }) });
                                notify("Nachricht entfernt 🗑");
                              } catch(err) { notify("Fehler: " + err.message); }
                            }}
                            style={{ background: "transparent", border: `1px solid #FF4757`, color: "#FF4757", padding: "12px 20px", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                            className="hov"
                          >
                            {isEN ? "Clear Ticker" : "Ticker leeren"}
                          </button>
                        </div>
                      </div>

                      <div style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"24px", display:"flex", flexDirection:"column", gap:14, boxShadow: "0 4px 12px rgba(0,0,0,0.02)"}}>
                        <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
                          <span style={{fontSize:18}}>🎛️</span> {isEN ? "Active Ticker Modules" : "Aktive Ticker Module"}
                        </div>
                        <p style={{fontSize:13, color:T.textMuted, lineHeight:1.5}}>
                          {isEN ? "Choose which dynamic info shows." : "Wähle aus, welche Infos im Ticker rotieren."}
                        </p>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginTop: 10}}>
                          {[
                            { key: "ticker_admin", label: "Admin Nachrichten", icon: <MessageSquare size={16} color="#0ECFD9"/> },
                            { key: "ticker_beta", label: "Neue Beta-Releases", icon: <Bug size={16} color="#EF4444"/> },
                            { key: "ticker_poll", label: "Aktive Umfragen", icon: <BarChart2 size={16} color="#8B5CF6"/> },
                            { key: "ticker_nl2", label: "NoLimits 2 Res.", icon: <RollerCoaster size={16} color="#F59E0B"/> },
                            { key: "ticker_roadmap", label: "Roadmap Fortschritt", icon: <Target size={16} color="#34D399"/> },
			    { key: "ticker_wiki", label: "Wiki Updates", icon: <BookOpen size={16} color="#F472B6"/> }
                          ].map(item => (
                            <div key={item.key} onClick={() => toggleTickerSetting(item.key)} className="hov" style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", background:T.cardHover, border:`1px solid ${T.border2}`, borderRadius:12, cursor:"pointer"}}>
                              <div style={{display:"flex", alignItems:"center", gap:10}}>
                                {item.icon}
                                <span style={{fontSize:13, fontWeight:600, color:T.text}}>{item.label}</span>
                              </div>
                              <div style={{width:38, height:22, borderRadius:11, background: tickerSettings[item.key] ? "#34D399" : T.border3, position:"relative", transition:"background 0.2s"}}>
                                <div style={{width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left: tickerSettings[item.key] ? 18 : 2, transition:"all 0.2s", boxShadow:"0 2px 4px rgba(0,0,0,0.2)"}} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 4. SYSTEM */}
                {adminTab === "system" && (
                  <div style={{animation: "fadeUp 0.2s ease"}}>
                    <div style={{marginBottom:24,paddingBottom:16,borderBottom:`1px solid ${T.border}`}}>
                      <h2 style={{fontSize:24,fontWeight:800,color:T.text,letterSpacing:"-0.5px"}}>Wartung & Features</h2>
                      <div style={{fontSize:14,color:T.textMuted,marginTop:6}}>Überwache die Systemgesundheit und sichere deine Daten.</div>
                    </div>
                    {/* Bisheriger System-Inhalt */}
                    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: 20}}>
                      {/* Wartungsmodus */}
                      <div style={{background:T.card, border:`1px solid ${maintenanceMode ? "#F59E0B" : T.border}`, borderRadius:16, padding:"24px", gridColumn: "1 / -1", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
                            <span style={{fontSize:18}}>🚧</span> Wartungsmodus
                          </div>
                          <p style={{fontSize:13, color:T.textMuted, marginTop:6}}>
                            Sperrt alle normalen Nutzer sofort aus.
                          </p>
                        </div>
                        <button 
                          onClick={toggleMaintenance} 
                          style={{background: maintenanceMode ? "#F59E0B" : "transparent", border: `1px solid ${maintenanceMode ? "#F59E0B" : T.border3}`, color: maintenanceMode ? "#000" : T.text, padding:"11px 22px", borderRadius:11, fontSize:13, fontWeight:700, cursor:"pointer"}} className="hov">
                          {maintenanceMode ? "Wartungsmodus BEENDEN" : "Wartungsmodus AKTIVIEREN"}
                        </button>
                      </div>

			{/* Projekt-Reservierung Toggle */}
                      <div style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"24px", gridColumn: "1 / -1", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div>
                          <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
                            <span style={{fontSize:18}}>🔒</span> Projekt-Reservierung
                          </div>
                          <p style={{fontSize:13, color:T.textMuted, marginTop:6}}>
                            Erlaubt es Mitgliedern, das Projekt exklusiv zu blockieren (Lock-System).
                          </p>
                        </div>
                        <button 
                          onClick={async () => {
                            const newVal = systemFeatures.feature_reservation === false ? true : false;
                            try {
                              await api('/system/features', { method: 'PUT', body: JSON.stringify({ feature_reservation: newVal }) });
                              setSystemFeatures(prev => ({ ...prev, feature_reservation: newVal }));
                              notify(newVal ? "Reservierung AKTIVIERT ✅" : "Reservierung DEAKTIVIERT 🛑");
                            } catch(e) { notify("Fehler: " + e.message); }
                          }} 
                          style={{background: systemFeatures.feature_reservation !== false ? "#34D399" : "transparent", border: `1px solid ${systemFeatures.feature_reservation !== false ? "#34D399" : T.border3}`, color: systemFeatures.feature_reservation !== false ? "#000" : T.text, padding:"11px 22px", borderRadius:11, fontSize:13, fontWeight:700, cursor:"pointer"}} className="hov">
                          {systemFeatures.feature_reservation !== false ? "AN" : "AUS"}
                        </button>
                      </div>
                      
                      {/* Backups Card */}
                      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,display:"flex",flexDirection:"column"}}>
                        <div style={{padding:"20px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(0,0,0,0.05)"}}>
                          <div style={{fontSize:15,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:8}}><Save size={18} color="#0ECFD9"/> {t.backups||"Backups"}</div>
                          <button onClick={runBackup} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"8px 16px",borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 12px rgba(14,207,217,0.2)"}}>+ Erstellen</button>
                        </div>
                        <div style={{padding:"20px",display:"flex",flexDirection:"column",gap:12,maxHeight:500,overflowY:"auto"}}>
                          {backups.length===0&&<div style={{fontSize:13,color:T.textFaint,textAlign:"center",padding:"24px"}}>Keine Backups vorhanden.</div>}
                          {backups.map((b,i)=>(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",background:T.bg,border:`1px solid ${T.border2}`,borderRadius:12}}>
                              <span style={{fontSize:24}}>💾</span>
                              <div style={{flex:1}}>
                                <div style={{fontSize:14,fontWeight:600,color:T.textSub}}>{b.name}</div>
                                <div style={{fontSize:12,color:T.textFaint}}>{fmtSize(b.size)}</div>
                              </div>
                              <a href={`/api/admin/backups/${b.name}`} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.text,padding:"6px 14px",borderRadius:8,fontSize:12,fontWeight:600,textDecoration:"none"}} className="hov">⬇ Download</a>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Audit Log Card */}
                      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,display:"flex",flexDirection:"column"}}>
                        <div style={{padding:"20px",borderBottom:`1px solid ${T.border}`,background:"rgba(0,0,0,0.05)"}}>
                          <div style={{fontSize:15,fontWeight:700,color:T.text,display:"flex",alignItems:"center",gap:8}}><FileText size={18} color="#F59E0B"/> {t.auditLog||"Audit-Log"}</div>
                        </div>
                        <div style={{padding:"0 20px",display:"flex",flexDirection:"column",maxHeight:500,overflowY:"auto"}}>
                          {auditLog.length===0&&<div style={{fontSize:13,color:T.textFaint,textAlign:"center",padding:"24px"}}>Keine Einträge.</div>}
                          {auditLog.map((a,i)=>{
                            const m=AUDIT_META[a.action]||AUDIT_META.default;
                            const actionMap = { create: isEN ? "created" : "erstellte", delete: isEN ? "deleted" : "löschte", login: isEN ? "logged in" : "loggte sich ein", status_change: isEN ? "changed status of" : "änderte Status von", version_change: isEN ? "changed version of" : "änderte Version von", archive: isEN ? "archived" : "archivierte" };
                            const actionText = actionMap[a.action] || a.action;
                            return(
                              <div key={i} style={{display:"flex",gap:14,padding:"16px 0",borderBottom:`1px solid ${T.bg}`}}>
                                <div style={{width:36,height:36,borderRadius:10,background:m.color+"20",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                  <RenderIcon icon={m.icon} size={18} color={m.color} />
                                </div>
                                <div style={{flex:1}}>
                                  <div style={{fontSize:13,color:T.textSub,lineHeight:1.5}}>
                                    <b style={{color:T.text}}>{a.user_name||"System"}</b> · {actionText} <b style={{color:T.textMuted}}>{a.entity}</b> {a.detail&&`(${a.detail})`}
                                  </div>
                                  <div style={{fontSize:11,color:T.textGhost,marginTop:4}}>{timeAgo(a.created_at)}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 5. DISCORD */}
                {adminTab === "discord" && (
                  <div style={{animation: "fadeUp 0.2s ease"}}>
                    <div style={{marginBottom:24,paddingBottom:16,borderBottom:`1px solid ${T.border}`}}>
                      <h2 style={{fontSize:24,fontWeight:800,color:T.text,letterSpacing:"-0.5px"}}>Discord Integration</h2>
                      <div style={{fontSize:14,color:T.textMuted,marginTop:6}}>Verbinde deinen Workspace mit deinem Discord-Server.</div>
                    </div>
                    <DiscordAdminTab ctx={ctx} />
                  </div>
                )}

              </div>
            </div>
          )}
	</Suspense>
        </div>
      </div>

      {/* TASK DETAIL PANEL */}
      {currentDetailTask&&(
        <div style={{position:"fixed",inset:0,zIndex:800,display:"flex",justifyContent:"flex-end"}} onClick={e=>e.target===e.currentTarget&&setDetailTask(null)}>
          <div className="detail-panel" style={{width:600,background:T.sidebar,borderLeft:`1px solid ${T.border}`,height:"100%",display:"flex",flexDirection:"column",animation:"slideIn 0.3s ease",boxShadow:"-16px 0 60px rgba(0,0,0,0.5)"}}>
            
            {/* FIXED HEADER */}
            <div style={{padding:"24px 24px 20px",borderBottom:`1px solid ${T.border}`,flexShrink:0,background:T.cardHover}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:10,wordBreak:"break-word"}}>{currentDetailTask.title}</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                    <div onClick={(e)=>{e.stopPropagation();if(canEdit){const p=document.getElementById("detailStatusDrop");if(p)p.focus();}}} style={{cursor:canEdit?"pointer":"default"}}>
                      <Badge label={currentDetailTask.status} color={getStatusMeta(currentDetailTask.status).color} bg={getStatusMeta(currentDetailTask.status).bg}/>
                    </div>
                    {canEdit&&<select id="detailStatusDrop" value={currentDetailTask.status} onChange={e=>updateTaskStatus(currentDetailTask.id,e.target.value)} style={{position:"absolute",opacity:0,width:1,height:1}}>{projectStatuses.map(s=><option key={s} value={s}>{s}</option>)}</select>}
                    <Badge label={tPrio(PRIORITIES[currentDetailTask.priority]?.label)} color={PRIORITIES[currentDetailTask.priority]?.color} bg={PRIORITIES[currentDetailTask.priority]?.bg}/>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {[...(currentDetailTask.tags || [])]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(tg=><span key={tg.id} style={{fontSize:11,background:tg.color+"15",color:tg.color,padding:"3px 8px",borderRadius:6,fontWeight:600}}>{tg.name}</span>)}
                    </div>
                  </div>
                  {milestones.find(m=>m.id===currentDetailTask.milestone_id)&&<div style={{fontSize:11,background:milestones.find(m=>m.id===currentDetailTask.milestone_id).color+"15",color:milestones.find(m=>m.id===currentDetailTask.milestone_id).color,padding:"4px 10px",borderRadius:7,display:"inline-block",fontWeight:600}}>🎯 {milestones.find(m=>m.id===currentDetailTask.milestone_id).name}</div>}
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  {canEdit&&<button onClick={() => { if(window.confirm(isEN ? "Permanently delete?" : "Wirklich dauerhaft löschen?")) { deleteTask(currentDetailTask.id); setDetailTask(null); } }} style={{background:"rgba(255,71,87,0.15)",border:"1px solid rgba(255,71,87,0.3)",color:"#FF4757",padding:"7px 12px",borderRadius:9,fontSize:13,cursor:"pointer",fontWeight:600,marginRight:6}} title={isEN ? "Delete permanently" : "Dauerhaft löschen"}>🗑</button>}
                  {canEdit&&<button onClick={()=>archiveTask(currentDetailTask.id)} style={{background:"rgba(52,211,153,0.15)",border:"1px solid rgba(52,211,153,0.3)",color:"#34D399",padding:"7px 12px",borderRadius:9,fontSize:13,cursor:"pointer",fontWeight:600}} title={isEN ? "Archive as completed" : "Als erledigt archivieren"}>✓</button>}
                  {canEdit && <button onClick={() => { setDetailTask(null); openEditTask(currentDetailTask) }} style={{ background: T.bg, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "7px 12px", borderRadius: 9, fontSize: 13, cursor: "pointer" }} title={isEN ? "Edit" : "Bearbeiten"}>✎</button>}
                  {getTaskAssigneeIds(currentDetailTask).includes(currentUser?.id) && (
                    <button onClick={() => toggleFocus(currentDetailTask)} style={{ background: (currentDetailTask.focused_by || []).includes(currentUser?.id) ? "rgba(245,158,11,0.15)" : T.bg, border: `1px solid ${(currentDetailTask.focused_by || []).includes(currentUser?.id) ? "rgba(245,158,11,0.4)" : T.border2}`, color: (currentDetailTask.focused_by || []).includes(currentUser?.id) ? "#F59E0B" : T.textMuted, padding: "7px 12px", borderRadius: 9, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }} title={isEN ? "Toggle My Day" : "Mein Tag"}>
                      <Sun size={16} fill={(currentDetailTask.focused_by || []).includes(currentUser?.id) ? "#F59E0B" : "none"} color={(currentDetailTask.focused_by || []).includes(currentUser?.id) ? "#F59E0B" : T.textMuted} />
                      <span className="desktop-only">{isEN ? "My Day" : "Fokus"}</span>
                    </button>
                  )}
		  <button onClick={(e) => handleShareTask(e, currentDetailTask.id)} style={{ background: T.bg, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "7px 12px", borderRadius: 9, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }} className="hov" title={isEN ? "Copy Link" : "Link kopieren"}>
   		    <Link size={16} />
 		  </button>
                  {(() => {const isFollowing = (currentDetailTask.followers || []).includes(currentUser?.id);return <button onClick={() => isFollowing ? unfollowTask(currentDetailTask.id) : followTask(currentDetailTask.id)} style={{ background: isFollowing ? "rgba(14,207,217,0.15)" : T.bg, border: `1px solid ${isFollowing ? "rgba(14,207,217,0.4)" : T.border2}`, color: isFollowing ? ACCENT_LIGHT : T.textMuted, padding: "7px 12px", borderRadius: 9, fontSize: 13, cursor: "pointer" }} title={isFollowing ? (isEN ? "Unfollow" : "Nicht mehr folgen") : (isEN ? "Follow task" : "Aufgabe folgen")}>{isFollowing ? "🔔" : "🔕"}</button>;})()}
                  <button onClick={()=>setDetailTask(null)} style={{background:T.bg,border:`1px solid ${T.border2}`,color:T.textMuted,width:32,height:32,borderRadius:9,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}><div style={{display:"flex"}}>{getTaskAssigneeIds(currentDetailTask).map((uid,idx)=><UserAvatar key={uid} user={users.find(u=>u.id===uid)} size={30} overlap idx={idx}/>)}</div><div style={{fontSize:11,color:T.textFaint,display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>{isEN ? "Created by" : "Erstellt von"} <span style={{color:T.textMuted,fontWeight:600}}>{currentDetailTask.creator_name||"?"}</span>{currentDetailTask.created_at&&<span>{isEN ? "on" : "am"} {fmtDate(currentDetailTask.created_at)} {isEN ? "at" : "um"} {new Date(currentDetailTask.created_at).toLocaleTimeString(isEN ? 'en-US' : 'de-DE', {hour: '2-digit', minute:'2-digit'})} {isEN ? "" : "Uhr"}</span>}</div></div>
            </div>
            
            {/* SCROLLABLE BODY */}
            <div style={{flex:1,overflow:"auto",padding:"24px"}} ref={commentAreaRef}>
              
              {/* BESCHREIBUNG */}
              {(currentDetailTask.description||currentDetailTask.desc)&&(
                <div style={{marginBottom:28}}>
                  <div style={{fontSize:11,fontWeight:700,color:T.textGhost,letterSpacing:"0.1em",marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}><AlignLeft size={14}/> {isEN ? "DESCRIPTION" : "BESCHREIBUNG"}</div>
                  </div>
                  <div dangerouslySetInnerHTML={renderMarkdown(currentDetailTask.description||currentDetailTask.desc)} style={{fontSize:14,color:T.textMuted,lineHeight:1.7,whiteSpace:"pre-wrap",background:T.card,padding:"18px",borderRadius:12,border:`1px solid ${T.border}`}} />               </div>
              )}

              {/* DATEIEN */}
              <div style={{marginBottom:28}}>
                <div style={{fontSize:11,fontWeight:700,color:T.textGhost,letterSpacing:"0.1em",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}><Paperclip size={14}/> {isEN ? "FILES" : "DATEIEN"} ({(currentDetailTask.files||[]).length})</div>
                </div>
                {(currentDetailTask.files||[]).length>0&&<div style={{background:T.cardHover,borderRadius:12,overflow:"hidden",marginBottom:10}}>{(currentDetailTask.files||[]).map((f,i)=>(<div key={f.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:i<currentDetailTask.files.length-1?`1px solid ${T.border}`:"none"}}><FileIcon mime={f.mimetype}/><div style={{flex:1,overflow:"hidden"}}><a href={"/uploads/"+f.filename} target="_blank" rel="noreferrer" style={{fontSize:12,color:T.textSub,fontWeight:500,textDecoration:"none"}}>{f.original_name}</a><div style={{fontSize:10,color:T.textFaint}}>{fmtSize(f.size)}</div></div>{canEdit&&<div onClick={()=>deleteFile(f.id)} style={{color:"#FF4757",fontSize:16,cursor:"pointer"}}>×</div>}</div>))}</div>}
                {canEdit&&<div onDragOver={e=>{e.preventDefault();e.currentTarget.classList.add("drag-over");}} onDragLeave={e=>e.currentTarget.classList.remove("drag-over")} onDrop={e=>{e.preventDefault();e.currentTarget.classList.remove("drag-over");const f=e.dataTransfer.files[0];if(f)uploadFile(f);}} onClick={()=>fileInputRef.current&&fileInputRef.current.click()} style={{border:`2px dashed ${T.border3}`,borderRadius:12,padding:"16px",textAlign:"center",cursor:"pointer"}}><input ref={fileInputRef} type="file" style={{display:"none"}} onChange={e=>{if(e.target.files[0])uploadFile(e.target.files[0]);e.target.value="";}}/>{uploadingFile?<Spinner/>:<div style={{fontSize:12,color:T.textFaint}}>{isEN ? "Drag files here or click (Max. 200MB)" : "Dateien hierher ziehen oder klicken (Max. 200MB)"}</div>}</div>}
              </div>
              
             {/* CHECKLISTE */}
              <div style={{marginBottom:28}}>
                <div style={{fontSize:11,fontWeight:700,color:T.textGhost,letterSpacing:"0.1em",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <CheckSquare size={14}/> {isEN ? "CHECKLIST" : "CHECKLISTE"}
                    {(currentDetailTask.checklist||[]).length>0&&<span style={{fontSize:11,color:"#34D399",background:"rgba(52,211,153,0.1)",padding:"2px 8px",borderRadius:6,marginLeft:10,fontWeight:600}}>{(currentDetailTask.checklist||[]).filter(c=>c.done).length}/{(currentDetailTask.checklist||[]).length}</span>}
                  </div>
                </div>
                {(currentDetailTask.checklist||[]).length>0&&<div style={{background:T.cardHover,borderRadius:12,overflow:"hidden",marginBottom:10}}>{(currentDetailTask.checklist||[]).map((item,i)=>(<div key={item.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:i<currentDetailTask.checklist.length-1?`1px solid ${T.border}`:"none"}}><div onClick={()=>canEdit&&toggleCheck(currentDetailTask.id,item.id)} style={{width:16,height:16,borderRadius:5,border:`2px solid ${item.done?"#34D399":T.border3}`,background:item.done?"#34D399":"transparent",cursor:canEdit?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{item.done&&<span style={{fontSize:9,color:"#0A0D15",fontWeight:700}}>✓</span>}</div><span style={{flex:1,fontSize:13,color:item.done?T.textGhost:T.textSub,textDecoration:item.done?"line-through":"none"}}>{item.text}</span>{item.assignee_id && <UserAvatar user={users.find(u=>u.id===item.assignee_id)} size={22}/>}</div>))}</div>}
                {canEdit&&<div style={{display:"flex",gap:8}}><input value={ctx.newCheckItem} onChange={e=>ctx.setNewCheckItem(e.target.value)} onKeyDown={e=>e.key==="Enter"&&ctx.addCheckToDetail()} placeholder={isEN ? "New item..." : "Neuer Punkt…"} style={{...iS,flex:1}}/><button onClick={ctx.addCheckToDetail} style={{background:T.border3,border:"none",color:T.textMuted,padding:"10px 16px",borderRadius:11,fontSize:12,cursor:"pointer",fontWeight:500,whiteSpace:"nowrap"}}>+ Add</button></div>}
              </div>

             {/* UNTERAUFGABEN */}
              <div style={{marginBottom:28}}>
                <div style={{fontSize:11,fontWeight:700,color:T.textGhost,letterSpacing:"0.1em",marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <List size={14}/> {isEN ? "SUB-TASKS" : "UNTERAUFGABEN"}
                  </div>
                </div>
                {/* Liste der existierenden Unteraufgaben */}
                <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:8}}>
                  {tasks.filter(t => t.parent_task_id === currentDetailTask.id).map(sub => {
                    // 🚀 Holt die Checklisten-Infos der Unteraufgabe
                    const cTotal = (sub.checklist || []).length;
                    const cDone = (sub.checklist || []).filter(c => c.done).length;
                    
                    return (
                      <div key={sub.id} onClick={() => setDetailTask(sub)} style={{padding:"8px 12px", background:T.cardHover, borderRadius:8, fontSize:13, cursor:"pointer", border:`1px solid ${T.border2}`, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span>{sub.title}</span>
                          {/* 🚀 Checklisten-Badge anzeigen */}
                          {cTotal > 0 && (
                            <span style={{ fontSize: 10, color: cDone === cTotal ? "#34D399" : T.textFaint, background: T.bg, border: `1px solid ${T.border2}`, padding: "2px 6px", borderRadius: 5, fontWeight: 600 }}>
                              ✓ {cDone}/{cTotal}
                            </span>
                          )}
                        </div>
                        <Badge label={sub.status} color={getStatusMeta(sub.status).color} bg={getStatusMeta(sub.status).bg}/>
                      </div>
                    );
                  })}
                </div>
                
                {/* 🚀 Das hier hat gefehlt! */}
                <button onClick={() => { 
                  setDetailTask(null); 
                  setTaskForm({...taskForm, title: "", parent_task_id: currentDetailTask.id}); 
                  setModal("task"); 
                }} style={{width:"100%", background:T.cardHover, border:`1px dashed ${T.border2}`, color:T.textMuted, padding:"8px", borderRadius:8, fontSize:12, cursor:"pointer"}} className="hov">
                  + Unteraufgabe hinzufügen
                </button>
              </div>
                            
              {/* TABS: KOMMENTARE & AKTIVITÄT */}
              <div>
                <div style={{display:"flex", gap:16, borderBottom:`1px solid ${T.border}`, marginBottom:16}}>
                  <div onClick={()=>setDetailTab("comments")} style={{paddingBottom:8, cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.05em", color:detailTab==="comments"?ACCENT_LIGHT:T.textGhost, borderBottom:detailTab==="comments"?`2px solid ${ACCENT_LIGHT}`:"2px solid transparent", transition:"all 0.2s"}}>
                    <MessageSquare size={14} style={{verticalAlign:"middle", marginRight:4}}/> {isEN ? "COMMENTS" : "KOMMENTARE"} ({(currentDetailTask.comments||[]).length})
                  </div>
                  <div onClick={()=>setDetailTab("history")} style={{paddingBottom:8, cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.05em", color:detailTab==="history"?ACCENT_LIGHT:T.textGhost, borderBottom:detailTab==="history"?`2px solid ${ACCENT_LIGHT}`:"2px solid transparent", transition:"all 0.2s"}}>
                    <Activity size={14} style={{verticalAlign:"middle", marginRight:4}}/> {isEN ? "ACTIVITY" : "AKTIVITÄT"} ({taskHistory.length})
                  </div>
                </div>

                {detailTab === "comments" && (
                  <div style={{ animation: "fadeUp 0.2s ease" }}>
                    {(currentDetailTask.comments||[]).length===0&&<div style={{fontSize:13,color:T.textFaint,padding:"20px",background:T.cardHover,borderRadius:12,textAlign:"center",marginBottom:16}}>{isEN ? "No comments yet." : "Noch keine Kommentare."}</div>}
                    {(currentDetailTask.comments||[]).map(c=>{const author=users.find(u=>u.id===(c.authorId||c.author_id));return(<div key={c.id} style={{display:"flex",gap:10,marginBottom:16}}><UserAvatar user={author} size={28}/><div style={{flex:1}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}><span style={{fontSize:12,fontWeight:600,color:T.textSub}}>{author?.name||"?"}</span><span style={{fontSize:10,color:T.textFaint}}>{timeAgo(c.created_at)}</span></div><div dangerouslySetInnerHTML={renderMarkdown(c.text)} style={{background:T.cardHover,border:`1px solid ${T.border2}`,borderRadius:"0px 12px 12px 12px",padding:"11px 14px",fontSize:13,color:T.textMuted,lineHeight:1.6,whiteSpace:"pre-wrap"}} /></div></div>);})}
                  </div>
                )}

                {detailTab === "history" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14, animation: "fadeUp 0.2s ease" }}>
                    {taskHistory.length===0&&<div style={{fontSize:13,color:T.textFaint,padding:"20px",background:T.cardHover,borderRadius:12,textAlign:"center",marginBottom:16}}>{isEN ? "No activity logged." : "Keine Aktivitäten protokolliert."}</div>}
                    {taskHistory.map((h, i) => {
                      const m = AUDIT_META[h.action] || AUDIT_META.default;
                      
                      // 🚀 Smarte Übersetzung der Aktionen
                      const actionMap = {
                        create: isEN ? "Task created" : "Aufgabe erstellt",
                        status_change: isEN ? "Status changed" : "Status geändert",
                        version_change: isEN ? "Version changed" : "Version geändert",
                        archive: isEN ? "Task archived" : "Aufgabe archiviert"
                      };
                      const actionText = actionMap[h.action] || h.action;

                      return (
                        <div key={i} style={{display:"flex", gap:12, alignItems:"flex-start"}}>
                          <div style={{width:28, height:28, borderRadius:8, background:m.color+"20", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                            <RenderIcon icon={m.icon} size={14} color={m.color} />
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13, color:T.textSub, lineHeight:1.5}}>
                              <b style={{color:T.text}}>{h.user_name || "System"}</b> · {actionText}
                              {h.detail && <span style={{color:T.textMuted, display:"block", marginTop:2}}>→ {h.detail.replace(/"/g, '')}</span>}
                            </div>
                            <div style={{fontSize:10, color:T.textGhost, marginTop:4}}>
                              {fmtDate(h.created_at)} · {new Date(h.created_at).toLocaleTimeString(isEN ? 'en-US' : 'de-DE', {hour: '2-digit', minute:'2-digit'})} {isEN ? "" : "Uhr"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
             </div>

            {/* FIXED BOTTOM: KOMMENTAR EINGABE */}
            <div style={{padding:"16px 24px 24px",borderTop:`1px solid ${T.border}`,flexShrink:0,background:T.sidebar}}>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <UserAvatar user={currentUser} size={30}/>
                <MentionInput value={commentText} onChange={setCommentText} onSubmit={addComment} placeholder={isEN ? "Write a comment... (@mention)" : "Kommentar schreiben… (@mention)"} users={users} T={T} iS={iS}/>
                <button onClick={addComment} style={{background:activeProject?.color||ACCENT_LIGHT,border:"none",color:"#0A0D15",width:36,height:36,borderRadius:11,fontSize:16,cursor:"pointer",fontWeight:700,opacity:commentText.trim()?1:0.4,transition:"all 0.2s"}}>→</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* EINSTELLUNGEN */}
      {modal==="settings"&&(
        <Modal title="⚙ Einstellungen" onClose={closeModal} T={T}>
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            <div style={{display:"flex",alignItems:"center",gap:16,padding:"16px",background:T.cardHover,borderRadius:12}}>
              <UserAvatar user={currentUser} size={56}/>
              <div>
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  <button onClick={()=>avatarInputRef.current?.click()} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"7px 14px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer"}}>{uploadingFile?<Spinner/>:"Bild ändern"}</button>
                  {currentUser.avatar_url && <button onClick={deleteAvatar} style={{background:"transparent",border:`1px solid ${T.border3}`,color:T.textMuted,padding:"7px 14px",borderRadius:8,fontSize:12,cursor:"pointer"}}>Entfernen</button>}
                </div>
                <div style={{fontSize:10,color:T.textGhost}}>JPG/PNG, Max. 5MB</div>
                <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png" style={{display:"none"}} onChange={e=>e.target.files[0]&&uploadAvatar(e.target.files[0])}/>
              </div>
            </div>
            <Field label="NAME" T={T}><input value={settingsForm.name} onChange={e=>setSettingsForm(f=>({...f,name:e.target.value}))} style={iS}/></Field>
            <Field label="BENUTZERNAME" T={T}><input value={settingsForm.username} onChange={e=>setSettingsForm(f=>({...f,username:e.target.value}))} style={iS}/></Field>
            
            {/* DISCORD TEST BUTTON */}
       {isDiscordActive && (
            <Field label="DISCORD ID" T={T}>
              <div style={{display:"flex",gap:8}}>
                <input value={settingsForm.discord_id||""} onChange={e=>setSettingsForm(f=>({...f,discord_id:e.target.value}))} placeholder="z.B. 123456789012345678" style={{...iS,flex:1}}/>
                <button onClick={async()=>{try{await api("/discord/test",{method:"POST"});notify("Discord DM gesendet ✓");}catch(e){notify("Fehler: "+e.message);}}} style={{background:"#5865F2",border:"none",color:"#fff",padding:"0 16px",borderRadius:9,fontSize:12,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>🧪 Testen</button>
              </div>
              <div style={{fontSize:10,color:T.textFaint,marginTop:6}}>Discord → Einstellungen → Erweitert → Entwicklermodus → Rechtsklick auf Namen → ID kopieren</div>
            </Field>
       )}

            <Field label="PROFILFARBE" T={T}><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{AVATAR_COLORS.map(c=><div key={c} onClick={()=>setSettingsForm(f=>({...f,color:c}))} style={{width:26,height:26,borderRadius:"50%",background:c,cursor:"pointer",border:settingsForm.color===c?`3px solid ${T.textSub}`:`3px solid transparent`,transition:"all 0.1s"}}/>)}</div></Field>
            
            <div style={{marginTop:10,borderTop:`1px solid ${T.border}`,paddingTop:20}}>
              <h3 style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>🔐 Passwort ändern</h3>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <input type="password" value={settingsForm.currentPassword} onChange={e=>setSettingsForm(f=>({...f,currentPassword:e.target.value}))} placeholder="Aktuelles Passwort" style={iS}/>
                <input type="password" value={settingsForm.newPassword} onChange={e=>setSettingsForm(f=>({...f,newPassword:e.target.value}))} placeholder="Neues Passwort (min. 6 Zeichen)" style={iS}/>
                <input type="password" value={settingsForm.newPassword2} onChange={e=>setSettingsForm(f=>({...f,newPassword2:e.target.value}))} placeholder="Neues Passwort bestätigen" style={iS}/>
              </div>
            </div>

            <div style={{display:"flex",gap:12,justifyContent:"flex-end",marginTop:20}}>
              <button onClick={closeModal} style={{background:"transparent",border:`1px solid ${T.border3}`,color:T.textMuted,padding:"11px 20px",borderRadius:11,fontSize:14,cursor:"pointer"}}>Abbrechen</button>
              <button onClick={saveSettings} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"11px 24px",borderRadius:11,fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:`0 6px 16px rgba(14,207,217,0.2)`}}>Speichern</button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* PASSWORT ÄNDERN ZWANG */}
      {modal==="change_password"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:900}}>
          <div style={{background:T.sidebar,border:`1px solid ${T.border3}`,borderRadius:18,padding:"32px",width:420,boxShadow:"0 32px 64px #00000090",animation:"scaleIn 0.2s ease"}}>
            <div style={{textAlign:"center",marginBottom:24}}><div style={{fontSize:40,marginBottom:12}}>🔐</div><div style={{fontSize:18,fontWeight:700,color:T.text,marginBottom:6}}>Passwort ändern</div><div style={{fontSize:13,color:T.textMuted}}>Einmalpasswort aktiv – neues Passwort festlegen.</div></div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <Field label="NEUES PASSWORT" T={T}><input type="password" value={changePwForm.newPassword} onChange={e=>setChangePwForm(f=>({...f,newPassword:e.target.value}))} placeholder="Min. 6 Zeichen" style={iS} autoFocus/></Field>
              <Field label="BESTÄTIGEN" T={T}><input type="password" value={changePwForm.newPassword2} onChange={e=>setChangePwForm(f=>({...f,newPassword2:e.target.value}))} placeholder="Wiederholen" style={iS}/></Field>
              <button onClick={changePassword} style={{background:ACCENT_GRADIENT,border:"none",color:"#fff",padding:"12px",borderRadius:11,fontSize:14,fontWeight:700,cursor:"pointer",marginTop:4}}>Passwort festlegen →</button>
            </div>
          </div>
        </div>
      )}

      {/* PROJEKT BEARBEITEN MODAL MIT EIGENEM STATUS-BUTTON */}
      {modal==="project"&&(
        <Modal title={editTarget?"Projekt bearbeiten":"Neues Projekt"} onClose={closeModal} width={380} T={T} footer={
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {editTarget && isAdmin && (
              <button onClick={()=>openStatuses(editTarget)} style={{width:"100%",background:T.cardHover,border:`1px solid ${T.border2}`,color:T.text,padding:"10px",borderRadius:11,fontSize:13,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                ◉ Eigene Status-Spalten anpassen
              </button>
            )}
            <div style={{display:"flex",gap:9,justifyContent:"flex-end",marginTop:10}}>
              <button onClick={closeModal} style={{background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"9px 18px",borderRadius:9,fontSize:12,cursor:"pointer"}}>Abbrechen</button>
              <button onClick={saveProject} style={{background:projectForm.color,border:"none",color:"#fff",padding:"9px 22px",borderRadius:9,fontSize:12,cursor:"pointer",fontWeight:600}}>{editTarget?"Speichern":"Erstellen"}</button>
            </div>
          </div>
        }>
          <Field label="PROJEKTNAME" T={T}><input value={projectForm.name} onChange={e=>setProjectForm(f=>({...f,name:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&saveProject()} placeholder="z.B. Website Relaunch…" style={iS} autoFocus/></Field>
          <Field label="EMOJI" T={T}><div style={{display:"flex",gap:7,flexWrap:"wrap"}}>{["🌐","📱","🚀","💡","🎯","📊","🛠️","🎨","📝","🔥"].map(e=><div key={e} onClick={()=>setProjectForm(f=>({...f,icon:e}))} style={{width:34,height:34,borderRadius:9,border:`2px solid ${projectForm.icon===e?ACCENT_LIGHT:T.inputBorder}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,background:projectForm.icon===e?"rgba(14,207,217,0.15)":T.input}}>{e}</div>)}</div></Field>
          <Field label="FARBE" T={T}><div style={{display:"flex",gap:7,flexWrap:"wrap"}}>{PROJECT_COLORS.map(c=><div key={c} onClick={()=>setProjectForm(f=>({...f,color:c}))} style={{width:28,height:28,borderRadius:8,background:c,cursor:"pointer",border:`3px solid ${projectForm.color===c?"#fff":"transparent"}`,transition:"all 0.15s"}}/>)}</div></Field>
        </Modal>
      )}

      {/* COMMAND PALETTE (Strg + K) */}
      {cmdOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(6px)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:"12vh"}} onClick={(e)=>{if(e.target===e.currentTarget)setCmdOpen(false)}}>
          <div style={{width:"100%",maxWidth:600,background:T.sidebar,border:`1px solid ${T.border2}`,borderRadius:16,boxShadow:"0 32px 72px rgba(0,0,0,0.6)",overflow:"hidden",animation:"fadeUp 0.2s ease",display:"flex",flexDirection:"column"}}>
            
            {/* Suchfeld */}
            <div style={{display:"flex",alignItems:"center",padding:"18px 24px",borderBottom:`1px solid ${T.border}`}}>
              <Search size={22} color={ACCENT_LIGHT} />
              <input ref={cmdInputRef} value={cmdSearch} onChange={e=>setCmdSearch(e.target.value)} placeholder="Suche nach Aufgaben, Projekten..." style={{background:"transparent",border:"none",color:T.text,fontSize:18,outline:"none",flex:1,marginLeft:14,fontFamily:"inherit"}} />
              <div style={{fontSize:11,background:T.cardHover,border:`1px solid ${T.border2}`,color:T.textMuted,padding:"4px 8px",borderRadius:6,fontWeight:700}}>ESC</div>
            </div>

            {/* Ergebnisse */}
            <div style={{maxHeight:400,overflowY:"auto",padding:"12px"}}>
              {cmdSearch.trim() === "" ? (
                <div style={{padding:"12px"}}>
                  {recentSearches.length === 0 ? (
                    <div style={{textAlign:"center",color:T.textGhost,fontSize:14,padding:"20px"}}>Tippe etwas, um zu suchen...</div>
                  ) : (
                    <>
                      <div style={{fontSize:11, fontWeight:700, color:T.textFaint, marginBottom:10, letterSpacing:"0.1em"}}>ZULETZT GESUCHT</div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {recentSearches.map((r, i) => (
                          <div key={i} onClick={()=>{
                            // 🚀 WEICHE FÜR KÜRZLICH GESUCHTE ITEMS
                            if(r.type==="project") { 
                              setActiveProjectId(r.id); 
                              const p = projects.find(x=>x.id===r.id);
                              setActiveSection(p && p.name.toLowerCase().includes("beta") ? "beta" : "tasks");
                            }
                            else { 
                              const t = allTasks.find(x => x.id === r.id) || {id: r.id, project_id: r.project_id};
                              handleOpenTask(t);
                            }
                            setCmdOpen(false);
                          }} className="tr" style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,cursor:"pointer"}}>
                            <Clock size={16} color={T.textMuted} />
                            <div style={{flex:1}}><div style={{fontSize:14,fontWeight:500,color:T.text}}>{r.title}</div><div style={{fontSize:11,color:T.textFaint}}>{r.type==="project"?"Projekt":"Aufgabe"}</div></div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  
                  {/* Gefilterte Projekte */}
                  {visibleProjects.filter(p => p.name.toLowerCase().includes(cmdSearch.toLowerCase())).map(p => (
                    <div key={`p-${p.id}`} onClick={()=>{
                        // 🚀 WEICHE FÜR PROJEKTE IN DER SUCHE
                        addRecentSearch({ id: p.id, title: p.name, type: "project" });
                        setActiveProjectId(p.id);
                        setActiveSection(p.name.toLowerCase().includes("beta") ? "beta" : "tasks");
                        setCmdOpen(false);
                    }} className="tr" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderRadius:10,cursor:"pointer"}}>
                      <div style={{width:28,height:28,borderRadius:8,background:p.color+"20",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <RenderIcon icon={p.icon} size={14} color={p.color}/>
                      </div>                      
                      <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600,color:T.text}}>{p.name}</div><div style={{fontSize:11,color:T.textFaint}}>Projekt</div></div>
                      <div style={{fontSize:16,color:T.textGhost}}>→</div>
                    </div>
                  ))}

 		  {/* Gefilterte Wiki Seiten */}
                  {wikiPages.filter(p => (p.title||"").toLowerCase().includes(cmdSearch.toLowerCase())).slice(0, 4).map(p => (
                    <div key={`w-${p.id}`} onClick={()=>{
                        addRecentSearch({ id: p.id, title: p.title.split('|||')[0], type: "wiki" });
                        localStorage.setItem("tf_jump_to_wiki", p.id);
                        setActiveSection("wiki");
                        setCmdOpen(false);
                    }} className="tr" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderRadius:10,cursor:"pointer"}}>
                      <BookOpen size={20} color="#F472B6" />
                      <div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:500,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.title.split('|||')[0]}</div><div style={{fontSize:11,color:T.textFaint}}>Wiki-Seite</div></div>
                    </div>
                  ))}

                  {/* Gefilterte Aufgaben */}
                  {allTasks.filter(t => !t.archived && t.title.toLowerCase().includes(cmdSearch.toLowerCase())).slice(0, 8).map(task => {
                    const p = projects.find(x => x.id === task.project_id);
                    return (
                      <div key={`t-${task.id}`} onClick={()=>{
                          // 🚀 WEICHE FÜR AUFGABEN IN DER SUCHE
                          addRecentSearch({ id: task.id, title: task.title, type: "task", project_id: task.project_id });
                          handleOpenTask(task);
                          setCmdOpen(false);
                      }} className="tr" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderRadius:10,cursor:"pointer"}}>
                         <CheckSquare size={20} color={T.textMuted} />
                         <div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:500,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{task.title}</div><div style={{fontSize:11,color:T.textFaint}}>Aufgabe {p ? `in ${p.name}` : ''}</div></div>
                         <Badge label={task.status} color={getStatusMeta(task.status).color} bg={getStatusMeta(task.status).bg}/>
                      </div>
                    );
                  })}

                  {/* Keine Ergebnisse */}
                  {visibleProjects.filter(p => p.name.toLowerCase().includes(cmdSearch.toLowerCase())).length === 0 && allTasks.filter(t => !t.archived && t.title.toLowerCase().includes(cmdSearch.toLowerCase())).length === 0 && (
                    <div style={{padding:"20px",textAlign:"center",color:T.textFaint,fontSize:14}}>Keine passenden Ergebnisse gefunden.</div>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      )}

{/* 🛟 SYSTEM QUICK SUBMIT MODAL */}
      {showSystemFeedback && (
        <Modal title={isEN ? "TaskFlow Feedback" : "TaskFlow Feedback"} onClose={() => setShowSystemFeedback(false)} T={T}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            
            <div style={{ display: "flex", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 11, padding: 4 }}>
              <button onClick={() => setSysFeedbackForm({ ...sysFeedbackForm, type: "bug" })} style={{ flex: 1, background: sysFeedbackForm.type === "bug" ? T.card : "transparent", color: sysFeedbackForm.type === "bug" ? "#FF4757" : T.textMuted, border: "none", padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.2s", boxShadow: sysFeedbackForm.type === "bug" ? "0 2px 8px rgba(0,0,0,0.05)" : "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Bug size={16}/> Bug / Fehler
              </button>
              <button onClick={() => setSysFeedbackForm({ ...sysFeedbackForm, type: "feature" })} style={{ flex: 1, background: sysFeedbackForm.type === "feature" ? T.card : "transparent", color: sysFeedbackForm.type === "feature" ? "#0ECFD9" : T.textMuted, border: "none", padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.2s", boxShadow: sysFeedbackForm.type === "feature" ? "0 2px 8px rgba(0,0,0,0.05)" : "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Zap size={16}/> Feature / Idee
              </button>
            </div>

            <Field label={isEN ? "TITLE" : "TITEL"} T={T}>
              <input value={sysFeedbackForm.title} onChange={e => setSysFeedbackForm({ ...sysFeedbackForm, title: e.target.value })} placeholder={isEN ? "What's going on?" : "Was ist passiert / Was wünschst du dir?"} style={iS} autoFocus />
            </Field>

            <Field label={isEN ? "DESCRIPTION" : "BESCHREIBUNG"} T={T}>
              <textarea value={sysFeedbackForm.description} onChange={e => setSysFeedbackForm({ ...sysFeedbackForm, description: e.target.value })} placeholder={isEN ? "Provide some details..." : "Genaue Details..."} style={{ ...iS, height: 100, resize: "none" }} />
            </Field>

            <div style={{ fontSize: 11, color: T.textGhost, display: "flex", alignItems: "flex-start", gap: 8, background: T.cardHover, padding: 12, borderRadius: 10, border: `1px dashed ${T.border2}` }}>
              <div style={{ fontSize: 16 }}>🕵️‍♂️</div>
              <div style={{ lineHeight: 1.4 }}>
                {isEN ? "To help the admin, a snapshot of your current screen context and recent background logs will be securely attached to this report." : "Um dem Admin zu helfen, wird ein unsichtbarer Schnappschuss deines aktuellen Standorts (Tab/Projekt) und der letzten Hintergrund-Fehlermeldungen an dieses Ticket angehängt."}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
              <button onClick={() => setShowSystemFeedback(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "10px 18px", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}>{isEN ? "Cancel" : "Abbrechen"}</button>
              <button onClick={async () => {
                if (!sysFeedbackForm.title.trim()) return notify(isEN ? "Title required!" : "Titel erforderlich!");
                try {
                  const payload = {
                    ...sysFeedbackForm,
                    context: { activeSection, activeProjectId, modal, projectView, timestamp: new Date().toISOString() },
                    system_log: sysFeedbackForm.type === "bug" ? systemLogs : [] // Sendet Logs nur bei Bugs!
                  };
                  await api('/system-tickets', { method: "POST", body: JSON.stringify(payload) });
                  setShowSystemFeedback(false);
                  setSysFeedbackForm({ type: "bug", title: "", description: "" });
                  notify(isEN ? "Ticket submitted! Thank you. ✅" : "Ticket eingereicht! Vielen Dank. ✅");
                } catch(e) { notify("Fehler: " + e.message); }
              }} style={{ background: sysFeedbackForm.type === "bug" ? "#FF4757" : ACCENT_GRADIENT, border: "none", color: "#fff", padding: "10px 24px", borderRadius: 10, fontWeight: 700, cursor: "pointer" }}>
                {isEN ? "Submit" : "Einreichen"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <AppModals ctx={ctx}/>
    </div>
  );
}

function DiscordAdminTab({ ctx }) {
  const { T, api, notify, isEN } = ctx;
  const [cfg, setCfg] = useState({ discord_enabled: true, discord_channel_id: "", discord_notify_beta: true, discord_notify_polls: true, discord_notify_ideas: true, discord_notify_reports: true });
  
  useEffect(() => { api("/system/discord-settings").then(setCfg).catch(()=>{}); }, [api]);

  const save = async () => {
    try {
      await api("/system/discord-settings", { method: "PUT", body: JSON.stringify(cfg) });
      notify("Discord-Einstellungen gespeichert! ✅");
    } catch(e) { notify("Fehler: " + e.message); }
  };

  return (
    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", gap: 20}}>
      {/* HAUPTSCHALTER */}
      <div style={{background:T.card, border:`1px solid ${!cfg.discord_enabled ? "#FF4757" : T.border}`, borderRadius:16, padding:"24px", gridColumn: "1 / -1", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <div>
          <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
            <span style={{fontSize:18}}>🤖</span> Discord Integration
          </div>
          <p style={{fontSize:13, color:T.textMuted, marginTop:6}}>
            Wenn deaktiviert, werden keine Nachrichten mehr gesendet und die Discord-Eingabefelder für Nutzer verschwinden aus der Oberfläche.
          </p>
        </div>
        <button onClick={() => setCfg({...cfg, discord_enabled: !cfg.discord_enabled})} style={{background: !cfg.discord_enabled ? "#FF4757" : "#5865F2", border: "none", color: "#fff", padding:"11px 22px", borderRadius:11, fontSize:13, fontWeight:700, cursor:"pointer"}} className="hov">
          {cfg.discord_enabled ? "Integration DEAKTIVIEREN" : "Integration AKTIVIEREN"}
        </button>
      </div>

      {cfg.discord_enabled && (
        <div style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"24px", gridColumn: "1 / -1", display:"flex", flexDirection:"column", gap:14}}>
          <div style={{fontSize:15, fontWeight:700, color:T.text, display:"flex", alignItems:"center", gap:10}}>
            <span style={{fontSize:18}}>📢</span> Globale Kanal-Nachrichten
          </div>
          <p style={{fontSize:13, color:T.textMuted}}>Gib hier die ID des Discord-Kanals ein, in dem globale Ankündigungen gepostet werden sollen.</p>
          
          <div style={{marginBottom: 16}}>
            <div style={{fontSize:10, color:T.textFaint, fontWeight:700, letterSpacing:"0.05em", marginBottom:8}}>DISCORD PUBLIC CHANNEL ID</div>
            <input value={cfg.discord_channel_id} onChange={e => setCfg({...cfg, discord_channel_id: e.target.value})} placeholder="z.B. 123456789012345678" style={{width:"100%", background:T.input, border:`1px solid ${T.inputBorder}`, padding:"10px 14px", borderRadius:11, color:T.text, outline:"none"}} />
          </div>

          <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12}}>
            {[
              { key: "discord_notify_reports", label: "Wöchentlicher Report (Sonntag)", icon: "📊" },
              { key: "discord_notify_beta", label: "Neue Beta-Releases", icon: "🚀" },
              { key: "discord_notify_polls", label: "Neue Umfragen", icon: "🗳️" },
              { key: "discord_notify_ideas", label: "Neue Ideen-Themen", icon: "💡" }
            ].map(item => (
              <div key={item.key} onClick={() => setCfg({...cfg, [item.key]: !cfg[item.key]})} className="hov" style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", background:T.cardHover, border:`1px solid ${T.border2}`, borderRadius:12, cursor:"pointer"}}>
                <div style={{display:"flex", alignItems:"center", gap:10}}>
                  <span style={{fontSize:16}}>{item.icon}</span>
                  <span style={{fontSize:13, fontWeight:600, color:T.text}}>{item.label}</span>
                </div>
                <div style={{width:38, height:22, borderRadius:11, background: cfg[item.key] ? "#34D399" : T.border3, position:"relative", transition:"background 0.2s"}}>
                  <div style={{width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left: cfg[item.key] ? 18 : 2, transition:"all 0.2s", boxShadow:"0 2px 4px rgba(0,0,0,0.2)"}} />
                </div>
              </div>
            ))}
          </div>

          <div style={{marginTop: 16, display:"flex", justifyContent:"flex-end"}}>
            <button onClick={save} style={{background: "#0ECFD9", color: "#000", border: "none", padding: "10px 24px", borderRadius: 10, fontWeight: 700, cursor: "pointer"}}>Einstellungen speichern</button>
          </div>
        </div>
      )}
    </div>
  );
}
